// src/components/desk/ContractComposer.jsx — the pieces of the contract UI that TWO surfaces
// share: the contract PAGE (`#contract/<id>`, ContractPage.jsx) and the compact list on the
// meeting card (ContractPanel.jsx).
//
// DESIGN BUILD (2026-09-11, design/CONTRACTS_DESIGNER_HANDOFF_2026-09-11.md, round 1): the
// `Composer` is now the PAPER — a light sheet inside the dark Desk (tokens in ./contract/
// paper.js) with the values as grouped rows in document order, each `label · input · source
// tag`, and the clause text COLLAPSED into slim section rows that open one editor at a time
// (./contract/ClauseEditor.jsx). The language pills, the sticky bar and the party timeline
// moved to their own files under ./contract/; every payload helper here is unchanged.
//
// Owner rulings 2026-09-10: "we need this to be a separate link. we press create contract then
// choose a type and the opens contract page with inherited values" · "it must be linkable to
// send" · "editable until signed". The page is the one that draws these; the panel only links
// to it. Until 2026-09-10 all of this lived inline in ContractPanel.jsx — the history of the
// rules below is in that file's header of that date.
//
// Rules that look like styling and are not:
//   • A required field with `source:'none'` is HIGHLIGHTED and the submit button stays off
//     until it is answered (design §6.4 rule 1: "a version with an unanswered required field
//     cannot be sent" — diagnosis rule 6, a document with an empty line is a lock with nothing
//     behind it). The server 400s `missing_fields` anyway; asking here keeps the demand where
//     the decision is.
//   • A language not in `template.langs_reviewed` is shown DISABLED with a hint, never hidden:
//     the agent must be able to see that English exists and is not yet reviewed, or they will
//     ask for it as a bug.
//   • Sending is the agent's OWN tap on wa.me / mailto / copy (delivery stage 1, design §2 —
//     no infrastructure), and the tap is what records `delivered_via`. The phone/email come
//     from the existing contact reveal (`revealContactBook`), never from the meeting payload,
//     which returns no contact by rule. A party whose number cannot be revealed still gets a
//     WhatsApp button — `wa.me/?text=…` with no number lets the agent pick the chat — because
//     a lock must name an action the viewer HAS.
//   • Party legal names and the language set are LOCKED on an existing contract (a version
//     N+1 in a different language, or with a different party, is a different document, not
//     an amendment); only the values are editable, and only until signed. The exceptions
//     (API §"One contract, many languages", migration 1044): a party's SPELLINGS
//     (`names[lang]`) and their link language (`preferred_language`) may be added or
//     corrected on a save — the rerender accepts `parties:[{role,legal_name,names,
//     preferred_language}]` — because the same person is the same party in any script.
//   • Languages are a SET of 1–3 (owner 2026-09-10 night: "do not restrict the underlying
//     model to Georgian + one language"). Three checkboxes ka / en / ru, each offered only
//     when the template has it REVIEWED, at least one required. The order SENT is always
//     ka, en, ru filtered by what is checked, and the FIRST sent prevails — the row says so
//     ("პირველი ენა უპირატესია"), and says which one when Georgian is off. Nothing here
//     hardwires Georgian: it is merely the default tick.
//   • ONE canonical `legal_name`, exactly as written on the person's ID/passport (owner:
//     "keep one canonical legal_name … do not require translated names and do not let them
//     replace the canonical legal name"). Per CHECKED language an OPTIONAL spelling
//     `names[lang]`; an empty box stays empty (nothing transliterates), the renderer falls
//     back to the legal name. `display_name` is sent = legal_name for older readers.
//   • Each party has a `preferred_language` ("their signing link opens in that language
//     automatically") — a small select over the checked languages, default = the first.
//   • (SUPERSEDED the same day — see the next bullet: no spelling boxes at all.)
//     A SPELLING BOX ONLY WHERE THE SCRIPT DIFFERS (owner 2026-09-11): "If the canonical name
//     is already in Georgian, do not show 'სახელი ქართულად'. If Russian is added, show only
//     'სახელი რუსულად' as optional … Never duplicate the same script for no reason." The
//     legal name's script is detected (`scriptOf`: Georgian / Cyrillic / Latin letters) and
//     the box for that language is not drawn; a name in an unknown script draws them all.
//   • THE LINK LANGUAGE IS THE KEY, AND THE NAME IS THE ONE IN THE DOCUMENT (owner 2026-09-11,
//     later): "ბმულის ენა is the key — if it's Georgian the nationality is Georgian too";
//     "let's use the name that's in the document"; "no need to have 3 translations right
//     now". So the composer draws NO spelling boxes (`names[lang]` stays in the model and
//     the API, empty — the renderer prints the legal name in every column), the link
//     language sits right under the party kind, and it seeds the ID kind: Georgian →
//     პირადი ნომერი, otherwise პასპორტი (until a number is typed).
//   • IDENTITY IS PART OF THE CONTRACT, CONTACT IS PART OF THE RELATIONSHIP (owner 2026-09-11,
//     migration 1051, design §9.13). Per party: kind (person / company), the ID kind + number
//     (a company: registration number + representative name and authority), phone, email, and
//     the sharing rule (phone shown to the other party by default, email not). The agent
//     PREFILLS; each party confirms or corrects its own on its private link — a correction
//     after any signature is a new version — so nothing here is required beyond the name, and
//     the rows carry a note saying so. Prefilled contact comes from the party row (the draft).
//
// Three more owner rulings, 2026-09-10 (template v3, migration 1048):
//   • PREPAYMENT IS A LIST, NOT CHECKBOXES. One field of kind `prepay_list` — rows of
//     (which month · paid in full? · else paid / remaining / due date · ×) plus "add a month".
//     The value is the API's `[{month, full, paid, remaining, due}]`, built by `PrepayListInput`.
//     A new partial row's currency follows the rent field's (`values.rent.currency`), USD when
//     the rent has none — so the agent is not asked a question the form already answered.
//   • THE SENTENCE UNDER THE LIST ("paid x, left to pay y till date") is drawn live, muted, in
//     the UI language (ka fallback), by `prepaySentence` in src/lib/prepaySentence.js — the
//     pure module that MIRRORS the server's wording, so what the agent reads is what clause 3
//     will say. The wording is pinned by prepaySentence.test.mjs.
//   • FIELD CONDITIONS. A draft field may carry `condition` ('utilities_mode=included',
//     'payment_method=app', 'partial_remaining', '!x', null); the form HIDES a field whose
//     condition is false against the current values (`fieldCondition`, the server's semantics),
//     and a hidden field is not "missing" for the Create/Save gate — a question the answer
//     removed cannot be the reason the button is off. Its value is left in `values` untouched,
//     so flipping the condition back restores what was typed.

import { rangeProblem, rowsProblems, filledRows, numberFromInput, numberStep, hasDecimals } from '../../lib/contractFieldRules.js'
import { partyKey, seatOf, withSeats, addSeat, removeSeat, canAddSeat, residentEmpty, partiesToSend, residentRoom } from '../../lib/contractSeats.js'
export { partiesToSend }
import { Fragment, useEffect, useId, useState } from 'react'
import { t, tLog, tOr, getLanguage } from '../../lib/i18n.js'
import { describeContractError } from '../../lib/contractErrors.js'
import { isInherited, movedFromSource, overriddenKeys, overridesPayload, sameAnswer } from '../../lib/contractOverrides.js'
import { advanceDerivations, priorPaymentForced, DERIVED_FROM_TOTAL } from '../../lib/contractDerive.js'
import { dateProblems } from '../../lib/contractDates.js'
import { advanceAmountProblems, refundReduced } from '../../lib/contractAmounts.js'
import { creditableAtSigning, priorCreditProblem, binderCreditProblem, mandateProblem, addressScriptProblem, serviceChargeTwiceProblem, refusalSentence, paymentCalendarProblem, remoteProblem,
  commissionSettlementProblem, commissionCurrencyMixed, rentalCalendar } from '../../lib/contractChecks.js'
export { isInherited, overriddenKeys, overridesPayload, sameAnswer }
import { DateInput } from '../DateInput.jsx'
import { DateTimeInput } from '../DateTimeInput.jsx'
import { prepaySentence, fieldCondition, monthLabel, normLang, PREPAY_MONTHS, isMoney,
  prepayMonthOptions } from '../../lib/prepaySentence.js'
import { addPeriod, diffPeriod, asPeriod, describe as describeLease, durationText, LEASE_UNITS, LEASE_PRESET_MONTHS } from '../../lib/leasePeriod.js'
import { GROUP, INK_MUTED, L, optionText, PDATE, PINP, TAG, TAG_ASK, ka } from './contract/paper.js'
import { AmountInput } from './contract/AmountInput.jsx'
import { ClauseEditor, driftIssueText } from './contract/ClauseEditor.jsx'
import { PaymentAccountPicker } from './contract/PaymentAccountPicker.jsx'
import { RentalPaymentPlan, RentalSigningBlock, RentalHandoverBlock } from './contract/RentalPaymentPlan.jsx'
import { RentalOpening } from './contract/RentalOpening.jsx'
import { openingQuestions, openingFromValues, openingMissing, openingSteps, routesForMethod } from '../../lib/rentalOpening.js'
import { rentalPaymentProblem, paymentMessage, paymentPlanProblem, monthlySameAvailable, reconcileAccountSelection, methodDerivations, monthlyRouteOf, signingRouteOf, signingSteps, signingRouteAllowed } from '../../lib/rentalPaymentRoutes.js'
// Asked by RentalPaymentPlan (lease v8), never as rows of their own.
const PAYMENT_PLAN_KEYS = new Set(['remote_transaction', 'tenant_inspected_in_person', 'initial_payment_deadline',
  'payment_method', 'payment_app', 'payment_recipient', 'payment_bank', 'payment_account_holder', 'payment_account', 'payment_reference',
  'initial_payment_swift', 'initial_recipient_type', 'initial_receiving_agent', 'initial_completion_rule', 'initial_custom_completion_rule',
  'initial_processing_days', 'initial_bank_charges', 'initial_bank_charges_note', 'recurring_recipient_type', 'owner_payment_bank',
  'owner_payment_account_holder', 'owner_payment_iban', 'owner_payment_swift', 'recurring_payment_method', 'recurring_completion_rule',
  'recurring_custom_completion_rule', 'recurring_processing_days', 'recurring_bank_charges', 'recurring_bank_charges_note',
  'recurring_payment_methods', 'key_handover_timing', 'key_handover_notice_hours', 'physical_handover_required', 'defect_report_hours',
  // the custom rule's own translations are asked right under it in the plan (owner 2026-09-23), not as loose rows
  'initial_custom_completion_rule_en', 'initial_custom_completion_rule_ru', 'recurring_custom_completion_rule_en', 'recurring_custom_completion_rule_ru',
  // …and the shared-fee note's (core 3144, reviews 2026-10-09), under the note it translates
  'initial_bank_charges_note_en', 'initial_bank_charges_note_ru', 'recurring_bank_charges_note_en', 'recurring_bank_charges_note_ru'])
import { fieldsHiddenBySections } from '../../lib/contractSectionFields.js'
// Who signs for the agency — the client's half of the rule, pure and tested
// (src/lib/contractAgencyRep.test.mjs). The server's half is lib/agency_rep.js.
import { repPayload, agencyRepShown } from '../../lib/contractAgencyRep.js'
// 💰 Who is preselected as having taken the money — the server decides eligibility (1171); this
// only writes its answer into an empty field.
import { withFundReceiver, fundReceiverFields } from '../../lib/contractFundReceiver.js'
// 📅 Which day's exchange rate. „Payment date" appears only when one was recorded — see the
// module header for why a plausible substitute is worse than a missing button.
import { fxDateChoices, activeFxChoice } from '../../lib/contractFxDate.js'
import { SectionSlimRows } from './contract/SectionRows.jsx'
import { safeHtml } from '../../lib/safeHtml.js'
import { normalizePhone, contactProblems } from '../../lib/contactRules.js'
export { normalizePhone, contactProblems } from '../../lib/contactRules.js'

export const DEFAULT_TEMPLATE = 'rent_residential_v1'
const CURRENCIES = ['USD', 'GEL', 'EUR']
export const inp = 'rounded-md border border-input bg-background px-2 py-1 text-xs'

// Label in the UI language (defined in ./contract/paper.js, re-exported for the callers
// that always imported it from here).
export { ka, L } from './contract/paper.js'
export { rentalPaymentProblem } from '../../lib/rentalPaymentRoutes.js'

export const STATUS_TONE = {
  draft: 'neutral', sent: 'info', partially_signed: 'warn', signed: 'ok', declined: 'warn', void: 'neutral',
}

// Which surface an existing contract gets, from its DERIVED status (API §ContractDetail).
//   'editable' — draft / sent / declined: nobody signed this version, a save is a plain save
//   'partial'  — someone signed: read-only until the agent confirms discarding their signature
//   'final'    — signed or void: read-only for good
export function editability(status) {
  // 🔁 an expired revision (its seven-day window closed unsigned) is closed like a void one (core 3097)
  if (status === 'signed' || status === 'void' || status === 'expired') return 'final'
  if (status === 'partially_signed') return 'partial'
  return 'editable'
}

export function fmtWhen(iso) {
  if (!iso) return ''
  try { return new Date(iso).toLocaleString(ka() ? 'ka-GE' : 'en-GB', { dateStyle: 'short', timeStyle: 'short', hour12: false }) }
  catch { return String(iso).slice(0, 16).replace('T', ' ') }
}

// wa.me wants digits only; Georgian 9-digit numbers get 995 prepended (the convention in
// MatchSuggestions.jsx / ContactRow). No digits → the number-less form, the agent picks the chat.
export function waUrl(phone, text) {
  const digits = String(phone || '').replace(/\D/g, '')
  const num = digits.length === 9 ? `995${digits}` : digits
  return `https://wa.me/${num}?text=${encodeURIComponent(text)}`
}

// The message the party receives. Georgian when the document is Georgian (the common case);
// an English-only contract goes with the English sentence. Chosen by the CONTRACT's langs,
// not the agent's UI language — the reader is the party, not the agent.
// owner 2026-09-23: in the PARTY's link language — the same stored language their page opens in,
// so an English-side tenant gets an English message AND an English page (both follow the save)
export function messageFor(contract, party) {
  const langs = contract.langs || ['ka']
  const lang = langs.includes(party?.preferred_language) ? party.preferred_language : preferredOf(party, langs)
  const vars = { name: party.display_name || '', url: party.link?.url || '' }
  if (lang === 'ru') return `Здравствуйте, ${vars.name}. Пожалуйста, ознакомьтесь с договором и подпишите его по этой ссылке: ${vars.url} — senTavus`
  return tLog(lang === 'en' ? 'desk_contract_msg_text_en' : 'desk_contract_msg_text', vars)
}

// One value per field, in the shape the API takes back. Money rides as {value,currency}.
export function seedValues(fields, prefill) {
  const out = {}
  for (const f of fields || []) {
    const pre = prefill && Object.prototype.hasOwnProperty.call(prefill, f.key) ? prefill[f.key] : undefined
    if (f.kind === 'money') {
      const v = pre && typeof pre === 'object' ? pre : (f.value && typeof f.value === 'object' ? f.value : { value: pre ?? f.value ?? null, currency: f.currency || 'USD' })
      out[f.key] = { value: v.value ?? null, currency: v.currency || f.currency || 'USD' }
    } else if (f.kind === 'bool') {
      out[f.key] = pre !== undefined ? !!pre : !!f.value
    } else if (f.kind === 'multi') {
      // A checklist (utilities): the API's array of option values; the template's `default`
      // seeds a new contract (all the usual ones on), an older version's scalar is dropped.
      const src = Array.isArray(pre) ? pre : Array.isArray(f.value) ? f.value : Array.isArray(f.default) ? f.default : []
      out[f.key] = src.filter(x => typeof x === 'string')
    } else if (f.kind === 'agent') {
      out[f.key] = (pre && typeof pre === 'object' && pre.party_id) ? { party_id: pre.party_id, name: pre.name || '' }
        : (f.value && typeof f.value === 'object' && f.value.party_id) ? { party_id: f.value.party_id, name: f.value.name || '' }
        : null
    } else if (f.kind === 'prepay_list') {
      // ⚠️ A row says money was PAID, not merely negotiated. A new contract starts empty;
      // only an explicit import from a signed contract or the agent's own addition may say so.
      // Existing versions keep their stored list, including an empty one.
      const src = Array.isArray(pre) ? pre
        : f.source === 'contract' && f.source_ref?.status === 'signed' && Array.isArray(f.value) ? f.value
        : []
      out[f.key] = src.filter(r => r && typeof r === 'object').map(r => ({ ...r }))
    } else {
      out[f.key] = pre !== undefined ? pre : (f.value ?? (['initial_recipient_type', 'initial_completion_rule', 'initial_processing_days', 'initial_bank_charges', 'recurring_recipient_type', 'recurring_payment_method', 'recurring_completion_rule', 'recurring_processing_days', 'recurring_bank_charges'].includes(f.key) ? f.default : '') ?? '')
    }
  }
  if ((fields || []).some(f => f.key === 'initial_recipient_type')) {
    const oldMethod = { transfer: 'local_bank_transfer', app: 'card_or_electronic_payment', unrestricted: 'other' }
    out.payment_method = oldMethod[out.payment_method] || out.payment_method || 'local_bank_transfer'
  }
  return out
}

export function isBlank(f, v) {
  if (f.kind === 'money') return v == null || v.value == null || v.value === '' || Number.isNaN(Number(v.value))
  if (f.kind === 'bool') return false            // a checkbox is always an answer
  if (f.kind === 'agent') return !(v && typeof v === 'object' && v.party_id)
  // A row with no month CHOSEN is not an answer (owner 2026-09-21): the server drops it, so a
  // form showing rows without months saved a lease with no prepaid rent. Same rule as the
  // server's prepayMonthProblem: every row names a month, and at least one is a rent month.
  if (f.kind === 'prepay_list') {
    const rows = Array.isArray(v) ? v.filter(r => r && typeof r === 'object') : []
    const named = r => r.month === 'last' || r.month === 'deposit' || (/^[1-9]\d?$/.test(String(r.month ?? '')) && Number(r.month) <= 60)
    return rows.length === 0 || rows.some(r => !named(r)) || !rows.some(r => r.month !== 'deposit')
  }
  if (f.kind === 'multi') return !Array.isArray(v) || v.length === 0
  if (f.kind === 'rows') return filledRows(f, v).length === 0
  return v == null || String(v).trim() === ''
}

// The fields the form DRAWS: those whose `condition` holds against the current values
// (header, ruling 3) AND whose question still appears somewhere in the document (owner
// 2026-09-12 — a switched-off section takes its questions with it; see fieldsHiddenBySections).
// One definition, so the render and the missing-gate agree by construction: a field the form
// does not ask must not be a field the Save button waits for.
// ⚠️ `sections` is OPTIONAL and omitting it reproduces the old behaviour exactly — the callers
// that have no section list (the readiness probe on a bare draft) keep asking everything.
export const visibleFields = (fields, values, sections = null, langs = null) => {
  const hidden = sections ? fieldsHiddenBySections(sections) : null
  // ⚠️ A CHAIN, not one hop (2026-09-17): the advance asked "receiving account" because its
  // condition `receipt_method=transfer` held on a DEFAULT, while receipt_method itself was hidden.
  // A hidden field counts as empty (ruling D1), so prune until nothing changes — the same loop as
  // contract_render.js visibleValues, or the form asks what the paper will never print.
  const seen = { ...(values || {}) }
  // 🔁 a replacement edition's rent prepaid under the previous edition counts as a prepayment for which questions
  // exist — the same view as contract_render.js conditionView, or the paper prints an answer the form never shows
  const view = v => (v.revision_of && !(Array.isArray(v.prepayments) && v.prepayments.length) && Array.isArray(v.revision_carried_prepaid) && v.revision_carried_prepaid.length)
    ? { ...v, prepayments: v.revision_carried_prepaid } : v
  const conditional = (fields || []).filter(f => f && f.key && f.condition)
  for (let pass = 0; pass <= conditional.length; pass++) {
    let changed = false
    for (const f of conditional) {
      if (!(f.key in seen)) continue
      if (!fieldCondition(f.condition, view(seen))) { delete seen[f.key]; changed = true }
    }
    if (!changed) break
  }
  // The server's missing-field gate reads the same section rule (contract_sections.js
  // fieldsHiddenBySections, 2026-09-17), so a required field of a switched-off section is not asked.
  return (fields || []).filter(f => fieldCondition(f.condition, view(seen)) && !(hidden && hidden.has(f.key))
    // 🌐 A translation box (`for_lang`, 2026-09-21) is asked only when the contract has that
    // language — and then it is REQUIRED, like the server's freeTextLangProblem: contractual free
    // text is written by a person in every language, never machine-translated at signing.
    && !(f.for_lang && langs && !langs.includes(f.for_lang))
    // asked only when the owner-fault refund is below the advance (1255)
    && !(f.key === 'refund_reduction_reason' && !refundReduced(values)))
}

function suggestedBreakMonth(values) {
  const term = diffPeriod(values.lease_start, values.lease_end)
  return term?.exactMonths && term.months >= 2 ? Math.floor(term.months / 2) : null
}

export function fillBreakMonth(values, wasSuggested = false) {
  if (values.lease_model !== 'break') return wasSuggested
  const term = diffPeriod(values.lease_start, values.lease_end)
  const suggestion = suggestedBreakMonth(values)
  const current = Number(values.lease_break_after_months)
  if (suggestion && (wasSuggested || !Number.isInteger(current) || current < 1 || current >= term.months)) {
    values.lease_break_after_months = suggestion
    return true
  }
  return false
}

// The composer's state, from a draft (`contract-draft`) and — for an existing contract —
// the contract whose values pre-fill the form and whose id the save goes to.
const PAYMENT_PURPOSES = [
  ['first', 'პირველი თვის ქირა', 'First month rent'],
  ['last', 'ბოლო თვის ქირა', 'Last month rent'],
  ['deposit', 'უზრუნველყოფის დეპოზიტი', 'Security deposit'],
  ['first_last', 'პირველი + ბოლო თვის ქირა', 'First + last month'],
  ['first_deposit', 'პირველი თვე + დეპოზიტი', 'First month + deposit'],
  ['first_last_deposit', 'პირველი + ბოლო თვე + დეპოზიტი', 'First + last + deposit'],
  ['settlement', 'სააგენტოს ანგარიშსწორება', 'Agency settlement'],
  ['other', 'სხვა', 'Other'],
]
function paymentReference(draft, purpose) {
  const name = (draft?.parties || []).find(p => p.role === 'tenant')?.display_name || ''
  const address = draft?.meeting?.address || ''
  const label = PAYMENT_PURPOSES.find(p => p[0] === purpose)?.[1] || ''
  return [name, label, address].filter(Boolean).join(' · ')
}
function paymentPurposeOf(values) {
  const months = new Set((values.prepayments || []).map(r => String(r.month)))
  const first = months.has('1'), last = months.has('last'), deposit = Number(values.deposit?.value) > 0
  if (first && last && deposit) return 'first_last_deposit'
  if (first && last) return 'first_last'
  if (first && deposit) return 'first_deposit'
  if (first) return 'first'
  if (last) return 'last'
  if (deposit) return 'deposit'
  return 'first'
}
export function composerStateFrom(draft, rerenderOf = null) {
  // ⚖️ a template still in legal review is drafted in every language it is written in — the server accepts those
  //    (langsOk) and refuses signing instead; its translations are not "reviewed", nothing about it is yet
  const reviewed = (draft.template?.legal_review === 'pending' ? draft.template?.langs_available : draft.template?.langs_reviewed) || []
  const prefill = rerenderOf?.current_version?.values || null
  const seeded = seedValues(draft.fields, prefill)
  const values = { ...seeded }
  const ownerId = (draft.parties || []).find(p => p.role === 'landlord')?.party_id
  const accountSelection = {}
  const accountFor = (partyId, iban) => {
    const list = (draft.payment_accounts || []).filter(a => a.party_id === partyId)
    return list.find(a => String(a.iban).replace(/\s/g, '') === String(iban || '').replace(/\s/g, ''))
      || list.find(a => a.is_default && a.verified_at)
      || [...list].filter(a => a.last_used_at).sort((a, b) => String(b.last_used_at).localeCompare(String(a.last_used_at)))[0]
  }
  for (const [stage, ibanKey, bankKey, holderKey, swiftKey, partyId] of [
    ['initial', 'payment_account', 'payment_bank', 'payment_account_holder', 'initial_payment_swift', values.initial_recipient_type === 'owner' ? ownerId : null],
    ['recurring', 'owner_payment_iban', 'owner_payment_bank', 'owner_payment_account_holder', 'owner_payment_swift', ownerId],
  ]) {
    const existing = values[ibanKey]
    const account = partyId && accountFor(partyId, existing)
    if (account && existing && String(account.iban).replace(/\s/g, '') === String(existing).replace(/\s/g, '')) accountSelection[stage] = account.id
    if (account && !existing) {
      Object.assign(values, { [ibanKey]: account.iban, [bankKey]: account.bank_name, [holderKey]: account.account_holder, [swiftKey]: account.swift })
      accountSelection[stage] = account.id
    }
  }
  const ownerName = (draft.parties || []).find(p => p.role === 'landlord')?.display_name || ''
  if (!values.payment_recipient && values.initial_recipient_type === 'owner') values.payment_recipient = ownerName
  const inferredPurpose = paymentPurposeOf(values)
  const initialPurpose = values.payment_reference && !String(values.payment_reference).includes(PAYMENT_PURPOSES.find(p => p[0] === inferredPurpose)?.[1] || '')
    ? 'other' : inferredPurpose
  if (!values.payment_reference && initialPurpose !== 'other') values.payment_reference = paymentReference(draft, initialPurpose)
  // The term is set by the dates, so the model is one question: an early-exit right or not
  // (owner 2026-09-17). A form that never asked it is a plain fixed term.
  if ((draft.fields || []).some(f => f.key === 'lease_model') && !values.lease_model) values.lease_model = 'full'
  const suggestedBreak = fillBreakMonth(values)
  return {
    draft, rerenderOf,
    paymentAccountSelection: accountSelection,
    paymentPurpose: initialPurpose,
    // A NEW lease starts monthly rent "the same way as at signing" (owner 2026-09-17); a saved one
    // reads what it stored — the template's bank-transfer default is not somebody's answer.
    paymentMonthlyRoute: rerenderOf ? null : 'same',
    // 🚪 THE OPENING (owner 2026-09-19): four questions before the lease form. A saved lease reads its
    // answers back from what it stored and never asks again; a new one starts with all four open.
    opening: openingQuestions(draft.fields).length
      ? (rerenderOf ? openingFromValues(values, { monthlySame: monthlyRouteOf(values) === 'same' }) : {})
      : null,
    openingDone: !!rerenderOf || !openingQuestions(draft.fields).length,
    // …and the signing route is NOT pre-answered: the template's "local bank transfer" default is
    // not the agent's answer either, and pre-selecting it is how an agent ended at an IBAN nobody asked.
    paymentSigningChosen: !!rerenderOf,
    paymentPurposeTouched: initialPurpose === 'other',
    // The contract this one is drawn from (1056) — the draft's pick, sent on create.
    related_contract_id: rerenderOf?.related?.id || draft.related?.id || null,
    values: withFundReceiver(values, draft),
    breakAuto: suggestedBreak,
    // The SAME seed, kept frozen. A field's `source` names the rung that supplied its value,
    // and the moment the agent edits that value the claim stops being true — which is the whole
    // of the owner's 2026-09-12 report: a tag reading „ოფისის სტანდარტი" over a figure that was
    // not the standard any more. Comparing against this is the only way to know.
    seeded: withFundReceiver(seeded, draft),
    // The overrides this contract already carries (its current version's), and the reasons the
    // agent is typing right now. They start as the same thing: reopening a contract must show the
    // sentence somebody wrote, not an empty box demanding it again.
    overrides: rerenderOf?.current_version?.overrides || {},
    overrideReasons: Object.fromEntries(Object.entries(rerenderOf?.current_version?.overrides || {}).map(([k, e]) => [k, e?.reason || ''])),
    // Georgian first when it is reviewed; otherwise whatever IS reviewed; never a language
    // the server would refuse (`lang_not_reviewed`).
    // …plus, on a NEW contract, every party's inherited link language that is reviewed
    // (owner 2026-09-11: the client's language from the fill form is the key — a
    // Russian-speaking tenant's contract starts as ka+ru, not ka with a Georgian link).
    // 🔒 GEORGIAN IS ALWAYS IN THE SET (owner ruling 2026-09-16, review L-01) — the API refuses a
    //    contract without it (lib/contract_langs.js), so the form never starts without it either.
    langs: rerenderOf ? orderLangs(['ka', ...(rerenderOf.langs || []).filter(l => reviewed.includes(l))])
      : orderLangs(['ka',
        ...(draft.parties || []).map(p => p.preferred_language).filter(l => l && reviewed.includes(l))]),
    // 🪑 co-owners are several seats of one role (lib/contractSeats.js); a saved contract's row id rides along
    parties: withSeats((rerenderOf?.parties || draft.parties || []).map(p => ({
      role: p.role, seat: p.seat ?? null, row_id: rerenderOf ? (p.id || null) : null, party_id: p.party_id ?? null, required: p.required !== false,
      // ONE canonical name as on the ID (header). Older rows carry only `display_name`,
      // which was the same fact under the previous shape.
      legal_name: p.legal_name || p.display_name || '',
      // The optional spellings per language (header); never a transliteration.
      names: { ka: p.names?.ka || '', en: p.names?.en || '', ru: p.names?.ru || '' },
      // Null = "the first language of the contract" — resolved at render/send time by
      // `preferredOf`, so a language dropped from the set never leaves a dangling choice.
      preferred_language: p.preferred_language || null,
      preferred_language_chosen: p.preferred_language_chosen === true,
      // Identity + contact (header, migration 1051). The number arrives in clear for the
      // agent who entered it (detail) — never from a draft, which carries only the contact.
      kind: p.kind === 'company' ? 'company' : 'person',
      id_kind: p.id_kind || null, id_number: p.id_number || '',
      rep_name: p.rep_name || '', rep_position: p.rep_position || '',
      // WHO signs for the agency (owner ruling 2026-09-12). The server decides this; the
      // composer only carries the id back so an authorised agent's switch survives the save.
      rep_party_id: p.rep_party_id || null,
      phone: p.phone || '', email: p.email || '',
      share_phone: p.share_phone !== false, share_email: p.share_email !== false,
      identity_confirmed_at: p.identity_confirmed_at || null,
      // where the draft's hints came from (meeting | fill_form | requirement) + the stated
      // nationality — shown, never sent
      source: p.source || null, nationality: p.nationality || null,
    }))),
  }
}

// The language SET, kept in the API's canonical order: ka, en, ru filtered by what is
// checked. The first of the result is the prevailing text (API §"One contract, many
// languages"). Nothing here privileges Georgian beyond its position in the order.
export const LANG_ORDER = ['ka', 'en', 'ru']
export const orderLangs = langs => LANG_ORDER.filter(l => (langs || []).includes(l))
// Georgian cannot be switched off: it is the legally authoritative text (owner ruling 2026-09-16).
export const toggleLang = (langs, l, on) => orderLangs(['ka', ...(on ? [...(langs || []), l] : (langs || []).filter(x => x !== l))])

// The party's link language, resolved: their choice when it is one of the contract's
// languages, else the contract's first language (the server's own fallback for an absent
// `preferred_language`, so both ends agree).
export function preferredOf(p, langs) {
  const L = orderLangs(langs)
  // 🌐 owner 2026-09-23: "the party on the English side gets the English version automatically".
  // Until the agent picks a language by hand, the default follows the person — a name in Georgian
  // script → ka, Cyrillic → ru, Latin letters or a passport → en — and replaces the blanket first
  // language (every party used to be saved as ka, so a Latin-named tenant's link opened in Georgian).
  // A requirement hint other than the first language (ru/en from the client's demand) is kept.
  if (!p?.lang_chosen && !p?.preferred_language_chosen && (!L.includes(p?.preferred_language) || p.preferred_language === L[0])) {
    const g = inferredLangOf(p, L)
    if (g) return g
  }
  return L.includes(p?.preferred_language) ? p.preferred_language : (L[0] || 'ka')
}

export function inferredLangOf(p, L) {
  const name = [p?.legal_name, p?.display_name].filter(Boolean).join(' ')
  if (/[Ⴀ-ჿ]/.test(name)) return L.includes('ka') ? 'ka' : null
  if (/[Ѐ-ӿ]/.test(name)) return L.includes('ru') ? 'ru' : null
  if (/[A-Za-z]/.test(name) || p?.id_kind === 'passport') return L.includes('en') ? 'en' : null
  return null
}

// `parties[].names` as the API takes it: ONLY the non-blank spellings, ONLY for the
// contract's languages (a spelling typed for a language later unchecked is not sent).
export function partyNamesPayload(p, langs) {
  const names = {}
  for (const l of orderLangs(langs)) {
    const v = String(p.names?.[l] || '').trim()
    if (v) names[l] = v
  }
  return names
}

// One party as the API takes it on create AND on save (header): the canonical name, the
// spellings, the link language — plus `display_name` = legal name for older readers.
export function partyPayload(p, langs, { withId = false } = {}) {
  const legal = String(p.legal_name || '').trim()
  const out = { role: p.role, seat: seatOf(p), ...(p.row_id ? { id: p.row_id } : {}), legal_name: legal, display_name: legal,
    names: partyNamesPayload(p, langs), preferred_language: preferredOf(p, langs),
    // a hand-picked link language is remembered server-side, so a reload never re-infers over it
    preferred_language_chosen: !!(p.lang_chosen || p.preferred_language_chosen),
    // identity + contact (header): a blank ID is sent as blank (the party fills it in on
    // their link); the sharing rule always rides along.
    kind: p.kind === 'company' ? 'company' : 'person',
    id_kind: String(p.id_number || '').trim() ? (p.kind === 'company' ? 'company_reg' : (p.id_kind || 'personal_no')) : '',
    id_number: String(p.id_number || '').trim(),
    // 🔒 THE AGENCY'S REPRESENTATIVE IS AN ID, NOT A NAME (owner 2026-09-12). `repPayload` is
    // the tested rule: for that one role it sends `rep_party_id` and nothing else about the
    // signature, and for every other company it sends the representative as before.
    ...repPayload(p),
    phone: String(p.phone || '').trim(), email: String(p.email || '').trim(),
    share_phone: p.share_phone !== false, share_email: p.share_email !== false,
    // 🏠 a resident is listed, never signs (the server sets this too — it does not trust the client)
    required: p.role === 'resident' ? false : p.required !== false }
  if (withId && p.party_id) out.party_id = p.party_id
  return out
}


// The script a name is written in — 'ka' (Mkhedruli), 'ru' (Cyrillic), 'en' (Latin) — by
// its first letter, or null when there is none. Drives which spelling boxes are drawn
// (header: never duplicate the same script).
export function scriptOf(name) {
  const m = String(name || '').match(/\p{L}/u)
  if (!m) return null
  const ch = m[0]
  if (/\p{Script=Georgian}/u.test(ch)) return 'ka'
  if (/\p{Script=Cyrillic}/u.test(ch)) return 'ru'
  if (/\p{Script=Latin}/u.test(ch)) return 'en'
  return null
}
// The languages that get an optional spelling box: the chosen set minus the legal name's own
// script.
export const spellingLangs = (p, langs) => { const sc = scriptOf(p?.legal_name); return orderLangs(langs).filter(l => l !== sc) }
export const ID_KINDS_PERSON = ['personal_no', 'passport']
export const ID_KINDS_ALL = ['personal_no', 'passport', 'company_reg']

// `e.message` is the server's error CODE (coreApi carries `data.error`); the codes the
// contract names get their own sentence, anything else is shown as it came.
export { fundReceiverFields }

/** The page's own sentences for page-level refusals; null when the code is not one of them. */
export function pageErrorSentence(code) {
  return code === 'not_authorised_to_receive_funds' || code === 'fund_receiver_not_authorised' ? t('desk_contract_err_fund_receiver')
    : code === 'invalid_advance_amount' ? t('desk_contract_err_advance_amount')
    : code === 'lang_not_reviewed' ? t('desk_contract_err_lang')
    : code === 'georgian_required' ? t('desk_contract_lang_ka_required')
    : code === 'signed' ? t('desk_contract_err_signed')
    : code === 'signed_partially' ? t('desk_contract_err_partial')
    : code === 'translation_drift' ? t('desk_contract_err_drift')
    : null
}
// ONE wording for every contract refusal (2026-09-29, "fix it on every error"): this used to keep a
// second, shorter chain of its own that ignored the part the server named. It now asks
// contractErrors.js, with the page's own sentences as `known`; the title is the one-line form, and
// `contractErrorDetail` is the full form (title + the named parts) for the error banner.
export function contractErrorDetail(e, fields = []) {
  return describeContractError(e, { fields, lang: normLang(getLanguage()), known: pageErrorSentence })
}
export function contractErrorText(e, fields = []) {
  const d = contractErrorDetail(e, fields)
  return d.items.length ? `${d.title} ${d.items.map(x => x.label).join(' · ')}` : d.title
}

// The `issues[]` of a `translation_drift` refusal (API §"One contract, many languages":
// `{clause_no, kind:'missing_text'|'placeholders', lang, detail}`), read off the error
// body coreApi now carries (`err.body`). Anything else → an empty list.
export function driftIssuesOf(e) {
  if (e?.message !== 'translation_drift') return []
  const rows = e?.body?.issues
  return Array.isArray(rows) ? rows.filter(r => r && typeof r === 'object') : []
}

// The drift list, drawn on the paper under the section rows: one line per issue, NAMING the
// clause and the placeholder ("Clause 3b · {{prepay_summary}} missing in English" — round 1
// decision 9). The same sentence is drawn inline in that clause's editor. Pure presentation;
// the page owns the state.
export function DriftIssues({ issues }) {
  if (!issues || issues.length === 0) return null
  return (
    <div className="rounded-lg border border-red-700/40 bg-red-700/5 px-3 py-2 text-[12px] space-y-1 text-[#14130F]" data-testid="contract-drift">
      <div className="font-medium text-red-700">{t('desk_contract_drift_title')}</div>
      <ul className="space-y-0.5">
        {issues.map((it, i) => <li key={`${it.clause_no}-${it.lang}-${i}`}>{driftIssueText(it)}</li>)}
      </ul>
    </div>
  )
}

// ── Readiness: the ONE definition the sticky bar and the submit share ──────────────────
// Only the fields whose condition holds are drawn, and only those can be missing (header,
// ruling 3). `missing` carries the LABELS, because the bar names them ("2 fields left: lease
// start, payment method" — round 1 decision 9).
// ☎️ ONE STORED SHAPE FOR EVERY NUMBER (owner 2026-09-12: "we need international number
// normalization"). Agents type +995 591 32 86 86, 591328686, 00995591328686 and 995-591-32-86-86
// for the same phone, and a contract that stores four spellings of one number cannot match a
// party to a client, cannot dial from a link, and cannot tell two people apart from one person
// who typed differently. And every guess about LENGTH was a guess about which country the number
// belonged to — which is exactly what normalising removes: once the country code is explicit, the
// rule is simply "+ then 8–15 digits", the same for Tbilisi and Tel Aviv.
//
// Georgia is the assumed home country and the only assumption made: a bare 9-digit number becomes
// +995…, because that is what an agent in this office types when they do not think about it. Any
// number that names its own country — with +, with 00, or with a leading 995 — is taken at its
// word and never re-homed.
// ⚠️ Normalising is NOT validating. A number that cannot be normalised is returned unchanged so
//    the agent still sees what they typed and the field can say why it is refused; silently
//    rewriting somebody's input into something that passes is how a wrong number gets signed.


// 🗺️ A FIELD'S VALUE CAN BE THE WRONG SHAPE WITHOUT BEING EMPTY (owner 2026-09-12, over a
// cadastral code reading "Hsshh"). The required-field gate asks only whether something is there.
// A cadastral code either identifies a parcel in the public registry or identifies nothing, and
// nonsense in a contract's property clause is worse than a blank line because it reads as an
// answer — a reader checks the registry, finds nothing, and doubts the whole document.
// SHAPE ONLY, never existence: 2–7 dot-separated numeric groups. Whether the parcel exists is the
// registry's business and this form has no way to know.
// ⚠️ Keyed by field, so the next format (an IBAN, a registration number) is one entry here rather
//    than another special case wired into the row.
const VALUE_SHAPES = {
  cadastral_code: v => /^[0-9]{1,3}(\.[0-9]{1,4}){1,6}$/.test(v),
}
export function valueProblem(f, v) {
  if (['protection_months', 'guarantee_months', 'guarantee_weeks', 'lease_break_after_months', 'routine_inspection_interval_months', 'routine_inspection_notice_hours', 'cure_days', 'binder_handover_days'].includes(f?.key) && v != null && v !== '' && (!Number.isInteger(Number(v)) || Number(v) <= 0)) return true
  // 🔴 AN ENUM VALUE THIS TEMPLATE VERSION DOES NOT HAVE (owner 2026-09-13, from a real contract
  // that printed „სამეწარმეო საქმიანობა: home_office"). His document is frozen on a version whose
  // option list predates that value; the renderer's lookup missed and the machine key went onto a
  // party-facing page. The renderer now prints „—" instead, but that is the floor — the repair is
  // here, where the value can still be changed to one the document can actually say.
  // ⚠️ Blank is not an unknown value; the required-field gate speaks for an empty box.
  if (f?.kind === 'enum' && Array.isArray(f.options) && f.options.length) {
    const s = typeof v === 'string' ? v.trim() : v == null ? '' : String(v)
    if (s && !f.options.some(o => o.value === s)) return true
  }
  const rule = VALUE_SHAPES[f?.key]
  if (!rule) return false
  const s = typeof v === 'string' ? v.trim() : ''
  return !!s && !rule(s)
}

// ☎️ A CONTACT MAY BE ABSENT; IT MAY NOT BE NONSENSE (owner 2026-09-12, over a party carrying the
// phone "5913286865455484" and the email "dhsdjjs.hjs"). Contact is deliberately outside the
// identity gate — a party can be recorded without one — but "not required" and "anything goes"
// are different rules and only the first was implemented. These two fields are how a signing link
// REACHES a person: a malformed email means the link is never delivered and nothing says so, and
// a 16-digit phone means nobody can ring to ask why.
// The check is deliberately loose: it rejects what cannot be a contact, not what looks unusual.
// A phone needs 5–15 digits once punctuation is stripped (15 is E.164's own ceiling) — a Georgian
// mobile is 9, with or without +995, and both pass. Guessing harder would reject real foreign
// numbers, and the client on the other side of these contracts is often foreign.

// 🪪 TWO PARTIES TO A CONTRACT ARE NOT THE SAME PERSON (owner 2026-09-12: "I was able to write
// similar ids in both parties" — both sides carrying 01000000000). Nothing anywhere refused it:
// the identity gate asks whether each party HAS an id, never whether they are different people.
// A lease where the landlord and the tenant hold one personal number is not a contract, it is a
// data-entry accident that would reach a signing page and then a court.
// ⚠️ Compared within an id KIND: a passport number and a company registration code may coincide
//    as strings without meaning anything. Digits only, so spacing and dashes cannot hide a match.
// ⚠️ The 11-digit rule applies to a Georgian personal number ONLY — the field's own label already
//    promises „11 ციფრი". A passport is deliberately unchecked; formats differ by country and
//    rejecting a real one is worse than accepting an odd one.
export function identityProblems(parties) {
  const bad = new Map()   // partyKey (role, or role#seat for a co-owner) -> Set of problems
  const add = (role, what) => { if (!bad.has(role)) bad.set(role, new Set()); bad.get(role).add(what) }
  const seen = new Map()  // "kind:digits" -> first role that used it
  for (const p of (parties || [])) {
    const num = String(p?.id_number || '').replace(/\s|-/g, '')
    if (!num) continue
    if (p?.id_kind === 'personal_no' && !/^[0-9]{11}$/.test(num)) add(partyKey(p), 'id_format')
    const key = `${p?.id_kind || '?'}:${num}`
    if (seen.has(key)) { add(partyKey(p), 'id_duplicate'); add(seen.get(key), 'id_duplicate') }
    else seen.set(key, partyKey(p))
  }
  return bad
}

// 💯 A COMPOSITION THAT DOES NOT ADD UP (owner 2026-09-13: „$1,000 ერთ სამართლებრივ კალათაში
// აღარ ჯდება"). This replaces the 100% check, and it is the same rule one layer down: the
// percentages split ONE sum, so the pair had to reach 100; the components are NAMED AMOUNTS with
// different legal natures (migration 1149), so the parts have to reach the sum that was paid.
// ⚠️ THE CUSTODY FIGURE IS NOT A PART. `part_held_by_agency` says how much of the advance is
//    sitting with senTavus; adding it would count the same money twice, and both of the owner's
//    scenarios (500+500 with no custody, or 1,000 advance of which 500 is held) must pass.
// ⚠️ A CURRENCY MISMATCH IS ITS OWN PROBLEM, not a wrong total — adding across currencies would
//    invent a rate, the same reason binder-rules refuses to subtract one (2026-08-06).
// ⚠️ Silent while the total or the advance part is empty: a half-typed composition is not yet a
//    contradiction, and the required-field gate already speaks for an empty box.
// ⚖️ INERT SINCE 1179, AND KEPT ON PURPOSE. The two parts were removed from the advance when Civil
// Code 744–745(2) barred an advance on brokerage remuneration (pending counsel). `componentProblems`
// skips a composition whose fields the template lacks, so this entry checks nothing today. It stays
// because the day those fields return, their arithmetic check must already be waiting for them —
// re-adding a money composition without its sum check is how 1149's $200 loss would ship.
const COMPOSITIONS = [{ total: 'advance_amount', parts: ['part_advance', 'part_commission_prepaid'] }]
const moneyNum = v => {
  const raw = v && typeof v === 'object' ? v.value : v
  if (raw == null || String(raw).trim() === '') return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}
const moneyCur = v => (v && typeof v === 'object' ? v.currency : null) || null
export function componentProblems(fields, values) {
  const bad = new Map()
  const has = k => (fields || []).some(f => f.key === k)
  for (const c of COMPOSITIONS) {
    const present = c.parts.filter(has)
    if (!has(c.total) || present.length === 0) continue
    const total = moneyNum(values?.[c.total])
    if (total == null) continue
    const filled = present.filter(k => moneyNum(values?.[k]) != null)
    // the FIRST part carries the composition: with it empty there is nothing to check against
    if (!filled.includes(present[0])) continue
    const cur = moneyCur(values?.[c.total])
    const wrongCur = cur ? filled.filter(k => moneyCur(values?.[k]) && moneyCur(values?.[k]) !== cur) : []
    if (wrongCur.length) { for (const k of wrongCur) bad.set(k, { why: 'currency' }); continue }
    const sum = filled.reduce((a, k) => a + moneyNum(values?.[k]), 0)
    // a hundredth of a unit: these are hand-typed decimals, not exact binary fractions
    if (Math.abs(sum - total) > 0.005) {
      for (const k of [c.total, ...filled]) bad.set(k, { why: 'sum', sum, total, currency: cur })
    }
  }
  return bad
}

// ONE sentence for both ends of the problem, because the bar and the field must say the same
// thing — the pair is what is wrong, so neither number is named as the mistaken one.
const partSentence = why => why.why === 'currency'
  ? t('desk_contract_parts_currency')
  : tLog('desk_contract_parts_total', { n: `${why.sum} ${why.currency || ''}`.trim(), m: `${why.total} ${why.currency || ''}`.trim() })
// Mixed currencies have no defensible balance without an agreed exchange rate — since 2026-09-23 the
// server REFUSES them ('currency' / 'cobroke_currency'), so commissionProblem blocks readiness too.
export const commissionProblem = values => commissionSettlementProblem(values)

export function tenancyProblem(values) {
  if (!['full', 'short', 'break'].includes(values?.lease_model)) return 'lease_model'
  const diff = diffPeriod(values.lease_start, values.lease_end)
  if (!diff) return 'lease_end'
  if (values.lease_model === 'break') {
    if (!diff.exactMonths) return 'lease_end'
    const n = Number(values.lease_break_after_months)
    if (!Number.isInteger(n) || n < 1 || n >= diff.months) return 'lease_break_after_months'
    const notice = Number(values.tenant_notice_days)
    if (!Number.isInteger(notice) || notice < 1) return 'tenant_notice_days'
  }
  return null
}

// `rentalPaymentProblem` moved to src/lib/rentalPaymentRoutes.js (2026-09-21) so it can be
// unit-tested: it is the mirror of the server's paymentRulesProblem and guards money, and a rule
// that cannot be run outside a browser is a rule nobody proves. Re-exported below for callers.

// A party's name on the page: its role, and for a co-owner its seat („მესაკუთრე 2").
export const partyWho = (p, parties = null) => {
  // 🏠 residents are numbered from 2 by their place in the list (the tenant is person 1), as the lease prints them
  if (p?.role === 'resident') {
    const at = (parties || []).filter(x => x?.role === 'resident').indexOf(p)
    return `${tOr('desk_contract_role_resident', 'resident')} ${at >= 0 ? at + 2 : seatOf(p) + 1}`
  }
  return `${tOr(`desk_contract_role_${p.role}`, p.role)}${seatOf(p) > 1 ? ` ${seatOf(p)}` : ''}`
}
// What a range problem says, in the reader's language: the bounds, or the date it must follow.
export function rangeSentence(r, fields = []) {
  if (r?.after) return tLog('desk_contract_range_after', { f: L((fields || []).find(x => x.key === r.after) || {}, 'label') || r.after })
  if (r?.min != null && r?.max != null) return tLog('desk_contract_range_between', { min: r.min, max: r.max })
  if (r?.min != null) return tLog('desk_contract_range_min', { min: r.min })
  return tLog('desk_contract_range_max', { max: r?.max })
}

/** 🏠 The system's English/Russian for an address translation box (`address_en`…), or null — then the box is required. */
export const addressAutoFor = (state, f) => (f?.translation_of && f?.for_lang && state?.addressAuto?.[f.translation_of]?.[f.for_lang]) || null

export function composerReadiness(state) {
  const { draft, values, langs, parties } = state
  // …and the same section list the form draws from, so Save never waits for a question the
  // agent can no longer see (owner 2026-09-12).
  const shown = visibleFields(draft?.fields || [], values, state.sections || null, langs || ['ka'])
  const missing = shown.filter(f => (f.required || (f.for_lang && !addressAutoFor(state, f))) && isBlank(f, values[f.key])).map(f => ({ key: f.key, label: L(f, 'label') || f.key }))
  // 🔒 THE IDENTITY IS THE AGENT'S JOB AND IT IS REQUIRED (owner 2026-09-11: "this part should be
  // required — personal numbers too, from agent, to fill before sending for signature; on
  // signature they must not be able to modify it themselves"). A party is incomplete until it
  // carries a name, an ID number and — for a company — the representative. The server refuses
  // the same three at create, at mint-links and at sign; this is the copy that says so BEFORE
  // the agent presses the button. Contact is NOT identity and is not gated here.
  const partyMissing = []
  for (const p of (parties || [])) {
    if (!p.required) continue
    const who = partyWho(p)
    const isCompany = p.kind === 'company'
    const pk = partyKey(p)
    if (!String(p.legal_name || '').trim()) partyMissing.push({ key: `party:${pk}:name`, label: `${who} — ${t(isCompany ? 'desk_contract_company_name' : 'desk_contract_legal_name')}` })
    if (!String(p.id_number || '').trim()) partyMissing.push({ key: `party:${pk}:id`, label: `${who} — ${t(isCompany ? 'desk_contract_id_company_reg' : 'desk_contract_id_number')}` })
    if (isCompany && !String(p.rep_name || '').trim()) partyMissing.push({ key: `party:${pk}:rep`, label: `${who} — ${t('desk_contract_rep_name')}` })
  }
  // A wrong answer is not a MISSING one — the agent filled it in — so these are their own list
  // and their own sentence. Folding them into `missing` would tell somebody to complete a field
  // that already has something in it.
  const partyInvalid = []
  const idBad = identityProblems(parties)
  for (const p of (parties || [])) {
    const who = partyWho(p, parties)
    const pk = partyKey(p)
    // 🏠 a resident is optional, but one with ANY detail is sent, and a listed person needs a name
    if (p.role === 'resident' && !residentEmpty(p) && !String(p.legal_name || '').trim())
      partyInvalid.push({ key: `party:${pk}:name`, label: `${who} — ${t('desk_contract_legal_name')}` })
    for (const bad of contactProblems(p)) partyInvalid.push({ key: `party:${pk}:${bad}`, label: `${who} — ${t(`desk_contract_${bad}`)}` })
    for (const bad of (idBad.get(pk) || [])) partyInvalid.push({ key: `party:${pk}:id`, label: `${who} — ${t(`desk_contract_${bad}`)}` })
  }
  // 🏠 more residents than the lease's number admits (the tenant is person 1) — the server's residentsProblem, said first
  {
    const res = (parties || []).filter(p => p.role === 'resident' && !residentEmpty(p)).length
    const max = Number(values?.max_residents)
    if (res && shown.some(f => f.key === 'max_residents') && Number.isFinite(max) && max >= 1 && res > max - 1)
      partyInvalid.push({ key: 'max_residents', label: refusalSentence('residents_exceed_max', normLang(getLanguage())) })
  }
  // 🏠 „all co-owners sign" needs two or more owner seats, „sole owner" exactly one — the server's
  // ownerAuthorityProblem, said before Save
  if (shown.some(f => f.key === 'owner_authority')) {
    const owners = (parties || []).filter(p => p.role === 'owner').length
    if (values.owner_authority === 'co_owners_all_sign' && owners < 2) partyInvalid.push({ key: 'owner_authority', label: t('desk_contract_co_owners_must_sign') })
    if (values.owner_authority === 'sole' && owners > 1) partyInvalid.push({ key: 'owner_authority', label: t('desk_contract_owner_authority_mismatch') })
  }
  // A value in the wrong SHAPE is refused the same way (the cadastral rule), and so is a local
  // override with no sentence beside it: the owner's condition for allowing an override at all
  // is that it says why, and a save is the moment it stops being a keystroke and becomes one.
  for (const f of shown) {
    if (valueProblem(f, values[f.key])) partyInvalid.push({ key: f.key, label: L(f, 'label') || f.key })
    // 🔢 the spec's own limits (contract_field_rules.js on the server refuses the same)
    const range = rangeProblem(f, values[f.key], values)
    if (range) partyInvalid.push({ key: `range:${f.key}`, label: `${L(f, 'label') || f.key} — ${rangeSentence(range, shown)}` })
    if (f.kind === 'rows' && rowsProblems(f, values[f.key]).length) partyInvalid.push({ key: `rows:${f.key}`, label: `${L(f, 'label') || f.key} — ${t('desk_contract_rows_incomplete')}` })
  }
  // A date that contradicts another date, and a split that does not reach 100. Both are named at
  // BOTH ends: neither number is the wrong one on its own, and the agent has to see the pair to
  // know which to change.
  // only VISIBLE answers: a stale lease_start left behind when the purpose became a purchase, or a
  // cleared contingency's deadline, is not an answer and may not contradict anything
  const dateBad = dateProblems(Object.fromEntries(shown.map(f => [f.key, values[f.key]])))
  for (const f of shown) {
    const why = dateBad.get(f.key)
    if (why) partyInvalid.push({ key: `date:${f.key}`, label: `${L(f, 'label') || f.key} — ${t(`desk_contract_${why}`)}` })
  }
  // A-01: a portion of the advance may not exceed it or change currency — refused by the API too
  const amountBad = advanceAmountProblems(values)
  for (const f of shown) {
    const why = amountBad.get(f.key)
    if (why) partyInvalid.push({ key: `amount:${f.key}`, label: `${L(f, 'label') || f.key} — ${t(`desk_contract_${why}`)}` })
  }
  const partBad = componentProblems(shown, values)
  for (const [k, why] of partBad) {
    const f = shown.find(x => x.key === k)
    partyInvalid.push({ key: `part:${k}`, label: `${L(f || {}, 'label') || k} — ${partSentence(why)}` })
  }
  if (shown.some(f => f.key === 'commission_status') && commissionProblem(values)) {
    partyInvalid.push({ key: 'commission_status', label: t('desk_contract_commission_mismatch_bar') })
  }
  if (shown.some(f => f.key === 'lease_model')) {
    const problem = tenancyProblem(values)
    if (problem) partyInvalid.push({ key: problem, label: problem === 'lease_break_after_months'
      ? t('desk_contract_break_month_bar')
      : problem === 'tenant_notice_days' ? t('desk_contract_notice_days_bar')
      : t('desk_contract_lease_model_bar') })
  }
  // 2026-09-17: an earlier payment credited to a first month nobody pays at signing, and a binder
  // credited to a price or deposit the deal does not have — both refused only by the server before.
  if (shown.some(f => f.key === 'prior_payment_credited_against')) {
    const pc = priorCreditProblem(values)
    if (pc) partyInvalid.push({ key: 'prior_payment_credited_against', label: refusalSentence(pc, normLang(getLanguage())) })
  }
  if (shown.some(f => f.key === 'utilities_list') && shown.some(f => f.key === 'service_charge_mode') && serviceChargeTwiceProblem(values))
    partyInvalid.push({ key: 'utilities_list', label: refusalSentence('building_service_charged_twice', normLang(getLanguage())) })
  if (shown.some(f => f.key === 'address') && addressScriptProblem(values))
    partyInvalid.push({ key: 'address', label: refusalSentence('address_not_georgian', normLang(getLanguage())) })
  if (shown.some(f => f.key === 'binder_credited_to')) {
    const bc = binderCreditProblem(values)
    if (bc) partyInvalid.push({ key: bc === 'binder_credit_deposit_missing' ? 'deposit' : 'binder_credited_to', label: refusalSentence(bc, normLang(getLanguage())) })
  }
  // 🏷️ the owner mandate's figures agree with each other (review 2026-10-09; server: lib/mandate_rules.js)
  if ((draft?.fields || []).some(f => f.key === 'sale_commission_percent')) {
    const mp = mandateProblem(Object.fromEntries(shown.map(f => [f.key, values[f.key]]).concat([['deal_scope', values.deal_scope]])))
    if (mp) partyInvalid.push({ key: mp.field, label: refusalSentence(mp.code, normLang(getLanguage())) })
  }
  // 📅 THE PAYMENT CALENDAR AND THE REMOTE LEASE (2026-09-17) — both refused only by the server before,
  // as invalid_payment_calendar / invalid_remote_rental. Ported line for line (contractChecks.js), run on
  // the same raw values and for the same templates the server runs them for.
  const lang0 = normLang(getLanguage())
  const tplHas = k => (draft?.fields || []).some(f => f.key === k)
  const calendarCode = tplHas('initial_recipient_type') ? paymentCalendarProblem(values) : null
  const pcSaid = shown.some(f => f.key === 'prior_payment_credited_against') ? priorCreditProblem(values) : null
  if (calendarCode && calendarCode !== pcSaid)
    partyInvalid.push({ key: `calendar:${calendarCode}`, label: refusalSentence(calendarCode, lang0) || refusalSentence('invalid_payment_calendar', lang0) })
  const remoteCode = tplHas('remote_transaction') ? remoteProblem(values) : null
  if (remoteCode && !(remoteCode === 'prepayments' && calendarCode))
    partyInvalid.push({ key: remoteCode, label: refusalSentence(`remote_${remoteCode}`, lang0) || refusalSentence('invalid_remote_rental', lang0) })
  const warnings = []
  // 🚪 the opening's unanswered questions come first — the form behind them is not drawn yet
  const openQs = state.opening ? openingQuestions(draft?.fields) : []
  if (openQs.length && !state.openingDone) {
    for (const q of openingMissing(state.opening, openQs))
      missing.push({ key: `opening:${q}`, label: t(`desk_rental_open_q${{ inspection: 1, handover: 2, method: 3, recurring: 4 }[q]}`) })
  }
  // "another way" chosen in the opening: the monthly route must be picked in the form
  if (state.paymentMonthlyRoute === 'specified' && state.openingDone)
    missing.push({ key: 'payment_monthly_route', label: paymentMessage('monthly_route', normLang(getLanguage())) })
  const monthlyOpen = shown.some(f => f.key === 'initial_recipient_type') && state.paymentSigningChosen !== false
    && state.paymentMonthlyRoute === 'same' && !monthlySameAvailable(values)
  if (monthlyOpen) missing.push({ key: 'payment_monthly_route', label: paymentMessage('monthly_route', normLang(getLanguage())) })
  if (shown.some(f => f.key === 'initial_recipient_type') && state.paymentSigningChosen === false) {
    missing.push({ key: 'payment_signing_route', label: paymentMessage('signing_route', normLang(getLanguage())) })
  } else if (shown.some(f => f.key === 'initial_recipient_type')) {
    // while monthly rent is unanswered, its stored values are only the template's defaults — nothing
    // the agent chose — so a complaint about them would be the silent default speaking again
    const rawProblem = rentalPaymentProblem(values, draft?.payment_banks)
    const problem = monthlyOpen && /^(owner_payment|recurring_)/.test(String(rawProblem || '')) ? null : rawProblem
    const lang = normLang(getLanguage())
    if (problem) partyInvalid.push({ key: problem, label: paymentMessage(problem, lang) || `${L(shown.find(f => f.key === problem) || {}, 'label') || problem} — ${t('desk_contract_check_payment_rule')}` })
    const plan = paymentPlanProblem(values)
    if (plan && plan !== problem) partyInvalid.push({ key: `plan:${plan}`, label: paymentMessage(plan, lang) })
    if (values.initial_recipient_type === 'authorised_agent' &&
        ['instant_transfer', 'local_bank_transfer', 'international_wire'].includes(values.payment_method) &&
        !(draft?.payment_accounts || []).some(a => a.id === state.paymentAccountSelection?.initial && a.verified_at))
      partyInvalid.push({ key: 'approved_agent_payment_account', label: paymentMessage('approved_agent_account', normLang(getLanguage())) })
  }
  for (const k of overriddenKeys(shown, values, state.seeded)) {
    if (!String((state.overrideReasons || {})[k] || '').trim()) {
      const f = shown.find(x => x.key === k)
      partyInvalid.push({ key: `override:${k}`, label: `${L(f || {}, 'label') || k} — ${t('desk_contract_override_reason_needed')}` })
    }
  }
  // 🚪 While the opening is on screen the form behind it is not drawn, so the bar names ONLY the
  // opening's questions — a list of fields the agent cannot see yet is not a door (2026-09-19).
  if (openQs.length && !state.openingDone) {
    const opening = missing.filter(x => String(x.key).startsWith('opening:'))
    return { missing: opening, invalid: [], warnings: [], needLang: false, ready: false }
  }
  // the key-handover plan is the opening's question — never asked a second time as a bare field
  const all = [...partyMissing, ...missing.filter(x => !(openQs.length && x.key === 'key_handover_timing'))]
  const needLang = !langs || langs.length === 0
  const invalid = partyInvalid
  // `invalid` is separate from `missing` on purpose (see contactProblems / identityProblems):
  // one says a question is unanswered, the other says the answer cannot be true. Both stop the
  // button, and the bar names them separately so the agent knows which kind of fix is wanted.
  // `warnings` never stop the button: the server accepts them, and the form must not build a wall it does not.
  return { missing: all, invalid, warnings, needLang, ready: all.length === 0 && invalid.length === 0 && !needLang }
}

// ── The language pills on the paper header (designer 2a: ქარ / ENG / + РУС) ───────────────
// `mode`: 'free' (a NEW contract — the pills toggle the set), 'add' (an existing UNSIGNED
// contract — a language may be ADDED and goes out as `langs` on the save; one already in the
// contract stays), 'locked' (after a signature; the destructive door is the page's, and once
// it is open the page passes 'free'). `reviewed` limits what may be offered: an unreviewed
// language is shown disabled with the reason, never hidden. The last language on cannot be
// dropped. Drawn on the DARK header bar.
export function LangPills({ langs, reviewed, available, base = [], mode = 'free', onChange }) {
  const chosen = orderLangs(langs)
  const rows = LANG_ORDER.filter(l => (available || []).includes(l) || (reviewed || []).includes(l) || chosen.includes(l))
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="contract-langs" data-mode={mode}>
      {rows.map(l => {
        const ok = (reviewed || []).includes(l)
        const on = chosen.includes(l)
        const last = on && chosen.length === 1
        const required = l === 'ka' && on
        const fixed = mode === 'locked' || (mode === 'add' && on && base.includes(l))
        const disabled = !ok || last || fixed || required
        const title = !ok ? t('desk_contract_lang_unreviewed') : required ? t('desk_contract_lang_ka_required') : last ? t('desk_contract_need_lang')
          : fixed ? t('desk_contract_lang_locked') : on ? t('desk_contract_lang_remove') : t('desk_contract_lang_add')
        return (
          <button key={l} type="button" data-lang={l} aria-pressed={on} disabled={disabled} title={title}
            className={`rounded-full px-2.5 py-0.5 font-mono text-[11px] tracking-[.06em] border transition-colors ${on
              ? 'bg-[#F7F5F0] text-[#14130F] border-[#F7F5F0]'
              : 'border-[#F7F5F0]/30 text-[#F7F5F0]/70 hover:border-[#F7F5F0]/70'} ${disabled && !on ? 'opacity-40' : ''} ${fixed ? 'cursor-default' : ''}`}
            onClick={() => !disabled && onChange(toggleLang(langs, l, !on))}>
            {on ? '' : '+ '}{tOr(`desk_contract_lang_short_${l}`, l)}
          </button>
        )
      })}
      {chosen.length > 0 && chosen[0] !== 'ka' && (
        <span className="text-[10px] text-[#F7F5F0]/60 italic" data-testid="contract-lang-prevails">
          {tLog('desk_contract_lang_prevails_note', { lang: tOr(`desk_contract_lang_${chosen[0]}`, chosen[0]) })}
        </span>
      )}
    </div>
  )
}

// ── The paper's groups: values in DOCUMENT order ─────────────────────────────────────────
// A field's group is read off its KEY (the template's keys are stable names — API §contract-
// draft); an unknown key lands in "other" rather than being dropped. Order = document order:
// parties (drawn separately) · property · term & money · use & conditions · other.
export const FIELD_GROUPS = ['property', 'money', 'use', 'other']
export const MONEY_ORDER = ['lease_start', 'lease_end', 'lease_model', 'lease_break_after_months', 'tenant_notice_days',
  // the sequence the money moves in (owner 2026-09-22): what is paid first, THEN when the next one falls
  'rent', 'payment_currency_rule', 'due_day', 'prepayments', 'prepaid_refund_days', 'first_recurring_payment_date', 'late_days', 'deposit', 'deposit_uses', 'deposit_notice_days',
  // the service agreement (owner 2026-09-22: "sort fields and sections correctly"): the fee, then who
  // shares it, then what was paid before, then how and to whom it was paid, then its state, then
  // the re-letting guarantee — the order the money actually moved in
  'fee', 'commission_schedule', 'commission_second_part', 'commission_second_after_months', 'cobroke_active', 'cobroke_kind', 'cobroke_agent_name', 'cobroke_agent_id_kind', 'cobroke_agent_id_number',
  'cobroke_agency_name', 'cobroke_agency_id_code', 'cobroke_phone', 'cobroke_email', 'cobroke_share', 'cobroke_share_paid_to',
  'prior_payment_kind', 'prior_payment_amount', 'prior_payment_on', 'prior_payment_credited_against', 'prior_payment_note', 'prior_payment_credited_other',
  'settlement', 'commission_receiving_account', 'commission_receiving_agent', 'settlement_reference', 'settlement_other',
  'commission_status', 'guarantee_active', 'guarantee_months', 'guarantee_weeks']
// 🧭 THE ORDER AN AGENT FILLS A CONTRACT IN, per contract type (owner 2026-09-23: "make sure every field
// follows each other logically"). Inside each group a listed key keeps this rank; an unlisted one keeps its
// template order after them. Earlier rulings stand: late days sit with the money (2026-09-22), additional
// conditions stay last, notice channels live inside the party blocks.
export const FORM_ORDER = {
  rent_residential: ['agreement_date', 'address', 'property_type', 'cadastral_code', 'furnished',
    'lease_start', 'lease_end', 'lease_model', 'lease_break_after_months', 'tenant_notice_days', 'paid_early_exit',
    'rent', 'payment_currency_rule',
    // what was paid before comes BEFORE what it is credited against (audit 2026-09-23)
    'prior_payment_kind', 'prior_payment_amount', 'prior_payment_on', 'prior_payment_credited_against', 'prior_payment_credited_other', 'prior_payment_note',
    'prepayments', 'deposit', 'deposit_uses', 'due_day', 'first_recurring_payment_date', 'late_days', 'deposit_notice_days', 'prepaid_refund_days',
    'utilities_mode', 'utilities_cap', 'utilities_list', 'max_residents', 'pets', 'pets_note', 'smoking', 'guests', 'guests_note', 'sublet', 'business_use',
    'cure_days', 'minor_repair_cap', 'showing_window_days', 'routine_inspection_interval_months', 'routine_inspection_notice_hours',
    'additional_conditions', 'additional_conditions_en', 'additional_conditions_ru', 'additional_conditions_material'],
  // 🏢 the commercial lease (2026-09-23): the premises and what they are for first, then the term and the money,
  // then how the premises are used, fitted out, kept and insured, and the exit — the order the clauses print in
  rent_commercial: ['agreement_date', 'address', 'premises_type', 'premises_other', 'premises_area', 'cadastral_code', 'fixtures_included',
    'registration', 'lease_start', 'lease_end', 'lease_model', 'lease_break_after_months', 'tenant_notice_days', 'paid_early_exit',
    'rent', 'rent_free_months', 'payment_currency_rule', 'vat_mode', 'withholding',
    'prior_payment_kind', 'prior_payment_amount', 'prior_payment_on', 'prior_payment_credited_against', 'prior_payment_credited_other', 'prior_payment_note',
    'prepayments', 'deposit', 'deposit_uses', 'due_day', 'first_recurring_payment_date', 'late_days', 'deposit_notice_days', 'prepaid_refund_days',
    'utilities_mode', 'utilities_cap', 'utilities_list', 'service_charge_mode', 'service_charge_amount', 'service_charge_basis',
    'permitted_use', 'use_change', 'operating_hours', 'permits_by', 'signage', 'office_access', 'keep_open', 'outdoor_seating', 'floor_load', 'hazardous_goods',
    'fit_out', 'fit_out_description', 'tenant_repair_cap', 'landlord_insurance', 'insurance_tenant', 'sublet', 'assignment',
    'cure_days', 'improvements_exit', 'improvements_note', 'showing_window_days', 'routine_inspection_interval_months', 'routine_inspection_notice_hours',
    'additional_conditions', 'additional_conditions_en', 'additional_conditions_ru', 'additional_conditions_material'],
  advance: ['agreement_date', 'place', 'address', 'property_type', 'cadastral_code',
    // purpose decides which of the terms below exist at all — asked first
    'purpose', 'purpose_other', 'purpose_other_en', 'purpose_other_ru', 'rent', 'lease_start', 'lease_end', 'deposit', 'prepayments', 'purchase_price', 'purchase_terms', 'purchase_terms_en', 'purchase_terms_ru',
    'advance_amount', 'advance_paid_on', 'currency_treatment', 'fx_rate_source', 'fx_rate_source_other', 'fx_rate_kind', 'fx_rate_value', 'fx_rate_date',
    'advance_receipt_form', 'receipt_form_other', 'receipt_form_other_en', 'receipt_form_other_ru', 'receiving_agent', 'receipt_method', 'receipt_method_other', 'receipt_method_other_en', 'receipt_method_other_ru', 'receipt_account', 'agent_received_amount', 'distribution_deadline', 'part_held_by_agency',
    'deadline_date', 'deadline_time', 'refund_condition', 'refund_condition_en', 'refund_condition_ru', 'payer_fault_retention',
    'pets', 'pets_note', 'pets_note_en', 'pets_note_ru', 'smoking', 'guests', 'guests_note', 'guests_note_en', 'guests_note_ru', 'sublet', 'business_use', 'cure_days',
    'owner_authority', 'authority_representative', 'authority_note', 'authority_note_en', 'authority_note_ru', 'contingency', 'contingency_en', 'contingency_ru', 'contingency_deadline', 'additional_conditions', 'additional_conditions_en', 'additional_conditions_ru'],
  binder: ['agreement_date', 'place', 'address', 'property_type', 'cadastral_code',
    'purpose', 'purpose_other', 'purpose_other_en', 'purpose_other_ru', 'rent', 'lease_start', 'lease_end', 'deposit', 'purchase_price', 'purchase_terms', 'purchase_terms_en', 'purchase_terms_ru',
    'binder_amount', 'binder_paid_on', 'binder_credited_to', 'binder_credited_other', 'binder_credited_other_en', 'binder_credited_other_ru',
    'binder_receipt_route', 'binder_receipt_route_other', 'binder_receipt_route_other_en', 'binder_receipt_route_other_ru', 'binder_payment_account', 'binder_receiving_rep', 'binder_handover_days',
    'deadline_date', 'deadline_time', 'cure_days', 'owner_authority', 'authority_representative', 'authority_note', 'contingency', 'contingency_en', 'contingency_ru', 'contingency_deadline', 'additional_conditions', 'additional_conditions_en', 'additional_conditions_ru'],
  agency_service: ['agreement_date', 'address', 'property_type', 'cadastral_code', 'tenant_name', 'tenant_id_kind', 'tenant_id_number',
    // the lease this service is for: concluded when, on which dates, first paid when
    'tenancy_concluded_on', 'lease_start', 'lease_end', 'lease_model', 'lease_break_after_months', 'tenant_notice_days', 'tenant_first_payment_on',
    'rent', 'fee', 'commission_schedule', 'commission_second_part', 'commission_second_after_months', 'cobroke_active', 'cobroke_kind', 'cobroke_agent_name', 'cobroke_agent_id_kind', 'cobroke_agent_id_number',
    'cobroke_agency_name', 'cobroke_agency_id_code', 'cobroke_phone', 'cobroke_email', 'cobroke_share', 'cobroke_share_paid_to',
    'prior_payment_kind', 'prior_payment_amount', 'prior_payment_on', 'prior_payment_credited_against', 'prior_payment_note', 'prior_payment_note_en', 'prior_payment_note_ru', 'prior_payment_credited_other', 'prior_payment_credited_other_en', 'prior_payment_credited_other_ru',
    'settlement', 'commission_receiving_account', 'commission_receiving_agent', 'settlement_reference', 'settlement_other', 'settlement_other_en', 'settlement_other_ru',
    'commission_status', 'guarantee_active', 'guarantee_months', 'guarantee_weeks', 'additional_conditions', 'additional_conditions_en', 'additional_conditions_ru'],
  // 🏷️ the exclusive mandate (2026-09-24): what it covers and on what authority, then Annex 1's figures, the
  // commission in the order the clauses print it, the term, Annex 2, additional conditions last
  owner_mandate: ['agreement_date', 'place', 'deal_scope', 'address', 'property_type', 'area_sqm', 'cadastral_code',
    'owner_authority', 'authority_representative', 'authority_note', 'authority_note_en', 'authority_note_ru',
    'asking_price', 'min_acceptable_price', 'asking_rent', 'min_acceptable_rent', 'lease_term_months',
    'sale_commission_percent', 'sale_commission_min', 'sale_commission_max', 'sale_self_rate', 'sale_breach_rate',
    'rent_fee_basis', 'rent_fee_months', 'rent_fee_fixed', 'commission_payment_days', 'expense_cap', 'expense_purpose', 'expense_purpose_en', 'expense_purpose_ru',
    'exclusive_start', 'exclusive_end', 'tail_days', 'tail_list_days', 'cure_days',
    'ad_channels', 'ad_restrictions', 'ad_restrictions_en', 'ad_restrictions_ru', 'known_encumbrances', 'encumbrance_note', 'encumbrance_note_en', 'encumbrance_note_ru',
    'prior_contacts_state', 'prior_contacts', 'additional_conditions', 'additional_conditions_en', 'additional_conditions_ru'],
  tenant_agency_service: ['agreement_date', 'address', 'monthly_rent_reference', 'tenant_agency_fee_type', 'tenant_agency_fee_value', 'tenant_agency_fee_basis_other', 'tenant_agency_fee_basis_other_en', 'tenant_agency_fee_basis_other_ru',
    'tenant_agency_fee_amount', 'fee_due_trigger', 'payment_status', 'payment_method', 'payment_method_other', 'payment_method_other_en', 'payment_method_other_ru', 'payment_date', 'payment_evidence_reference'],
}
// keys whose name would put them in the wrong group: each belongs with what it answers
const GROUP_OF = {
  purchase_price: 'money', purchase_terms: 'money', purchase_terms_en: 'money', purchase_terms_ru: 'money', receipt_form_other: 'money', receipt_form_other_en: 'money', receipt_form_other_ru: 'money', receiving_agent: 'money', agent_received_amount: 'money',
  distribution_deadline: 'money', currency_treatment: 'money', fx_rate_source: 'money', fx_rate_source_other: 'money', fx_rate_kind: 'money',
  fx_rate_value: 'money', fx_rate_date: 'money', refund_condition: 'money', refund_condition_en: 'money', refund_condition_ru: 'money',
  tenancy_concluded_on: 'money', tenant_first_payment_on: 'money', tenant_name: 'property', tenant_id_kind: 'property', tenant_id_number: 'property',
  // the commercial lease's own keys (2026-09-23)
  premises_type: 'property', premises_other: 'property', premises_area: 'property', fixtures_included: 'property', registration: 'property',
  vat_mode: 'money', withholding: 'money', rent_free_months: 'money', service_charge_mode: 'money', service_charge_amount: 'money', service_charge_basis: 'money',
  permitted_use: 'use', use_change: 'use', operating_hours: 'use', permits_by: 'use', signage: 'use', office_access: 'use', keep_open: 'use',
  outdoor_seating: 'use', floor_load: 'use', hazardous_goods: 'use', fit_out: 'use', fit_out_description: 'use', tenant_repair_cap: 'use',
  landlord_insurance: 'use', insurance_tenant: 'use', assignment: 'use', improvements_exit: 'use', improvements_note: 'use',
  // the owner mandate's own keys (2026-09-24)
  deal_scope: 'property', owner_authority: 'property', authority_representative: 'property', authority_note: 'property', authority_note_en: 'property', authority_note_ru: 'property',
  asking_price: 'money', min_acceptable_price: 'money', asking_rent: 'money', min_acceptable_rent: 'money', lease_term_months: 'money',
  sale_commission_percent: 'money', sale_commission_min: 'money', sale_commission_max: 'money', sale_self_rate: 'money', sale_breach_rate: 'money',
  expense_cap: 'money', expense_purpose: 'money', expense_purpose_en: 'money', expense_purpose_ru: 'money',
  exclusive_start: 'money', exclusive_end: 'money', tail_days: 'money', tail_list_days: 'money',
  ad_channels: 'use', ad_restrictions: 'use', ad_restrictions_en: 'use', ad_restrictions_ru: 'use',
  known_encumbrances: 'use', encumbrance_note: 'use', encumbrance_note_en: 'use', encumbrance_note_ru: 'use',
  prior_contacts_state: 'use', prior_contacts: 'use',
}
export const formRank = (kind, key) => { const i = (FORM_ORDER[kind] || []).indexOf(key); return i < 0 ? 999 : i }

export function fieldGroup(key) {
  const k = String(key || '')
  if (GROUP_OF[k]) return GROUP_OF[k]
  if (/^(address|property|type$|kind$|cadastral|furnish|signed_date|agreement_date|rooms|area|remote_transaction|tenant_inspected)/.test(k)) return 'property'
  // 💵 THE PAYMENT QUESTIONS SIT TOGETHER (owner 2026-09-22: "these fields are scattered"): the
  // first regular payment date matched no rule and fell to the bottom of the form beside the
  // additional conditions; the rent grace days sat among the use rules; the tenant's break notice
  // was read as a PROPERTY field because its key starts with tenant_. MONEY_ORDER below fixes
  // their order inside the group.
  if (/^(first_recurring_payment_date|late_days|tenant_notice_days|prepaid_refund_days)$/.test(k) || /^cobroke_/.test(k)) return 'money'
  // v14 (2026-09-22): designated notice channels and the minor-repair limit are use rules — without
  // this the tenant's channels read as PROPERTY (tenant_) and the landlord's fell below the
  // additional conditions, which must stay last.
  if (/^(landlord|tenant)_notice_(email|whatsapp)$/.test(k) || k === 'minor_repair_cap') return 'use'
  if (/^(landlord_wifi|landlord_ac|cure_days|showing_window)/.test(k)) return 'use'
  if (/^(lease_|rent|due_day|payment_|prior_payment|owner_payment|recurring_|prepay|deposit|currency|fee|settlement|commission|guarantee|advance_|binder|split_|purpose|deal_price|refund|deadline|protection_|initial_payment|receipt_|payer_fault|part_held|tenant_agency_fee|monthly_rent_reference)/.test(k)) return 'money'
  if (/^(tenant_|place$)/.test(k)) return 'property'
  // ⚠️ business_use and guests are USE RULES and were landing in 'other', which pushed
  // "დამატებითი პირობები" up the page — the owner's ruling 2026-09-12 is that additional
  // conditions must be the LAST thing filled, immediately before the photographs. With the
  // policies grouped where they belong, the free-text box is the only 'other' field and
  // lands last by the group order, with no per-field ordering rule to maintain.
  if (/^(utilities|max_|pets|entry_|routine_inspection|late_|notice|residents|occupants|smoking|sublet|business_use|guests|physical_handover|key_handover|defect_report)/.test(k)) return 'use'
  return 'other'
}

// ── The composer: the PAPER ───────────────────────────────────────────────────────────────
// `issues` = the drift list (the page's state); `activeKey` = the section whose clause editor
// is open, `onOpenSection(key|null)` opens/closes it; `wide` picks the inline side-by-side
// editor over the phone's sheet; `readOnlySections` after a signature. The language SET is
// edited by the header's LangPills, not here.
export function Composer({ state, setState, busy, issues = [], activeKey = null, onOpenSection, wide = false, readOnlySections = false, afterFields = null }) {
  // Which prepayment row's ⓘ is open. ONE at a time: several open notes push the rows the agent
  // is comparing off the screen. Value fields do not use this — their explanation is printed
  // under the box (see the note at the label row).
  const [helpFor, setHelpFor] = useState(null)
  // 🔒 INHERITED ANSWERS SIT CLOSED (owner 2026-09-22: "design must show what is inherited — not open
  // fields; show it closed with an edit button, it is better for the eye"). A field whose value came
  // from another record and still equals it is drawn as TEXT with „შეცვლა"; pressing it opens the
  // ordinary input for this session. Keys in this set have been opened.
  const [openedFields, setOpenedFields] = useState(() => new Set())
  const [partialPaymentOpen, setPartialPaymentOpen] = useState(false)
  const [partialPayment, setPartialPayment] = useState({ amount: '', on: '' })
  const { draft, values, langs, parties, rerenderOf, sections, seeded } = state
  // 🤝 THE CO-BROKER'S SHARE FILLS ITSELF (owner 2026-09-22: "why not auto fill"): while the co-broke is
  // ticked and the share is empty — or still the one filled here — it is HALF the fee (the agency's
  // standard split), in the fee's currency. Typing a share takes it over; clearing it hands it back.
  // 🔒 the private-contact model (lease v15): the template carries the per-side SMS field
  const privateModel = (draft?.fields || []).some(f => f.key === 'landlord_notice_sms')
  const noticeSideOf = role => ({ landlord: 'landlord', owner: 'landlord', lessor: 'landlord', tenant: 'tenant', lessee: 'tenant' })[role] || null
  // prefill each side ONCE from its party's contact: SMS from the phone (never WhatsApp — a number is not
  // assumed to be on it), email from the email. Nothing is overwritten once any channel has a value.
  useEffect(() => {
    if (!privateModel) return
    const patch = {}
    for (const side of ['landlord', 'tenant']) {
      if (state.noticeSeeded?.[side]) continue
      const p = (parties || []).find(x => noticeSideOf(x.role) === side)
      if (!p) continue
      const any = ['sms', 'whatsapp', 'email', 'address'].some(k => String(values?.[`${side}_notice_${k}`] || '').trim())
      if (!any) { if (p.phone) patch[`${side}_notice_sms`] = p.phone; if (p.email) patch[`${side}_notice_email`] = p.email }
      patch.__seeded = { ...(patch.__seeded || {}), [side]: true }
    }
    if (!patch.__seeded) return
    const { __seeded, ...vals } = patch
    setState(s => ({ ...s, noticeSeeded: { ...(s.noticeSeeded || {}), ...__seeded }, values: { ...s.values, ...vals } }))
  }, [privateModel, (parties || []).map(p => `${p.role}:${p.phone || ''}:${p.email || ''}`).join('|')])
  const feeV = values?.fee, shareV = values?.cobroke_share
  // 💵 THE FEE FROM ITS BASIS (owner 2026-09-23): the cooperation's percent × the rent on this page. The
  // server sends the percent (`fee_basis`) because the rent may have come from the import step, which it
  // never saw. The agent's OWN cooperation fills the fee; a colleague's is offered as a one-tap chip.
  const basis = draft?.fee_basis || null
  const rentV = values?.rent
  const feeFromBasis = basis && Number(rentV?.value) > 0
    ? { value: Math.round(Number(rentV.value) * basis.percent) / 100, currency: rentV.currency || 'USD' } : null
  const feeBlank = !feeV || feeV.value == null || feeV.value === ''
  // the meeting's RECORDED split for the other agent, else the agency's standard half
  const cobrokePct = Number.isFinite(Number(draft?.cobroke_percent)) && draft?.cobroke_percent != null ? Number(draft.cobroke_percent) : 50
  useEffect(() => {
    if (!feeFromBasis || !basis?.mine || !feeBlank) return
    setState(s => {
      const v = { ...s.values, fee: feeFromBasis }
      const sh = s.values.cobroke_share
      const shareBlank = !sh || sh.value == null || sh.value === ''
      // the share follows in the same step, so the two never disagree for a render
      if (String(s.values.cobroke_active) === 'true' && (shareBlank || s.cobrokeShareAuto))
        return { ...s, cobrokeShareAuto: true, values: { ...v, cobroke_share: { value: Math.round(feeFromBasis.value * cobrokePct) / 100, currency: feeFromBasis.currency } } }
      return { ...s, values: v }
    })
  }, [feeFromBasis?.value, feeFromBasis?.currency, basis?.mine, feeBlank])
  useEffect(() => {
    if (String(values?.cobroke_active) !== 'true') return
    const fee = Number(feeV?.value)
    if (!(fee > 0)) return
    const blankShare = !shareV || shareV.value == null || shareV.value === ''
    if (!blankShare && !state.cobrokeShareAuto) return
    const half = { value: Math.round(fee * cobrokePct) / 100, currency: feeV.currency || 'USD' }
    if (!blankShare && Number(shareV.value) === half.value && shareV.currency === half.currency) return
    setState(s => ({ ...s, cobrokeShareAuto: true, values: { ...s.values, cobroke_share: half } }))
  }, [values?.cobroke_active, feeV?.value, feeV?.currency, shareV?.value, shareV?.currency, cobrokePct])
  // …and a ticked co-broke always says person or company: a draft saved before the choice existed
  // (1961) restores it blank, which the bar reported as missing AND as a changed inherited value
  useEffect(() => {
    if (String(values?.cobroke_active) === 'true' && !values?.cobroke_kind)
      setState(s => ({ ...s, values: { ...s.values, cobroke_kind: 'person' } }))
  }, [values?.cobroke_active, values?.cobroke_kind])
  const overrideReasons = state.overrideReasons || {}
  // Which signatures the SERVER will accept for the agency: the default, and this viewer
  // only when they hold the grant. It rides on the draft, so it is the server's answer and
  // not a role test done here (owner 2026-09-12).
  const agencyRep = draft?.agency_rep || null
  // ⚠️ NOT `agency_rep.all`, which is who may SIGN for senTavus. Until 1171 this control was fed
  // the signatories, so every person trusted to take a deposit had to be a person trusted to bind
  // the company — one grant answering two questions.
  const fundReceiver = draft?.fund_receiver || null
  const fields = draft.fields || []
  // THE DUE DAY FOLLOWS THE LEASE START (owner 2026-09-10: "this should be the day rent starts
  // as default"). Setting lease_start rewrites due_day to its day-of-month until the agent has
  // typed a due day by hand (`dueDayTouched`); the tag then reads "from lease start".
  //
  // START · DURATION · END ARE ONE LINKED CONTROL (owner ruling 2026-09-11, verbatim in
  // src/lib/leasePeriod.js). The CONTRACT FACTS are the two dates — values.lease_start and
  // values.lease_end are what the server receives; the duration is a VIEW of them and a
  // control that writes the end. `state.lease` is composer state, never a value:
  //   { duration: {n, unit} | null,   the figure the agent typed / tapped (null = not authored)
  //     source: 'duration'|'end'|null, which of the two the agent authored LAST — 'duration'
  //                                    means the end is DERIVED (start + n units − 1 day, and
  //                                    a new start recomputes it); 'end' or null means the
  //                                    end is a fact and the duration is DERIVED from the
  //                                    dates (a new start recomputes the duration instead)
  //     unit: 'day'|'month'|'year',    the unit the agent last chose (kept across edits)
  //     editing: bool }                a non-whole duration ("6 თვე და 12 დღე") is read-only
  //                                    text until the agent presses edit
  // The field the agent just edited is NEVER rewritten: an end typed by hand stays as typed
  // and reads as its real length; a duration typed by hand stays and rewrites the end. The
  // derived one wears the "ავტომატურად" tag (`desk_contract_src_period`).
  const leaseOf = s => s.lease || { duration: null, source: null, unit: 'month', editing: false }
  const setValue = (k, v) => setState(s => {
    const values = { ...s.values, [k]: v }
    let selectedPaymentId = s.paymentAccountSelection?.initial || null
    if (k === 'initial_recipient_type' && v !== 'authorised_agent') values.initial_receiving_agent = null
    // ticking the co-broke starts the other agent as a person (the block's default tab); unticking clears it
    if (k === 'cobroke_active') values.cobroke_kind = v ? (s.values.cobroke_kind || 'person') : ''
    // a share the agent types is theirs; an emptied one is filled from the fee again
    if (k === 'cobroke_share') return { ...s, values, cobrokeShareAuto: false }
    if (k === 'initial_recipient_type') {
      for (const key of ['payment_account', 'payment_bank', 'payment_account_holder', 'initial_payment_swift']) values[key] = ''
      const owner = (s.parties || []).find(p => p.role === 'landlord')
      const current = s.draft?.fund_receiver?.default
      values.initial_receiving_agent = v === 'authorised_agent' && current ? { party_id: current.party_id, name: current.name } : null
      values.payment_recipient = v === 'owner' ? (owner?.legal_name || owner?.display_name || '') : v === 'authorised_agent' ? (current?.name || '') : ''
      selectedPaymentId = null
    }
    if (k === 'initial_receiving_agent') {
      values.payment_recipient = v?.name || ''
      for (const key of ['payment_account', 'payment_bank', 'payment_account_holder', 'initial_payment_swift']) values[key] = ''
      selectedPaymentId = null
    }
    if (k === 'initial_recipient_type' || k === 'initial_receiving_agent') {
      const partyId = values.initial_recipient_type === 'owner' ? (s.parties || []).find(p => p.role === 'landlord')?.party_id
        : values.initial_recipient_type === 'authorised_agent' ? values.initial_receiving_agent?.party_id : null
      const accounts = (s.draft?.payment_accounts || []).filter(a => a.party_id === partyId &&
        (values.initial_recipient_type !== 'authorised_agent' || a.verified_at))
      const account = accounts.find(a => a.is_default && a.verified_at) ||
        [...accounts].filter(a => a.last_used_at).sort((a, b) => String(b.last_used_at).localeCompare(String(a.last_used_at)))[0]
      if (account) {
        Object.assign(values, { payment_account: account.iban, payment_bank: account.bank_name,
          payment_account_holder: account.account_holder, initial_payment_swift: account.swift })
        selectedPaymentId = account.id
      }
    }
    let paymentPurpose = s.paymentPurpose
    if ((k === 'prepayments' || k === 'deposit') && !s.paymentPurposeTouched) {
      paymentPurpose = paymentPurposeOf(values)
      values.payment_reference = paymentPurpose === 'other' ? '' : paymentReference(s.draft, paymentPurpose)
    }
    if (k === 'payment_method' || k === 'recurring_payment_method')
      Object.assign(values, methodDerivations(k === 'payment_method' ? 'initial' : 'recurring', v))
    if (k === 'remote_transaction' && v === true) {
      values.tenant_inspected_in_person = 'no'
      values.physical_handover_required = true
      values.key_handover_notice_hours ??= 24
      values.defect_report_hours ??= 24
      // ⚠️ NO PAYMENT METHOD HERE (owner 2026-09-19). This used to set SWIFT, "on verified
      //    initiation" and 3 days: a remote tenant may pay by instant transfer. The method is the
      //    opening's third answer (src/lib/rentalOpening.js) and nothing else writes it.
      // These rows allocate the future initial payment to rental periods. A later receipt
      // event, never this selection, proves that any money reached the recipient.
      // …but never a due day the agent typed by hand: readiness names the mismatch instead (the
      // server's remote rule), and the agent decides which of the two is wrong.
      const day = Number(String(values.lease_start || '').slice(8, 10))
      if (day > 0 && !s.dueDayTouched) values.due_day = day
    }
    let dueTouched = s.dueDayTouched
    if (k === 'due_day') dueTouched = true
    // 🧮 THE ADVANCE'S OWN ARITHMETIC (owner 2026-09-13: "received money should be auto filled
    // from the amount of avansi already filled on top", "and this also should be auto
    // calculated"). Two figures on that form are not questions — they are the total read a
    // second way — and asking for them invites a composition that does not add up.
    //   • what the agent received IS the sum handed over;
    //   • the main-deal advance is that sum LESS whatever prepays the agency commission.
    // ⚠️ Both stop deriving the moment the agent types one, exactly like the due day: a figure
    //    they set by hand is an answer, not a stale echo of the total.
    const touched = { ...(s.derivedTouched || {}) }
    if (k === 'agent_received_amount' || k === 'refund_amount') touched[k] = true
    Object.assign(values, advanceDerivations({ key: k, value: v, values: s.values, touched }))
    // "the agent receives PART of the advance": the whole advance is the wrong pre-fill (2026-09-17)
    if (k === 'advance_receipt_form' && v === 'agent_share' && !touched.agent_received_amount) values.agent_received_amount = null
    // a reason for a smaller refund goes when the refund is whole again — else the paper would say
    // "less than the whole advance" over a full return (1255)
    if (!refundReduced(values) && values.refund_reduction_reason) values.refund_reduction_reason = ''
    // …and one value the agent may not overrule, because every layer below already refuses it.
    Object.assign(values, priorPaymentForced({ key: k, value: v }))
    if (k === 'lease_start' && !dueTouched) {
      const m = String(v || '').match(/^\d{4}-\d{2}-(\d{2})$/)
      if (m) values.due_day = Number(m[1])
    }
    let lease = leaseOf(s)
    if (k === 'lease_start' && lease.source === 'duration' && lease.duration) {
      // start + duration → the end follows the new start (duration was the agent's).
      const end = addPeriod(v, lease.duration.n, lease.duration.unit)
      if (end) values.lease_end = end
    }
    // An end typed by hand is a fact: the duration now derives from the two dates.
    if (k === 'lease_end') lease = { ...lease, duration: null, source: 'end', editing: false }
    const breakAuto = k === 'lease_break_after_months' ? false : fillBreakMonth(values, s.breakAuto)
    const selection = reconcileAccountSelection(k === 'initial_recipient_type' || k === 'initial_receiving_agent'
      ? { ...(s.paymentAccountSelection || {}), initial: selectedPaymentId } : s.paymentAccountSelection, values, s.draft?.payment_accounts)
    return { ...s, values, dueDayTouched: dueTouched, derivedTouched: touched, lease, breakAuto, paymentPurpose,
      paymentAccountSelection: selection }
  })
  const selectPaymentAccount = (stage, patch, id) => setState(s => ({ ...s,
    values: { ...s.values, ...patch }, paymentAccountSelection: { ...(s.paymentAccountSelection || {}), [stage]: id } }))
  const savedPaymentAccount = (account, stage) => setState(s => ({ ...s,
    draft: { ...s.draft, payment_accounts: [...(s.draft.payment_accounts || []), account] },
    paymentAccountSelection: { ...(s.paymentAccountSelection || {}), [stage]: account.id } }))
  const choosePaymentPurpose = key => setState(s => ({ ...s, paymentPurpose: key, paymentPurposeTouched: true,
    values: { ...s.values, payment_reference: key === 'other' ? '' : paymentReference(s.draft, key) } }))
  const commissionFee = Number(values?.fee?.value)
  const binderCredit = values?.commission_from_binder
  const credit = binderCredit && binderCredit.currency === values?.fee?.currency ? Number(binderCredit.value) || 0 : 0
  // a credit in ANOTHER currency is still a credit — subtracting nothing from it would prefill the whole
  // fee as "paid" beside money already received. There is no balance without a rate, so nothing is prefilled.
  const creditMixed = !!(binderCredit && Number(binderCredit.value) && binderCredit.currency !== values?.fee?.currency)
  const commissionBalance = Number.isFinite(commissionFee) && !creditMixed ? Math.max(0, Math.round((commissionFee - credit) * 100) / 100) : null
  const commissionMixed = !!values?.commission_status && commissionCurrencyMixed(values)
  const chooseCommission = status => {
    if (status === 'partial') { setPartialPayment({ amount: values.commission_paid_amount?.value ?? '', on: values.commission_paid_on || '' }); setPartialPaymentOpen(true); return }
    setState(s => ({ ...s, values: { ...s.values, commission_status: status,
      commission_paid_amount: status === 'paid' && commissionBalance != null ? { value: commissionBalance, currency: s.values.fee?.currency || 'USD' } : null,
      commission_paid_on: status === 'paid' ? s.values.commission_paid_on || '' : null } }))
  }
  // The duration control writes: `{n, unit}` becomes the agent's figure and, when the start
  // is known and n is a whole positive number, the end is recomputed from it.
  const setDuration = ({ n, unit }) => setState(s => {
    const prev = leaseOf(s)
    const u = LEASE_UNITS.includes(unit) ? unit : prev.unit || 'month'
    const k = n === '' || n == null ? '' : Number(n)
    const values = { ...s.values }
    if (Number.isInteger(k) && k >= 1) {
      const end = addPeriod(s.values?.lease_start, k, u)
      if (end) values.lease_end = end
    }
    const breakAuto = fillBreakMonth(values, s.breakAuto)
    return { ...s, values, breakAuto, lease: { duration: { n: k, unit: u }, source: 'duration', unit: u, editing: false } }
  })
  const setLeaseEditing = () => setState(s => ({ ...s, lease: { ...leaseOf(s), editing: true } }))
  // The sentence beside a local override. Kept per key rather than folded into `overrides`,
  // because a half-typed reason is not yet a decision — it becomes one when the save goes.
  const setOverrideReason = (k, v) => setState(s => ({ ...s, overrideReasons: { ...(s.overrideReasons || {}), [k]: v } }))
  const lease = leaseOf(state)
  const leaseDiff = diffPeriod(values.lease_start, values.lease_end)
  // The end is DERIVED when the agent's duration produced it from the current start.
  const derivedLeaseEnd = lease.source === 'duration' && !!lease.duration && !!values.lease_end
    && addPeriod(values.lease_start, lease.duration.n, lease.duration.unit) === values.lease_end
  // What the duration control shows: the agent's own figure while it is the source, else the
  // interval read back from the dates — a whole {n, unit}, or null when the interval is not
  // whole (then `leaseDiff` is drawn as text: "6 თვე და 12 დღე").
  const shownDuration = lease.source === 'duration' && lease.duration ? lease.duration : asPeriod(leaseDiff, lease.unit || 'month')
  const derivedDuration = lease.source !== 'duration' && !!leaseDiff
  // The number box and unit select: the shown duration; while a non-whole interval is being
  // edited, seeded with its whole months (or its days, under a month) so the agent starts
  // from the real figure rather than an empty box.
  const editSeed = lease.editing && leaseDiff ? (leaseDiff.months >= 1 ? { n: leaseDiff.months, unit: 'month' } : { n: leaseDiff.days, unit: 'day' }) : null
  const boxN = shownDuration?.n ?? editSeed?.n ?? ''
  const boxUnit = shownDuration?.unit || editSeed?.unit || lease.unit || 'month'
  // The prepayment month list follows the WHOLE months of the interval (floor); under a
  // month, today's full list.
  // ⚠️ When the calendar can be built, its PERIOD COUNT wins (2026-09-22, 88b2bd2d): a lease of
  // "12 months and 10 days" is 13 periods on the server, so "last month (the 12th)" named a period
  // the calendar did not charge. With a first regular payment date it is 12 again, and says so.
  const calendarPeriods = (() => { try { return rentalCalendar(values).periods.length } catch { return null } })()
  const periodMonths = calendarPeriods || (leaseDiff && leaseDiff.months >= 1 ? leaseDiff.months : null)
  const leaseSummary = describeLease(values.lease_start, values.lease_end, getLanguage(), { unit: shownDuration?.unit })
  const derivedDueDay = !state.dueDayTouched && /^\d{4}-\d{2}-\d{2}$/.test(String(values.lease_start || ''))
  // …and the two figures this form computes from the total it was given, rather than asking
  const derivedFromTotal = key => DERIVED_FROM_TOTAL.includes(key)
    && !(state.derivedTouched || {})[key] && values.advance_amount != null && values[key] != null
  const radioName = useId()
  const shown = visibleFields(fields, values, sections, langs || ['ka'])
  // 🪪 THE SAME NUMBER ON BOTH PARTIES, MARKED AT THE FIELD (owner 2026-09-13: "it gives me silent
  // error… it must protect mistake from writing one person's id in both fields by mistake").
  // The rule already existed and the sticky bar already named it — but the bar is at the foot of a
  // fifteen-section form, so from where the agent is typing the refusal has no visible cause. A
  // problem must be shown WHERE it can be fixed; the bar is the summary, not the notice.
  const idProblems = identityProblems(parties)
  const dateBad = dateProblems(Object.fromEntries(shown.map(f => [f.key, values[f.key]])))
  const amountBad = advanceAmountProblems(values)
  const partBad = componentProblems(shown, values)
  const commissionBad = shown.some(f => f.key === 'commission_status') && commissionProblem(values)
  const tenancyBad = shown.some(f => f.key === 'lease_model') ? tenancyProblem(values) : null
  const chosen = orderLangs(langs)
  const setParty = (i, patch) => setState(s => ({ ...s, parties: s.parties.map((x, j) => j !== i ? x : { ...x, ...patch }) }))
  const setPartyName = (i, lang, v) => setState(s => ({ ...s, parties: s.parties.map((x, j) => j !== i ? x
    : { ...x, names: { ...(x.names || {}), [lang]: v } }) }))
  const setSections = secs => setState(s => ({ ...s, sections: secs }))
  const active = activeKey ? (sections || []).find(x => x.key === activeKey) : null
  const removeSection = key => { setSections((sections || []).filter(x => x.key !== key)); onOpenSection && onOpenSection(null) }

  // WHAT A SUGGESTION READS AS. The value in the row's own words (a money value as "600 USD",
  // an enum by its label) so the agent can accept it without decoding it, and who recorded it
  // underneath. Deliberately NOT a "fill everything" button anywhere on this page: one reflex
  // click that accepts eight strangers' numbers is exactly the mistake this is here to prevent.
  // ONE formatter, because the overridden-value line reads the same way a suggestion does: the
  // agent is comparing two answers to the same question and they must be written alike.
  const showValue = (f, val) => {
    if (val == null || val === '') return ''
    if (val && typeof val === 'object' && 'value' in val) return val.value == null ? '' : `${val.value} ${val.currency || ''}`.trim()
    if (Array.isArray(val)) return val.map(x => { const o = (f.options || []).find(o => o.value === x); return L(o || {}, 'label') || String(x) }).join(', ')
    if (f.kind === 'enum') { const o = (f.options || []).find(o => o.value === val); return L(o || {}, 'label') || String(val) }
    if (f.kind === 'bool') return val ? t('desk_parse_value_yes') : t('desk_parse_value_no')
    if (f.kind === 'date' && /^\d{4}-\d{2}-\d{2}/.test(String(val))) return String(val).slice(0, 10).split('-').reverse().join('.')
    if (f.kind === 'agent' && typeof val === 'object') return val.name || ''
    return String(val)
  }
  const suggestionText = (f, sg) => {
    const val = sg.value
    return showValue(f, val)
  }
  const suggestionWho = sg => [sg.by_name, sg.at ? String(sg.at).slice(0, 10).split('-').reverse().join('.') : null]
    .filter(Boolean).join(' · ') || sg.source || ''

  // 📄 The document an inherited value came from, named. „ბეს შეთანხმებიდან (v1)" rather than
  // „ხელშეკრულებიდან" — and „ხელმოწერილი" only when it is, because an unsigned source is a
  // draft somebody may still change under this contract's feet.
  const DOC_KEY = {
    rent_residential_v1: 'desk_contract_doc_rental',
    rent_commercial_v1: 'desk_contract_doc_rental',
    binder_v1: 'desk_contract_doc_binder',
    advance_v1: 'desk_contract_doc_advance',
    agency_service_v1: 'desk_contract_doc_agency',
  }
  const docTag = ref => {
    const name = tOr(DOC_KEY[ref.template_code] || '', t('desk_contract_src_contract'))
    const bits = [ref.version ? `v${ref.version}` : null, ref.status === 'signed' ? t('desk_contract_doc_signed') : null].filter(Boolean)
    return bits.length ? `${name} (${bits.join(', ')})` : name
  }

  // The source tag: which rung answered. "you fill" is the highlighted ask and the ONLY tag
  // with colour (designer). On an existing contract the values came from ITS version, not
  // from the meeting's rungs — the tag then says so ("from v1") rather than describing a
  // different answer.
  const tagOf = (f, blank) => {
    if (blank) return { text: t('desk_contract_src_none'), ask: true }
    if (f.key === 'lease_break_after_months' && state.breakAuto) {
      const lang = normLang(getLanguage())
      return { text: { ka: 'შემოთავაზებული', en: 'Suggested', ru: 'Предложено' }[lang] || 'Suggested', ask: false }
    }
    // A derived end is a LIVE fact of this edit, so it outranks "from vN" on an existing
    // contract: the agent just typed a duration, and the end followed it.
    if (f.key === 'lease_end' && derivedLeaseEnd) return { text: t('desk_contract_src_period'), ask: false }
    // The agent has moved it off what the rung supplied, so no rung answers for it any more. Checked BEFORE
    // the "from vN" tag (owner 2026-09-23: "I am asked to say why it's different — and where do I say it?"):
    // on a saved contract the version tag returned first, so the reason box never appeared while the save
    // bar still demanded the reason — the same test as overriddenKeys, so the box and the bar always agree.
    if (movedFromSource(f, values, seeded)) {
      return { text: t('desk_contract_src_override'), ask: false, override: true }
    }
    if (rerenderOf) return { text: tLog('desk_contract_src_version', { v: rerenderOf.current_version?.version ?? '' }), ask: false }
    if (f.key === 'due_day' && derivedDueDay) return { text: t('desk_contract_src_lease_start'), ask: false }
    if (derivedFromTotal(f.key)) return { text: t('desk_contract_src_computed'), ask: false }
    if (f.source === 'none') return { text: t('desk_contract_src_none'), ask: true }
    if (seeded && Object.prototype.hasOwnProperty.call(seeded, f.key) && !sameAnswer(values[f.key], seeded[f.key])) {
      return { text: t('desk_contract_src_agent'), ask: false }
    }
    // 📄 WHICH DOCUMENT IT CAME FROM (owner 2026-09-13: "we should also see from which existing
    // contract we may inherit that value"). „from the rental contract" was said for anything a
    // contract supplied — including a figure taken off the ბე or the ავანსი, which is not a
    // rental at all. The server names the document now (`source_ref`); the tag reads it.
    // ⚠️ The fallback is the old label, so an older server that sends no ref changes nothing.
    if (f.source === 'contract' && f.source_ref) return { text: docTag(f.source_ref), ask: false }
    return { text: tOr(`desk_contract_src_${f.source}`, f.source || ''), ask: false }
  }

  // THE DEPOSIT IS PART OF THE PREPAYMENT (owner 2026-09-11: "Deposit is the part of წინასწარ
  // გადახდა"): when the prepayment list is on the paper, the deposit box is drawn INSIDE that
  // block (below the month rows, above the "paid at signing" sentence, which already counts it)
  // and not as a row of its own. Same field, same value, same tag — only the place changes.
  const depositField = shown.some(f => f.kind === 'prepay_list') ? shown.find(f => f.key === 'deposit') || null : null
  // the commercial lease runs on the same payment machinery (2026-09-23)
  const rentalPaymentPicker = (draft?.template?.code === 'rent_residential_v1' && Number(draft?.template?.version) >= 8)
    || draft?.template?.code === 'rent_commercial_v1'
  const compactPaymentFields = rentalPaymentPicker ? new Set([
    'payment_bank', 'payment_account_holder', 'initial_payment_swift',
    'owner_payment_bank', 'owner_payment_account_holder', 'owner_payment_swift',
    ...(!['instant_transfer', 'local_bank_transfer', 'international_wire'].includes(values.recurring_payment_method) ? ['owner_payment_iban'] : []),
    ...((values.initial_recipient_type === 'owner' || values.initial_recipient_type === 'authorised_agent') && values.payment_recipient ? ['payment_recipient'] : []),
  ]) : new Set()
  const paymentPlanOn = rentalPaymentPicker
  // 🚪 THE OPENING STEP (owner 2026-09-19) — each answer is written through setValue, so the derived
  // completion rule / processing days and every other per-key consequence apply exactly as before.
  const openQs = state.opening ? openingQuestions(fields) : []
  const answerOpening = (q, ans) => {
    for (const [k, v] of openingSteps(q, ans, values, { dueDayTouched: !!state.dueDayTouched })) setValue(k, v)
    setState(s => {
      const opening = { ...(s.opening || {}), [q]: ans }
      const next = { ...s, opening }
      if (q === 'recurring') next.paymentMonthlyRoute = ans === 'same_as_initial' ? 'same' : 'specified'
      if (q === 'method') {
        const allowed = (routesForMethod(ans) || []).filter(r => signingRouteAllowed(r, s.values.remote_transaction === true))
        // a recipient already chosen that the new method cannot use is asked again, never guessed
        if (allowed.length === 1) next.paymentSigningChosen = true   // one possible recipient: it is the answer
        else if (!allowed.includes(signingRouteOf({ ...s.values, payment_method: ans }))) next.paymentSigningChosen = false
        // "the same way" is impossible for "other" — the monthly question is open again
        if (ans === 'other' && opening.recurring === 'same_as_initial') { opening.recurring = null; next.paymentMonthlyRoute = 'specified' }
      }
      return next
    })
    if (q === 'method') {
      const allowed = (routesForMethod(ans) || []).filter(r => signingRouteAllowed(r, values.remote_transaction === true))
      if (allowed.length === 1) for (const [k, v] of signingSteps(allowed[0], { ...values, payment_method: ans })) setValue(k, v)
    }
  }
  const openingStep = openQs.length && !state.openingDone ? (
    <RentalOpening questions={openQs} answers={state.opening || {}} onAnswer={answerOpening}
      onDone={() => setState(s => (openingMissing(s.opening, openQs).length ? s : { ...s, openingDone: true }))} />
  ) : null
  const lastInGroup = f => (/^additional_conditions/.test(f.key) ? 1 : 0)
  // The term, then the rent and WHEN it is paid, then what is paid at signing: a field not named
  // here keeps its template order after these (sort is stable, rank 99).
  const moneyRank = f => { const i = MONEY_ORDER.indexOf(f.key); return i < 0 ? 99 : i }
  // the per-contract order (FORM_ORDER) leads; the older money list only breaks ties for unlisted keys
  const kindOf = draft?.template?.kind || rerenderOf?.template_kind || ''
  const orderRank = f => formRank(kindOf, f.key)
  const groups = FIELD_GROUPS.map(g => ({ g, rows: shown.filter(f => fieldGroup(f.key) === g && !(depositField && f.key === 'deposit') && !['commission_paid_amount', 'commission_paid_on'].includes(f.key) && !compactPaymentFields.has(f.key) && !(paymentPlanOn && PAYMENT_PLAN_KEYS.has(f.key))).sort((a, b) => (lastInGroup(a) - lastInGroup(b)) || (orderRank(a) - orderRank(b)) || (g === 'money' ? moneyRank(a) - moneyRank(b) : 0)) })).filter(x => x.rows.length > 0)
  if (paymentPlanOn) {
    // ✍️ how it is signed opens the money (before the term), the payment follows the money, the keys come
    // after the payment (owner 2026-09-23: "separate remote signing and key handover")
    const money = groups.find(x => x.g === 'money')
    if (money) { money.rows.unshift({ key: '__signing__' }); money.rows.push({ key: '__payment_plan__' }, { key: '__handover__' }) }
    else groups.push({ g: 'money', rows: [{ key: '__signing__' }, { key: '__payment_plan__' }, { key: '__handover__' }] })
  }
  const editor = active && (
    <ClauseEditor section={active} sections={sections} langs={chosen} fields={fields} issues={issues} wide={!!wide}
      readOnly={readOnlySections} disabled={busy} onChange={setSections}
      onClose={() => onOpenSection && onOpenSection(null)} onRemove={removeSection} />
  )

  const renderCobroke = nameField => {
    const company = values.cobroke_kind === 'company'
    const idKind = values.cobroke_agent_id_kind === 'passport' ? 'passport' : 'personal_no'
    const fieldOf = k => (fields || []).find(x => x.key === k)
    const need = k => { const x = fieldOf(k); return !!x && x.required && isBlank(x, values[k]) && conditionShows(x) }
    const conditionShows = x => !x.condition || shown.some(y => y.key === x.key)
    const box = (k, label, input) => (
      <label className={`min-w-0 block ${need(k) ? 'border-l-2 border-[#1F6F4F] pl-2 -ml-2' : ''}`} data-field={k} data-blank={need(k) ? '1' : '0'}>
        <span className={`block text-[11px] ${need(k) ? 'text-[#1F6F4F] font-medium' : INK_MUTED}`}>{label}</span>
        {input}
      </label>
    )
    const tagText = nameField.source && nameField.source !== 'none' && String(values.cobroke_agent_name || '').trim() ? tagOf(nameField, false).text : t('desk_contract_src_none')
    return (
      <div key="cobroke" className="space-y-2 min-w-0 rounded border border-[#B9B6AD]/60 px-2.5 py-2" data-cobroke-block>
        <div className="flex items-baseline justify-between gap-2">
          <span className={`text-[11px] ${INK_MUTED}`}>{L(fieldOf('cobroke_kind') || {}, 'label') || t('desk_contract_cobroke_block')}</span>
          <span className={TAG}>{tagText}</span>
        </div>
        <div className="flex items-center gap-3 text-[11px]" role="radiogroup" data-cobroke-kind={company ? 'company' : 'person'}>
          {['person', 'company'].map(k => {
            const on = (company ? 'company' : 'person') === k
            return (
              <button key={k} type="button" role="radio" aria-checked={on} data-cobroke-kind-pick={k}
                className={on ? 'text-[#14130F] font-medium underline underline-offset-4 decoration-[#1F6F4F] decoration-2' : `${INK_MUTED} hover:text-[#14130F]`}
                onClick={() => setValue('cobroke_kind', k)}>{t(`desk_contract_party_kind_${k}`)}</button>
            )
          })}
        </div>
        {company && box('cobroke_agency_name', `${t('desk_contract_company_name')} *`,
          <input className={`${PINP} w-full font-medium`} value={values.cobroke_agency_name || ''} placeholder={t('desk_contract_company_name_ph')}
            onChange={e => setValue('cobroke_agency_name', e.target.value)} />)}
        {company && box('cobroke_agency_id_code', `${t('desk_contract_idnum_company_reg')} *`,
          <input className={`${PINP} w-full`} value={values.cobroke_agency_id_code || ''} inputMode="numeric" maxLength={20}
            onChange={e => setValue('cobroke_agency_id_code', e.target.value)} />)}
        {box('cobroke_agent_name', `${company ? t('desk_contract_rep_name') : t('desk_contract_legal_name')} *`,
          <input className={`${PINP} w-full ${company ? '' : 'font-medium'}`} value={values.cobroke_agent_name || ''} placeholder={t('desk_contract_party_name_ph')}
            onChange={e => setValue('cobroke_agent_name', e.target.value)} />)}
        {!company && (
          <div className="grid gap-2 sm:grid-cols-2 items-end">
            <label className="min-w-0">
              <span className={`block text-[11px] ${INK_MUTED}`}>{t('desk_contract_id_kind')}</span>
              <select className={`${PINP} w-full`} value={idKind} data-cobroke-id-kind
                onChange={e => { const k = e.target.value; setValue('cobroke_agent_id_kind', k); if (k === 'personal_no') setValue('cobroke_agent_id_number', String(values.cobroke_agent_id_number || '').replace(/\D/g, '').slice(0, 11)) }}>
                {ID_KINDS_PERSON.map(k => <option key={k} value={k}>{t(`desk_contract_id_${k}`)}</option>)}
              </select>
            </label>
            {box('cobroke_agent_id_number', `${t(`desk_contract_idnum_${idKind}`)} *`,
              <input className={`${PINP} w-full`} value={values.cobroke_agent_id_number || ''} inputMode={idKind === 'personal_no' ? 'numeric' : 'text'}
                maxLength={idKind === 'personal_no' ? 11 : 40} placeholder={t('desk_contract_id_number_ph')}
                onChange={e => setValue('cobroke_agent_id_number', idKind === 'personal_no' ? e.target.value.replace(/\D/g, '').slice(0, 11) : e.target.value)} />)}
          </div>
        )}
        {(fields || []).some(x => x.key === 'cobroke_phone') && (
          <div className="grid gap-2 sm:grid-cols-2">
            {box('cobroke_phone', t('desk_contract_phone'),
              <input className={`${PINP} w-full`} type="tel" value={values.cobroke_phone || ''} placeholder="+995 5xx xx xx xx"
                onChange={e => setValue('cobroke_phone', e.target.value)} />)}
            {box('cobroke_email', t('desk_contract_email'),
              <input className={`${PINP} w-full`} type="email" value={values.cobroke_email || ''} placeholder="name@example.com"
                onChange={e => setValue('cobroke_email', e.target.value)} />)}
          </div>
        )}
      </div>
    )
  }

  // 📨 THE NOTICE CHANNELS (owner 2026-09-23): by default a party is notified at its own SHARED phone (SMS or
  // WhatsApp) and email, typed once in the parties block — nothing to fill here. „სხვა არხი" opens that
  // party's own notice fields, prefilled from its contacts, for when it says "notify me HERE for it to
  // count". Whatever is typed there wins; clearing it goes back to the party's contacts. A party with no
  // channel at all is left out of the contract's sentence.
  const renderNoticeChannelsV15 = () => {
    const sides = [['landlord', parties.find(p => noticeSideOf(p.role) === 'landlord')], ['tenant', parties.find(p => noticeSideOf(p.role) === 'tenant')]]
    const expanded = openedFields.has('notice:block')
    const summary = side => ['sms', 'whatsapp', 'email', 'address'].map(k => {
      const v = String(values[`${side}_notice_${k}`] || '').trim()
      return v ? `${k === 'sms' ? 'SMS' : k === 'whatsapp' ? 'WhatsApp' : k === 'email' ? t('desk_contract_email') : t('desk_contract_notice_address')}: ${v}` : null
    }).filter(Boolean).join(' · ')
    const missing = sides.filter(([side]) => !summary(side))
    return (
      <div key="notice-channels" className={`space-y-2 min-w-0 rounded border px-2.5 py-2 ${missing.length ? 'border-[#1F6F4F]' : 'border-[#B9B6AD]/60'}`} data-notice-block data-notice-missing={missing.length ? '1' : '0'}>
        <button type="button" className="w-full flex items-center justify-between gap-2 text-left" data-notice-toggle
          onClick={() => setOpenedFields(prev => { const n = new Set(prev); n.has('notice:block') ? n.delete('notice:block') : n.add('notice:block'); return n })}>
          <span className={`text-[11px] ${missing.length ? 'text-[#1F6F4F] font-medium' : INK_MUTED}`}>{t('desk_contract_notice_channels')}{missing.length ? ' *' : ''}</span>
          <span className={`text-[11px] underline ${INK_MUTED}`}>{expanded ? t('desk_contract_notice_hide') : t('desk_contract_lease_edit')}</span>
        </button>
        {sides.map(([side, p]) => {
          const same = String(values[`${side}_notice_sms`] || '') === String(p?.phone || '') && String(values[`${side}_notice_email`] || '') === String(p?.email || '')
            && !String(values[`${side}_notice_whatsapp`] || '').trim() && !String(values[`${side}_notice_address`] || '').trim()
          const custom = openedFields.has(`notice:${side}`)
          return (
            <div key={side} className="space-y-1" data-notice-side={side}>
              <div className="text-[11px] font-medium">{tOr(`desk_contract_role_${p?.role || side}`, side)}{p?.legal_name ? ` — ${p.legal_name}` : ''}</div>
              {!expanded ? (
                <div className="text-[12px]">{summary(side) || <span className="text-[#1F6F4F]">{t('desk_contract_notice_missing')}</span>}</div>
              ) : (
                <div className="space-y-1.5">
                  {same && !custom && <div className={`text-[11px] ${INK_MUTED}`}>{t('desk_contract_notice_same')}</div>}
                  {['sms', 'whatsapp', 'email'].map(k => {
                    const key = `${side}_notice_${k}`, v = String(values[key] || '')
                    const fallback = k === 'email' ? (p?.email || '') : (p?.phone || '')
                    return (
                      <div key={k} className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-2 items-center">
                        <label className="flex items-center gap-1.5 text-[12px] cursor-pointer">
                          <input type="checkbox" className="h-4 w-4 accent-[#1F6F4F]" checked={v !== ''} data-notice-on={key}
                            onChange={e => {
                              // no contact to prefill from: keep the tick and open the box for typing
                              if (e.target.checked && !fallback) setOpenedFields(prev => { const n = new Set(prev); n.add(`notice:${side}`); return n })
                              setValue(key, e.target.checked ? (fallback || ' ') : '')
                            }} />
                          {k === 'sms' ? 'SMS' : k === 'whatsapp' ? 'WhatsApp' : t('desk_contract_email')}
                        </label>
                        {custom || !same
                          ? <input className={`${PINP} w-full`} value={v.trim()} type={k === 'email' ? 'email' : 'tel'} data-field={key} onChange={e => setValue(key, e.target.value)} />
                          : <span className="text-[12px] truncate">{v.trim() || '—'}</span>}
                      </div>
                    )
                  })}
                  {(custom || !same) && (
                    <label className="block min-w-0" data-field={`${side}_notice_address`}>
                      <span className={`block text-[11px] ${INK_MUTED}`}>{t('desk_contract_notice_address')}</span>
                      <input className={`${PINP} w-full`} value={values[`${side}_notice_address`] || ''} onChange={e => setValue(`${side}_notice_address`, e.target.value)} />
                    </label>
                  )}
                  {!custom && same && (
                    <button type="button" className={`text-[11px] underline ${INK_MUTED} hover:text-[#14130F]`} data-notice-change={side}
                      onClick={() => setOpenedFields(prev => { const n = new Set(prev); n.add(`notice:${side}`); return n })}>{t('desk_contract_lease_edit')}</button>
                  )}
                </div>
              )}
            </div>
          )
        })}
        <div className={`text-[11px] ${INK_MUTED}`}>{t('desk_contract_notice_private_help')}</div>
      </div>
    )
  }
  const renderNoticeChannels = () => {
    if (privateModel) return renderNoticeChannelsV15()
    const sides = [['landlord', parties.find(p => p.role === 'landlord' || p.role === 'owner')], ['tenant', parties.find(p => p.role === 'tenant')]]
    return (
      <div key="notice-channels" className="space-y-2 min-w-0 rounded border border-[#B9B6AD]/60 px-2.5 py-2" data-notice-block>
        <div className={`text-[11px] ${INK_MUTED}`}>{t('desk_contract_notice_channels')}</div>
        {sides.map(([side, p]) => {
          const waK = `${side}_notice_whatsapp`, emK = `${side}_notice_email`
          const own = String(values[waK] || '').trim() || String(values[emK] || '').trim()
          const open = own || openedFields.has(`notice:${side}`)
          const sharedPhone = p && p.share_phone !== false ? String(p.phone || '').trim() : ''
          const sharedEmail = p && p.share_email !== false ? String(p.email || '').trim() : ''
          const effective = [sharedPhone && `${sharedPhone} (SMS / WhatsApp)`, sharedEmail].filter(Boolean).join(' · ')
          return (
            <div key={side} className="space-y-1" data-notice-side={side}>
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <span className="text-[11px] font-medium">{tOr(`desk_contract_role_${p?.role || side}`, side)}{p?.legal_name ? ` — ${p.legal_name}` : ''}</span>
                {!open && (
                  <button type="button" className={`text-[11px] underline ${INK_MUTED} hover:text-[#14130F]`} data-notice-other={side}
                    onClick={() => { setOpenedFields(prev => { const n = new Set(prev); n.add(`notice:${side}`); return n }); if (p?.phone && !values[waK]) setValue(waK, p.phone); if (p?.email && !values[emK]) setValue(emK, p.email) }}>
                    {t('desk_contract_notice_other')}</button>
                )}
              </div>
              {!open ? (
                <div className="text-[12px]" data-notice-effective={side}>{effective || <span className={INK_MUTED}>{t('desk_contract_notice_none')}</span>}</div>
              ) : (
                <>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <label className="min-w-0 block" data-field={waK}>
                      <span className={`block text-[11px] ${INK_MUTED}`}>{t('desk_contract_notice_phone')}</span>
                      <input className={`${PINP} w-full`} type="tel" value={values[waK] || ''} onChange={e => setValue(waK, e.target.value)} />
                    </label>
                    <label className="min-w-0 block" data-field={emK}>
                      <span className={`block text-[11px] ${INK_MUTED}`}>{t('desk_contract_email')}</span>
                      <input className={`${PINP} w-full`} type="email" value={values[emK] || ''} onChange={e => setValue(emK, e.target.value)} />
                    </label>
                  </div>
                  <button type="button" className={`text-[11px] underline ${INK_MUTED} hover:text-[#14130F]`} data-notice-reset={side}
                    onClick={() => { setValue(waK, ''); setValue(emK, ''); setOpenedFields(prev => { const n = new Set(prev); n.delete(`notice:${side}`); return n }) }}>
                    {t('desk_contract_notice_use_party')}</button>
                </>
              )}
            </div>
          )
        })}
        <div className={`text-[11px] ${INK_MUTED}`}>{t('desk_contract_notice_channels_help')}</div>
      </div>
    )
  }

  // One row of the paper. Lifted out of the group loop (2026-09-17) so the lease's payment plan can
  // draw its follow-up questions through exactly the same label · tag · input · help · marks.
  const renderRow = f => {
            // 🤝 THE OTHER AGENT IS ENTERED LIKE A PARTY (owner 2026-09-22: "make it like we have for the
            // client"): person or company, a person's document and number, a company's legal name and
            // code and the agent acting for it — one block, drawn where the agent's name sits.
            if (['cobroke_kind', 'cobroke_agent_id_kind', 'cobroke_agent_id_number', 'cobroke_agency_name', 'cobroke_agency_id_code', 'cobroke_phone', 'cobroke_email'].includes(f.key)) return null
            if (f.key === 'cobroke_agent_name') return renderCobroke(f)
            if (['landlord_notice_whatsapp', 'tenant_notice_email', 'tenant_notice_whatsapp', 'landlord_notice_sms', 'tenant_notice_sms',
              'landlord_notice_address', 'tenant_notice_address', 'landlord_contact_share', 'tenant_contact_share', 'landlord_contact_hide', 'tenant_contact_hide'].includes(f.key)) return null
            if (f.key === 'landlord_notice_email') return renderNoticeChannels()
            const v = values[f.key]
            const sysText = addressAutoFor(state, f)   // 🏠 the system's translation of the address, if it has one
            const blank = (f.required || (f.for_lang && !sysText)) && isBlank(f, v)
            const tag = tagOf(f, blank)
            const derived = f.key === 'lease_end' && derivedLeaseEnd
            // closed only while nothing needs the agent: inherited, unchanged, valid, and a plain input
            const SPECIAL = ['lease_model', 'commission_status', 'payment_account', 'owner_payment_iban', 'payment_reference']
            const closed = !blank && !readOnlySections && isInherited(f) && !tag.override && !openedFields.has(f.key)
              && !SPECIAL.includes(f.key) && f.kind !== 'prepay_list' && f.kind !== 'bool' && !valueProblem(f, v)
              && !dateBad.has(f.key) && !amountBad.has(f.key) && !partBad.has(f.key) && tenancyBad !== f.key
              && !(seeded && Object.prototype.hasOwnProperty.call(seeded, f.key) && !sameAnswer(v, seeded[f.key]))
              && String(showValue(f, v) || '').trim() !== ''
            const depositSlot = f.kind === 'prepay_list' && depositField ? (() => {
              const dv = values[depositField.key]
              const dblank = depositField.required && isBlank(depositField, dv)
              const dtag = tagOf(depositField, dblank)
              return (
                <div className={`space-y-1 min-w-0 ${dblank ? 'border-l-2 border-[#1F6F4F] pl-2 -ml-2' : ''}`} data-field={depositField.key} data-blank={dblank ? '1' : '0'} data-in-prepay="1">
                  <div className="flex items-baseline justify-between gap-2">
                    <label className={`text-[11px] ${dblank ? 'text-[#1F6F4F] font-medium' : INK_MUTED}`}>{L(depositField, 'label') || depositField.key}{depositField.required ? ' *' : ''}</label>
                    <span className={dtag.ask ? TAG_ASK : TAG} title={L(depositField, 'help') || undefined} data-src={dtag.ask ? 'none' : depositField.source}>{dtag.text}</span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                    <FieldInput f={depositField} v={dv} set={val => setValue(depositField.key, val)} values={values} paper />
                    {/* the deposit is drawn INSIDE the prepayment block, so the suggestion chip
                        has to be repeated here — the generic row below never renders this field. */}
                    {depositField.suggestion && isBlank(depositField, dv) && (
                      <button type="button" data-suggestion={depositField.key}
                        className="text-[11px] px-1.5 py-0.5 rounded border border-[#1F6F4F]/40 text-[#1F6F4F] hover:bg-[#1F6F4F]/10 max-w-full truncate"
                        title={suggestionWho(depositField.suggestion)}
                        onClick={() => setValue(depositField.key, depositField.suggestion.value)}>
                        {suggestionText(depositField, depositField.suggestion)}
                      </button>
                    )}
                  </div>
                  {L(depositField, 'help') && <div className={`text-[11px] ${INK_MUTED}`}>{L(depositField, 'help')}</div>}
                </div>
              )
            })() : null
            return (
              <Fragment key={f.key}>
                <div className={`space-y-1 min-w-0 ${blank ? 'border-l-2 border-[#1F6F4F] pl-2 -ml-2' : ''}`} data-field={f.key} data-blank={blank ? '1' : '0'} data-derived={derived ? '1' : '0'}>
                  {/* ⓘ ONLY WHERE THERE IS NO DESCRIPTION UNDER THE BOX (owner 2026-09-12:
                      "you have also repeated i twice… we can remove i s if we have these
                      descriptions below every box, and use i where we don't have these
                      descriptions below"). My first cut put the ⓘ on every field that carried
                      help — and this form ALREADY prints that help under the input, so pressing
                      the icon opened a panel repeating, word for word, the sentence three lines
                      below it. Two copies of one explanation is worse than none: the reader
                      stops trusting that the second thing they open will say something new.
                      So the description below is the explanation, and the icon is reserved for
                      places that have no room for one — the prepayment rows, where a rule
                      belongs to one MONTH rather than to the field. */}
                  <div className="flex items-baseline justify-between gap-2">
                    <label className={`text-[11px] ${blank ? 'text-[#1F6F4F] font-medium' : INK_MUTED}`}>{L(f, 'label') || f.key}{(f.required || (f.for_lang && !sysText)) ? ' *' : ''}</label>
                    <span className={tag.ask ? TAG_ASK : TAG} data-src={tag.ask ? 'none' : derived ? 'period' : f.source}>{tag.text}</span>
                  </div>
                  {/* 🏠 what the contract prints in this language when the box is left empty — the agent can write their own */}
                  {sysText && (
                    <div className={`text-[11px] ${INK_MUTED} break-words`} data-address-auto={f.key}>
                      {tLog(isBlank(f, v) ? 'desk_contract_address_auto_used' : 'desk_contract_address_auto_overridden', { v: sysText })}
                    </div>
                  )}
                  {/* ✍️ MARKED, AND ASKED WHY (owner 2026-09-12). The inherited value stays on the
                      screen beside the new one — a reader of this form must be able to see what
                      was overridden without opening the source document — and the sentence is
                      required before the contract can be created or saved. It is not a warning:
                      overriding is allowed, and the box is the price of doing it in the open. */}
                  {tag.override && (
                    <div className="rounded border border-[#8A4B2A]/30 bg-[#8A4B2A]/[.04] px-2 py-1.5 space-y-1" data-override={f.key}>
                      <div className={`text-[11px] ${INK_MUTED} break-words`} data-override-source={f.key}>
                        {tLog('desk_contract_override_was', { v: showValue(f, seeded[f.key]) || '—' })}
                      </div>
                      <input className={`${PINP} w-full ${String(overrideReasons[f.key] || '').trim() ? '' : 'border-[#8A4B2A]'}`}
                        value={overrideReasons[f.key] || ''} data-override-reason={f.key}
                        aria-invalid={String(overrideReasons[f.key] || '').trim() ? undefined : 'true'}
                        placeholder={t('desk_contract_override_reason_ph')}
                        onChange={e => setOverrideReason(f.key, e.target.value)} />
                    </div>
                  )}
                  {valueProblem(f, v) && (
                    <div className="text-[11px] text-[#8A4B2A]" data-value-bad={f.key}>{tOr(`desk_contract_bad_${f.key}`, t('desk_contract_bad_value'))}</div>
                  )}
                  {dateBad.has(f.key) && (
                    <div className="text-[11px] text-[#8A4B2A]" data-date-bad={f.key}>{t(`desk_contract_${dateBad.get(f.key)}`)}</div>
                  )}
                  {amountBad.has(f.key) && (
                    <div className="text-[11px] text-[#8A4B2A]" data-amount-bad={f.key}>{t(`desk_contract_${amountBad.get(f.key)}`)}</div>
                  )}
                  {partBad.has(f.key) && (
                    <div className="text-[11px] text-[#8A4B2A]" data-part-bad={f.key}>{partSentence(partBad.get(f.key))}</div>
                  )}
                  {f.key === 'commission_status' && commissionBad && <div className="text-[11px] text-[#8A4B2A]">{t('desk_contract_commission_mismatch')}</div>}
                  {f.key === 'commission_status' && commissionMixed && <div className="text-[11px] text-[#8A4B2A]" data-commission-mixed>{refusalSentence('commission_settlement_currency', normLang(getLanguage()))}</div>}
                  {closed ? (
                    <div className="flex items-center gap-2 flex-wrap min-w-0" data-closed-field={f.key}>
                      <span className="text-sm break-words min-w-0">{showValue(f, v)}</span>
                      <button type="button" className={`text-[11px] underline ${INK_MUTED} hover:text-[#14130F]`} data-closed-edit={f.key}
                        onClick={() => setOpenedFields(prev => { const n = new Set(prev); n.add(f.key); return n })}>{t('desk_contract_lease_edit')}</button>
                    </div>
                  ) : <>
                  <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                    {f.key === 'lease_model' ? (
                      <label className="flex items-center gap-2 text-sm cursor-pointer" data-lease-model>
                        <input type="checkbox" className="h-4 w-4 accent-[#1F6F4F]" checked={v === 'break'}
                          onChange={e => setValue(f.key, e.target.checked ? 'break' : 'full')} />
                        <span>{t('desk_contract_lease_break_toggle')}</span>
                      </label>
                    ) : f.key === 'commission_status' ? (
                      <div className="flex flex-wrap gap-1.5" data-commission-status>
                        {(f.options || []).map(o => <button key={o.value} type="button" onClick={() => chooseCommission(o.value)}
                          disabled={o.value === 'due' && credit > 0}
                          className={`rounded border px-2 py-1 text-xs ${v === o.value ? 'border-[#1F6F4F] bg-[#1F6F4F]/10 text-[#1F6F4F]' : 'border-[#B9B6AD] text-[#444]'}`}>
                          {optionText(o)}
                        </button>)}
                        {v === 'partial' && <button type="button" className="text-xs underline text-[#1F6F4F]" onClick={() => chooseCommission('partial')}>{t('desk_contract_commission_change_payment')}</button>}
                        {v === 'paid' && <label className="w-full text-xs">{t('desk_contract_payment_date')}
                          <span className="inline-flex flex-wrap items-center gap-2">
                            <DateInput value={values.commission_paid_on || ''} onChange={d => setValue('commission_paid_on', d)} className={PDATE} />
                            {/* one click for the usual case — the cash was received today (owner 2026-09-22) */}
                            <button type="button" className="text-xs underline text-[#1F6F4F]"
                              onClick={() => { const d = new Date(); const pad = n => String(n).padStart(2, '0'); setValue('commission_paid_on', `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`) }}>{t('desk_contract_today')}</button>
                          </span>
                        </label>}
                      </div>
                    ) : rentalPaymentPicker && ['payment_account', 'owner_payment_iban'].includes(f.key) ? (
                      <PaymentAccountPicker draft={draft} values={values} stage={f.key === 'payment_account' ? 'initial' : 'recurring'}
                        partyId={f.key === 'owner_payment_iban'
                          ? parties.find(p => p.role === 'landlord')?.party_id
                          : values.initial_recipient_type === 'owner' ? parties.find(p => p.role === 'landlord')?.party_id
                            : values.initial_recipient_type === 'authorised_agent' ? values.initial_receiving_agent?.party_id : null}
                        partyName={f.key === 'owner_payment_iban' ? parties.find(p => p.role === 'landlord')?.legal_name : values.payment_recipient}
                        recipientType={f.key === 'payment_account' ? values.initial_recipient_type : 'owner'}
                        selectedId={state.paymentAccountSelection?.[f.key === 'payment_account' ? 'initial' : 'recurring']}
                        onSelect={selectPaymentAccount} onSaved={savedPaymentAccount} />
                    ) : rentalPaymentPicker && f.key === 'payment_reference' ? (
                      <div className="w-full space-y-1" data-payment-purpose>
                        <select className={`${PINP} w-full`} value={state.paymentPurpose || 'other'} onChange={e => choosePaymentPurpose(e.target.value)}>
                          {PAYMENT_PURPOSES.map(p => <option key={p[0]} value={p[0]}>{normLang(getLanguage()) === 'ka' ? p[1] : p[2]}</option>)}
                        </select>
                        {state.paymentPurpose === 'other'
                          ? <input className={`${PINP} w-full`} value={v || ''} onChange={e => setValue(f.key, e.target.value)} />
                          : <div className={`text-[11px] ${INK_MUTED} break-words`}>{v || paymentReference(draft, state.paymentPurpose)}</div>}
                      </div>
                    ) : <FieldInput f={f} v={v} set={val => setValue(f.key, val)} values={values} paper periodMonths={f.key === 'lease_break_after_months' ? (leaseDiff?.exactMonths ? leaseDiff.months : null) : periodMonths} depositSlot={depositSlot} agents={draft?.fund_receiver?.all || null} />}
                    {/* ✋ SOMEBODY ELSE RECORDED THIS — one tap to accept it (owner 2026-09-12:
                        "it must be quick simple review. Like: yes that's correct. Or quick fill
                        suggestion from terms"). The agent's OWN recorded values arrive already
                        filled; a colleague's, an unauthored row's or a portal's do not, because
                        nobody has asked THIS owner about them in this deal. The chip names who
                        and when, and tapping it is the confirmation — cheap, but this agent's act. */}
                    {(() => {
                      const sg = f.suggestion || (f.key === 'fee' && feeFromBasis && basis && !basis.mine
                        ? { value: feeFromBasis, by_name: basis.by_name, at: basis.at } : null)
                      if (!sg || !isBlank(f, v)) return null
                      const why = !f.suggestion && f.key === 'fee' ? ` · ${basis.percent}% × ${values.rent.value} ${values.rent.currency || ''}`.trimEnd() : ''
                      return (
                        <button type="button" data-suggestion={f.key}
                          className="text-[11px] px-1.5 py-0.5 rounded border border-[#1F6F4F]/40 text-[#1F6F4F] hover:bg-[#1F6F4F]/10 max-w-full truncate"
                          title={suggestionWho(sg)}
                          onClick={() => setValue(f.key, sg.value)}>
                          {suggestionText(f, sg)}{why}{sg.by_name ? ` — ${sg.by_name}` : ''}
                        </button>
                      )
                    })()}
                    {/* Quick pick (owner 2026-09-11: "add here quick select today"): one tap sets
                        the start to today's LOCAL date; the duration presets below then place the
                        end. Nothing else on the row changes. */}
                    {f.key === 'lease_start' && (
                      <button type="button" className={`text-[11px] underline ${INK_MUTED} hover:text-[#14130F]`} data-lease-start-today
                        onClick={() => { const d = new Date(); const pad = n => String(n).padStart(2, '0'); setValue('lease_start', `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`) }}>{t('desk_contract_today')}</button>
                    )}
                  </div>
                  {L(f, 'help') && <div className={`text-[11px] ${INK_MUTED}`}>{L(f, 'help')}</div>}
                  </>}
                  {/* 🤝 with a co-broke the fee is the WHOLE fee — the settlement clause prints it as
                      the total and divides it (owner 2026-09-22: 600 = 300 senTavus + 300 the other agent) */}
                  {String(values.cobroke_active) === 'true' && f.key === 'fee' && (
                    <div className="text-[11px] text-[#1F6F4F]" data-fee-cobroke-note>{t('desk_contract_fee_cobroke_note')}</div>
                  )}
                  {String(values.cobroke_active) === 'true' && f.key === 'cobroke_share' && Number(values.fee?.value) > 0 && Number(values.cobroke_share?.value) >= 0
                    && values.fee?.currency === values.cobroke_share?.currency && (
                    (() => {
                      const agencyPart = Math.round((Number(values.fee.value) - Number(values.cobroke_share.value)) * 100) / 100
                      return (
                        <div className={`text-[11px] tabular-nums ${agencyPart <= 0 ? 'text-[#8A4B2A] font-medium' : ''}`} data-agency-share data-agency-share-bad={agencyPart <= 0 ? '1' : '0'}>
                          {t('desk_contract_agency_share')}: {agencyPart} {values.fee.currency}
                          {state.cobrokeShareAuto ? ` · ${t('desk_contract_share_auto')}` : ''}
                          {/* 300 in the fee and 300 to the other agent leaves senTavus nothing — the fee must be the whole 600 */}
                          {agencyPart <= 0 && <span className="block">{t('desk_contract_agency_share_zero')}</span>}
                        </div>
                      )
                    })()
                  )}
                  {/* 👥 An unusually high number is a permission, not a typo to wave through (owner 2026-10-13: a lease
                      went out allowing 10 permanent residents and the landlord boxed it). A warning, not a block. */}
                  {f.key === 'max_residents' && Number(v) > 6 && <div className="text-[11px] text-[#8A4B2A]" data-many-residents>{t('desk_contract_many_residents')}</div>}
                  {tenancyBad === f.key && <div className="text-[11px] text-[#8A4B2A]">{f.key === 'lease_break_after_months' ? t('desk_contract_break_before_end') : f.key === 'tenant_notice_days' ? t('desk_contract_notice_days_hint') : t('desk_contract_lease_model_hint')}</div>}
                </div>
                {/* THE DURATION, right under the start (header): a free number, its unit
                    (დღე · თვე · წელი), and three radios that set 3 / 6 / 12 months in one tap.
                    Composer state, not a value — the contract carries the two dates. While the
                    duration is read back from the dates it wears "ავტომატურად"; when the
                    interval is not a whole number of the unit it is drawn as TEXT ("6 თვე და
                    12 დღე") with an edit button, never rounded into the number box. */}
                {f.key === 'lease_start' && (
                  <div className="space-y-1 min-w-0" data-field="lease_duration" data-derived={derivedDuration ? '1' : '0'}>
                    <div className="flex items-baseline justify-between gap-2">
                      <label className={`text-[11px] ${INK_MUTED}`}>{t('desk_contract_period')}</label>
                      {derivedDuration && <span className={TAG} data-src="period">{t('desk_contract_src_period')}</span>}
                    </div>
                    <div className="flex items-center gap-x-3 gap-y-1.5 flex-wrap min-w-0">
                      {shownDuration || lease.editing || !leaseDiff ? (
                        <span className="inline-flex items-center gap-1.5 min-w-0">
                          <input type="number" inputMode="numeric" min="1" step="1" className={`${PINP} w-16 max-w-full min-w-0`} data-lease-n
                            value={boxN} onChange={e => setDuration({ n: e.target.value === '' ? '' : parseInt(e.target.value, 10), unit: boxUnit })} />
                          <select className={`${PINP} max-w-full min-w-0`} data-lease-unit value={boxUnit}
                            onChange={e => setDuration({ n: boxN, unit: e.target.value })}>
                            {LEASE_UNITS.map(u => <option key={u} value={u}>{t(`desk_contract_lease_unit_${u}`)}</option>)}
                          </select>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-2 min-w-0">
                          <span className="tabular-nums" data-lease-duration-text>{durationText(leaseDiff, getLanguage())}</span>
                          <button type="button" className={`text-[11px] underline ${INK_MUTED}`} data-lease-edit title={t('desk_contract_lease_edit_title')}
                            onClick={setLeaseEditing}>{t('desk_contract_lease_edit')}</button>
                        </span>
                      )}
                      <span className="inline-flex items-center gap-x-3 gap-y-1 flex-wrap" role="radiogroup">
                        {LEASE_PRESET_MONTHS.map(n => {
                          const on = !!shownDuration && ((shownDuration.unit === 'month' && Number(shownDuration.n) === n) || (shownDuration.unit === 'year' && Number(shownDuration.n) * 12 === n))
                          return (
                            <label key={n} className="inline-flex items-center gap-1 cursor-pointer whitespace-nowrap">
                              <input type="radio" name={radioName} className="accent-[#1F6F4F]" checked={on} data-lease-preset={n}
                                onChange={() => setDuration({ n, unit: 'month' })} />
                              <span>{tLog('desk_contract_lease_preset', { n })}</span>
                            </label>
                          )
                        })}
                      </span>
                    </div>
                  </div>
                )}
                {/* Under the end: the two generated lines — the interval and its length, then
                    either "N-თვიანი ვადა · ბოლო დღე: …" or, for a hand-set end that is not a
                    whole period, "ხელით მითითებული დასრულების თარიღი · 6 თვე და 12 დღე". */}
                {f.key === 'lease_end' && leaseSummary && (
                  <div className={`font-mono text-[10px] tracking-[.02em] ${INK_MUTED} space-y-0.5 min-w-0`} data-field="lease_summary">
                    <div className="break-words" data-lease-line-short>{leaseSummary.short}</div>
                    <div className="break-words" data-lease-line-detail>{leaseSummary.detail}</div>
                  </div>
                )}
              </Fragment>
            )
  }
  // 💸 THE LEASE'S MONEY AS THREE QUESTIONS (owner 2026-09-17) — see contract/RentalPaymentPlan.jsx
  const fieldsByKey = Object.fromEntries((fields || []).map(x => [x.key, x]))
  const blockProps = { values, fieldsByKey, renderRow, opening: state.opening && state.openingDone ? state.opening : null,
    openingQuestionsList: openQs, onChangeOpening: () => setState(x => ({ ...x, openingDone: false })) }
  const signingBlock = paymentPlanOn ? <RentalSigningBlock key="__signing__" {...blockProps} /> : null
  const handoverBlock = paymentPlanOn ? <RentalHandoverBlock key="__handover__" {...blockProps} /> : null
  const paymentPlan = paymentPlanOn ? (
    <RentalPaymentPlan key="__payment_plan__" values={values} fieldsByKey={fieldsByKey} setValue={setValue} renderRow={renderRow} langs={langs || ['ka']}
      optionText={optionText} inputClass={PINP} monthlyRoute={state.paymentMonthlyRoute ?? null}
      setMonthlyRoute={r => setState(x => ({ ...x, paymentMonthlyRoute: r }))}
      signingChosen={state.paymentSigningChosen !== false}
      onSigningChosen={() => setState(x => (x.paymentSigningChosen === false ? { ...x, paymentSigningChosen: true } : x))}
      opening={state.opening && state.openingDone ? state.opening : null} openingQuestionsList={openQs}
      onChangeOpening={() => setState(x => ({ ...x, openingDone: false }))} />
  ) : null

  if (openingStep) return (
    <fieldset className="text-[13px] text-[#14130F] min-w-0" disabled={busy} data-testid="contract-paper">{openingStep}</fieldset>
  )
  return (
    <fieldset className="space-y-5 text-[13px] text-[#14130F] min-w-0" disabled={busy} data-testid="contract-paper">
      {/* PARTIES — identity (name as on the ID, ID kind + number; a company: registration
          number + representative) and contact (phone, email, the sharing rule). Editable
          until signed; a change after a signature goes through the same confirm as any other
          edit (a new version). Each party confirms its own identity on its link (header). */}
      <section className="space-y-3" data-group="parties">
        <div className={GROUP}>{t('desk_contract_parties')}</div>
        {parties.map((p, i) => {
          const company = p.kind === 'company'
          // the same completeness rule the readiness gate uses (🔒 above)
          // ⚠️ The SAME three tests composerReadiness runs, so the marks on the page and the
          // list in the sticky bar can never name different things.
          const nameBlank = p.required !== false && !String(p.legal_name || '').trim()
          const idBlank = p.required !== false && !String(p.id_number || '').trim()
          const repBlank = p.required !== false && company && !String(p.rep_name || '').trim()
          const blank = p.required && (nameBlank || idBlank || repBlank)
          const idKind = company ? 'company_reg' : (ID_KINDS_PERSON.includes(p.id_kind) ? p.id_kind : (preferredOf(p, chosen) === 'ka' ? 'personal_no' : 'passport'))
          const idShort = idKind === 'personal_no' && String(p.id_number || '').length > 0 && String(p.id_number || '').length !== 11
          // Both sides of the pair are marked, not just the second one typed: neither is "the
          // wrong one" — the agent has to look at two numbers and decide which of them is a
          // person, and marking one would name a culprit the form cannot know.
          const pk = partyKey(p)
          const resident = p.role === 'resident'
          const idDup = !!idProblems.get(pk)?.has('id_duplicate')   // a SET, not an array
          // 🔒 THE AGENCY IS NOT AN EDITABLE PARTY (owner 2026-09-12). Its name, registration
          // number and representative are the company's, not this agent's to type: the card
          // states them and offers exactly one control, and only to an agent the admin has
          // authorised — "sign as me" instead of the director. `agencyRep.self` comes from the
          // server (the draft payload), so the switch cannot appear where the server would
          // refuse it, and its absence is the whole UI half of the rule.
          if (p.role === 'agency') {
            const view = agencyRepShown({ options: agencyRep, repPartyId: p.rep_party_id })
            const mine = view.canSwitch ? agencyRep.self : null
            const dflt = agencyRep?.default || null
            const onSelf = view.onSelf
            const lang = chosen[0] || 'ka'
            const nameOf = x => x ? (x[`name_${lang}`] || x.name_ka || x.name_en || '') : ''
            const titleOf = x => x ? (x[`title_${lang}`] || x.title_ka || x.title_en || '') : ''
            const shown = view.shown
            return (
              <div key={p.role} className="space-y-1.5" data-party-role="agency">
                <div className="flex items-baseline justify-between gap-2">
                  <span className={`font-mono text-[10px] tracking-[.12em] uppercase ${INK_MUTED}`}>{tOr('desk_contract_role_agency', 'agency')}</span>
                  <span className={TAG}>{t('desk_contract_agency_locked')}</span>
                </div>
                <div className="text-[13px] font-medium">{p.legal_name || p.display_name}</div>
                <div className={`text-[12px] ${INK_MUTED}`} data-testid="contract-agency-rep">
                  {shown
                    ? `${t('desk_contract_agency_signed_by')}: ${nameOf(shown)}${titleOf(shown) ? ` — ${titleOf(shown)}` : ''}`
                    : (p.rep_name ? `${t('desk_contract_agency_signed_by')}: ${p.rep_name}${p.rep_position ? ` — ${p.rep_position}` : ''}` : t('desk_contract_agency_no_signatory'))}
                </div>
                {mine && (
                  <div className="flex items-center gap-3 text-[11px]" role="radiogroup" aria-label={t('desk_contract_agency_signed_by')} data-testid="contract-agency-switch">
                    {[['default', dflt], ['self', mine]].map(([k, who]) => {
                      const on = (k === 'self') === onSelf
                      return (
                        <button key={k} type="button" role="radio" aria-checked={on} data-agency-pick={k}
                          className={on ? 'text-[#14130F] font-medium underline underline-offset-4 decoration-[#1F6F4F] decoration-2' : `${INK_MUTED} hover:text-[#14130F]`}
                          onClick={() => setParty(i, { rep_party_id: k === 'self' ? mine.party_id : (dflt?.party_id || null) })}>
                          {k === 'self' ? t('desk_contract_agency_me') : nameOf(dflt) || t('desk_contract_agency_default')}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          }
          return (
            <div key={pk} className={`space-y-2 ${blank ? 'border-l-2 border-[#1F6F4F] pl-2 -ml-2' : ''}`} data-party-role={p.role} data-party-seat={seatOf(p)}>
              {/* Role + source tag, then the person/company choice as a LIGHT text switch above
                  the name (owner 2026-09-11: "move the toggle above the name and make it
                  visually lighter — it competes with the identity fields"). */}
              <div className="flex items-baseline justify-between gap-2">
                <span className={`font-mono text-[10px] tracking-[.12em] uppercase ${INK_MUTED}`}>{partyWho(p, parties)}
                  {(seatOf(p) > 1 || resident) && (
                    <button type="button" className="ml-2 normal-case tracking-normal underline underline-offset-2 hover:text-[#8A4B2A]" data-remove-seat={pk}
                      onClick={() => setState(st => ({ ...st, parties: removeSeat(st.parties, pk) }))}>{t(resident ? 'desk_contract_remove_resident' : 'desk_contract_remove_co_owner')}</button>
                  )}
                </span>
                {!resident && <span className={blank ? TAG_ASK : TAG} data-party-source={p.source || ''}>{blank ? t('desk_contract_src_none') : p.identity_confirmed_at ? t('desk_contract_identity_confirmed') : rerenderOf ? tLog('desk_contract_src_version', { v: rerenderOf.current_version?.version ?? '' }) : p.source === 'contract' && p.identity_from_contract ? t('desk_contract_src_prior_contract') : p.source === 'contract' ? t('desk_contract_src_contract') : p.source === 'fill_form' ? t('desk_contract_src_fill_form') : p.source === 'requirement' ? t('desk_contract_src_requirement') : p.source === 'added' ? t('desk_contract_src_added') : t('desk_contract_src_meeting')}{!rerenderOf && p.nationality ? ` · ${p.nationality}` : ''}</span>}
              </div>
              {/* 🏠 a RESIDENT (owner 2026-09-29) fills the same person form, all of it optional: listed in the
                  lease, never signing — so no link language, no required marks, no sharing switches */}
              {resident && <div className={`text-[11px] ${INK_MUTED}`} data-resident-note>{t('desk_contract_resident_note')}</div>}
              {!resident && <div className="flex items-center gap-3 text-[11px]" role="radiogroup" aria-label={t('desk_contract_party_kind')} data-party-kind={p.kind || 'person'}>
                {['person', 'company'].map(k => {
                  const on = (p.kind || 'person') === k
                  return (
                    <button key={k} type="button" role="radio" aria-checked={on} data-party-kind-pick={k}
                      className={on ? 'text-[#14130F] font-medium underline underline-offset-4 decoration-[#1F6F4F] decoration-2' : `${INK_MUTED} hover:text-[#14130F]`}
                      onClick={() => setParty(i, { kind: k, id_kind: k === 'company' ? 'company_reg' : (ID_KINDS_PERSON.includes(p.id_kind) ? p.id_kind : 'personal_no') })}>{t(`desk_contract_party_kind_${k}`)}</button>
                  )
                })}
              </div>}
              {/* THE LINK LANGUAGE IS THE KEY (owner 2026-09-11: "if it's Georgian the nationality
                  is Georgian too"): a Georgian-language party holds a Georgian ID card (personal
                  number), any other a passport — the ID kind follows this choice until a number
                  is typed. And "use the name that's in the document": no per-language spellings
                  ("no need to have 3 translations right now") — the legal name as written on
                  that document is printed in every column. */}
              {!resident && <label className="flex items-center gap-2 text-[11px]">
                <span className={INK_MUTED}>{t('desk_contract_link_lang')}</span>
                <select className={`${PINP} max-w-full min-w-0 py-1`} value={preferredOf(p, chosen)} data-preferred-lang
                  onChange={e => { const l = e.target.value; setParty(i, { preferred_language: l, lang_chosen: true, ...(!company && !String(p.id_number || '').trim() ? { id_kind: l === 'ka' ? 'personal_no' : 'passport' } : {}) }) }}>
                  {chosen.map(l => <option key={l} value={l}>{tOr(`desk_contract_lang_${l}`, l)}</option>)}
                </select>
              </label>}
              {/* 🚨 REQUIRED, AND IT SAYS SO (owner, 2026-09-12: "found it — პირადი ნომერი · 11
                  ციფრი, but there is no sign that it's required"). The identity became required
                  on 2026-09-11 (B1275) and the fields kept the plain label a nice-to-have has,
                  so Create sat grey with its reason two feet away in the bar. They now carry the
                  same * and the same green blank-marker the template's own required fields use —
                  one visual language for "this is why you cannot save yet".
                  `data-field` matches the key composerReadiness emits, so the bar's named item
                  can bring the agent straight here. */}
              <label className={`block min-w-0 ${nameBlank ? 'border-l-2 border-[#1F6F4F] pl-2 -ml-2' : ''}`} data-field={`party:${pk}:name`} data-blank={nameBlank ? '1' : '0'}>
                <span className={`block text-[11px] ${nameBlank ? 'text-[#1F6F4F] font-medium' : INK_MUTED}`}>
                  {company ? t('desk_contract_company_name') : t('desk_contract_legal_name')}{resident ? '' : ' *'}
                </span>
                <AutoGrowText className={`${PINP} w-full font-medium`} value={p.legal_name || ''} aria-required={resident ? undefined : 'true'}
                  placeholder={company ? t('desk_contract_company_name_ph') : t('desk_contract_party_name_ph')} data-name-field="legal_name"
                  onChange={e => setParty(i, { legal_name: e.target.value })} />
              </label>
              {/* THE IDENTITY (owner 2026-09-11): the person/company switch above decides the
                  document family, so the selector only ever offers what that party can hold —
                  a person: პირადი ნომერი · პასპორტი; a company: no selector at all, its one
                  document is the registration number (owner, same day: "on company we don't need
                  a document; when ფიზიკური პირი is selected no need for company"). The number
                  field's label follows the kind; a personal number is digits only, exactly 11
                  (digits typed past eleven are dropped, fewer show the rule). */}
              <div className="grid gap-2 sm:grid-cols-2 items-end">
                {!company && (
                  <label className="min-w-0">
                    <span className={`block text-[11px] ${INK_MUTED}`}>{t('desk_contract_id_kind')}</span>
                    <select className={`${PINP} w-full`} value={idKind} data-id-kind
                      onChange={e => { const k = e.target.value; setParty(i, { id_kind: k, id_number: k === 'personal_no' ? String(p.id_number || '').replace(/\D/g, '').slice(0, 11) : (p.id_number || '') }) }}>
                      {ID_KINDS_PERSON.map(k => <option key={k} value={k}>{t(`desk_contract_id_${k}`)}</option>)}
                    </select>
                  </label>
                )}
                <label className={`min-w-0 ${idBlank ? 'border-l-2 border-[#1F6F4F] pl-2 -ml-2' : ''}`} data-field={`party:${pk}:id`} data-blank={idBlank ? '1' : '0'}>
                  <span className={`block text-[11px] ${idBlank ? 'text-[#1F6F4F] font-medium' : INK_MUTED}`}>{t(`desk_contract_idnum_${idKind}`)}{resident ? '' : ' *'}</span>
                  <input className={`${PINP} w-full ${idShort || idDup ? 'border-[#8A4B2A]' : ''}`} value={p.id_number || ''} data-id-number
                    inputMode={idKind === 'personal_no' ? 'numeric' : 'text'} maxLength={idKind === 'personal_no' ? 11 : 40} aria-invalid={idShort || idDup || undefined}
                    placeholder={t('desk_contract_id_number_ph')} aria-required={resident ? undefined : 'true'}
                    onChange={e => setParty(i, { id_number: idKind === 'personal_no' ? e.target.value.replace(/\D/g, '').slice(0, 11) : e.target.value, id_kind: idKind })} />
                  {idShort && <span className="block text-[11px] text-[#8A4B2A]" data-id-error>{t('desk_contract_id_11_error')}</span>}
                  {idDup && <span className="block text-[11px] text-[#8A4B2A]" data-id-duplicate>{t('desk_contract_id_duplicate_error')}</span>}
                </label>
                {company && (
                  <>
                    <label className={`min-w-0 ${repBlank ? 'border-l-2 border-[#1F6F4F] pl-2 -ml-2' : ''}`} data-field={`party:${pk}:rep`} data-blank={repBlank ? '1' : '0'}>
                      <span className={`block text-[11px] ${repBlank ? 'text-[#1F6F4F] font-medium' : INK_MUTED}`}>{t('desk_contract_rep_name')} *</span>
                      <input className={`${PINP} w-full`} value={p.rep_name || ''} aria-required="true" data-rep-name placeholder={t('desk_contract_party_name_ph')}
                        onChange={e => setParty(i, { rep_name: e.target.value })} />
                    </label>
                    <label className="min-w-0">
                      <span className={`block text-[11px] ${INK_MUTED}`}>{t('desk_contract_rep_position')}</span>
                      <input className={`${PINP} w-full`} value={p.rep_position || ''} data-rep-position placeholder={t('desk_contract_rep_position_ph')}
                        onChange={e => setParty(i, { rep_position: e.target.value })} />
                    </label>
                  </>
                )}
              </div>
              {/* THE CONTACT, each with a SMALL sharing switch at the right of its label (owner
                  2026-09-11: "გაზიარება ✓, same meaning, much less clutter") — phone shared by
                  default, email not. Shared = printed in the document and shown to the other party. */}
              <div className="grid gap-2 sm:grid-cols-2">
                {[['phone', 'share_phone', p.share_phone !== false, 'tel', '+995 5xx xx xx xx', 'data-party-phone', 'data-share-phone'],
                  ['email', 'share_email', p.share_email !== false, 'email', 'name@example.com', 'data-party-email', 'data-share-email']].map(([key, shareKey, shared, type, ph, inputAttr, btnAttr]) => (
                  <div key={key} className="min-w-0" data-field={`party:${pk}:${key}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className={`text-[11px] ${INK_MUTED}`}>{t(`desk_contract_${key}`)}</span>
                    </div>
                    <input className={`${PINP} w-full ${contactProblems(p).includes(key) ? 'border-[#8A4B2A]' : ''}`}
                      value={p[key] || ''} type={type} inputMode={type} {...{ [inputAttr]: '' }} placeholder={ph}
                      aria-invalid={contactProblems(p).includes(key) ? 'true' : undefined}
                      onChange={e => setParty(i, { [key]: e.target.value })}
                      onBlur={key === 'phone' ? e => {
                        // on the way OUT of the box, not on every keystroke: rewriting a number
                        // while it is half-typed moves the caret and fights the agent
                        const n = normalizePhone(e.target.value)
                        if (n !== e.target.value) setParty(i, { phone: n })
                      } : undefined} />
                    {contactProblems(p).includes(key) && (
                      <div className="text-[11px] text-[#8A4B2A] mt-0.5" data-contact-bad={key}>{t(`desk_contract_bad_${key}`)}</div>
                    )}
                  </div>
                ))}
              </div>
              {/* 🔒 ONE TOGGLE PER PARTY (lease v15, owner 2026-09-23), OFF by default: on = the other party
                  receives exactly these details once every required party has signed; off = never. The
                  signer can change it on their own page; what they choose is what the contract records. */}
              {/* 🔒 ONE CHECKBOX ON EVERY CONTRACT (owner 2026-09-23: "we have different sharing buttons, not
                  good"). Contracts without the lease's notice model have no after-signing contact section,
                  so here hidden = not printed at all; off = phone and email printed from the start. */}
              {!privateModel && !resident && (() => {
                const hidden = p.share_phone === false && p.share_email === false
                const shown = [p.share_phone !== false && p.phone, p.share_email !== false && p.email].filter(Boolean)
                return (
                  <label className="flex items-start gap-2 text-[12px] cursor-pointer" data-contact-omit-toggle={p.role}>
                    <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#1F6F4F]" checked={hidden}
                      onChange={e => setParty(i, { share_phone: !e.target.checked, share_email: !e.target.checked })} />
                    <span>{t('desk_contract_contact_omit')}
                      <span className={`block text-[11px] ${INK_MUTED}`}>{hidden ? t('desk_contract_contact_omit_on') : `${t('desk_contract_contact_omit_off')}${shown.length ? ` ${shown.join(' · ')}` : ''}`}</span>
                    </span>
                  </label>
                )
              })()}
              {privateModel && noticeSideOf(p.role) && (() => {
                // v16: "hide until signed" (off = visible from the start); v15: "share after signing" (off = never)
                const hideModel = (fields || []).some(f => f.key === `${noticeSideOf(p.role)}_contact_hide`)
                if (hideModel) {
                  const k = `${noticeSideOf(p.role)}_contact_hide`, on = values[k] === true || values[k] === 'true'
                  return (
                    <label className="flex items-start gap-2 text-[12px] cursor-pointer" data-contact-hide-toggle={p.role}>
                      <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#1F6F4F]" checked={on} onChange={e => setValue(k, e.target.checked)} />
                      <span>{t('desk_contract_contact_hide')}
                        <span className={`block text-[11px] ${INK_MUTED}`}>{on ? t('desk_contract_contact_hide_on') : t('desk_contract_contact_hide_off')}</span>
                      </span>
                    </label>
                  )
                }
                const k = `${noticeSideOf(p.role)}_contact_share`, on = values[k] === true || values[k] === 'true'
                return (
                  <label className="flex items-start gap-2 text-[12px] cursor-pointer" data-contact-share-toggle={p.role}>
                    <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#1F6F4F]" checked={on} onChange={e => setValue(k, e.target.checked)} />
                    <span>{t('desk_contract_contact_share')}
                      <span className={`block text-[11px] ${INK_MUTED}`}>{on ? `${t('desk_contract_contact_share_on')} ${[p.phone, p.email].filter(Boolean).join(' · ') || '—'}` : t('desk_contract_contact_share_off')}</span>
                    </span>
                  </label>
                )
              })()}
              {!resident && <div className={`text-[11px] ${INK_MUTED}`} data-identity-note>{t('desk_contract_identity_note')}</div>}
              {/* 🏠 RESIDENTS, one at a time (owner 2026-09-29: "we write 3 persons, we see add details of person 2,
                  then person 3"): the tenant is person 1, so a lease stating N residents offers N−1 more, and the
                  button sits under the tenant or the last resident. Only the lease that states the number has it. */}
              {(p.role === 'tenant' || resident) && !parties.slice(i + 1).some(x => x.role === 'resident') && residentRoom(fields, values, parties) > 0 && (
                <button type="button" className={`block text-[12px] underline underline-offset-4 decoration-[#1F6F4F] ${INK_MUTED} hover:text-[#14130F]`} data-add-seat="resident"
                  onClick={() => setState(st => ({ ...st, parties: addSeat(st.parties, 'resident', { after: 'tenant' }) }))}>＋ {tLog('desk_contract_add_resident', { n: parties.filter(x => x.role === 'resident').length + 2 })}</button>
              )}
              {p.role === 'owner' && (fields || []).some(f => f.key === 'owner_authority') && !parties.slice(i + 1).some(x => x.role === 'owner') && canAddSeat(parties, 'owner') && (
                <button type="button" className={`text-[12px] underline underline-offset-4 decoration-[#1F6F4F] ${INK_MUTED} hover:text-[#14130F]`} data-add-seat="owner"
                  onClick={() => setState(st => ({ ...st, parties: addSeat(st.parties, 'owner') }))}>＋ {t('desk_contract_add_co_owner')}</button>
              )}
            </div>
          )
        })}
      </section>

      {/* VALUES, grouped in document order. Each row: label above · input · the source tag at
          the right of the label line. The highlighted ask is the only row with colour. */}
      {groups.map(({ g, rows }) => (
        <section key={g} className="space-y-3" data-group={g}>
          <div className={GROUP}>{tOr(`desk_contract_group_${g}`, g)}</div>
          {rows.map(f => f.key === '__payment_plan__' ? paymentPlan : f.key === '__signing__' ? signingBlock : f.key === '__handover__' ? handoverBlock : renderRow(f))}
        </section>
      ))}

      {/* 📷 PHOTOS SIT AT THE END OF THE FILL, BEFORE THE SECTION TEXTS (owner 2026-09-12:
          "let's move photos before sections", "at the end of the fill"). They belong with the
          answering, not with the clause wording: an agent fills the boxes, photographs the flat,
          and only then reads the text. It arrives as a SLOT rather than an import because the
          annex is owned by the page (it has its own frozen version, its own signatures and its
          own write rules) and the composer is a form — the form lends it a place, nothing more. */}
      {afterFields}

      {/* THE SECTIONS as slim rows: clause text stays collapsed until one is tapped. On a
          desktop the editor opens INLINE under its row, both languages side by side; on a
          phone it is a full-screen sheet over the page. */}
      {sections && sections.length > 0 && (
        <section className="space-y-2" data-group="sections">
          <div className={GROUP}>{t('desk_contract_section_editors')}</div>
          {!readOnlySections && <div className={`text-[11px] ${INK_MUTED}`}>{t('desk_contract_sections_tap_hint')}</div>}
          <SectionSlimRows sections={sections} langs={chosen} issues={issues} activeKey={activeKey} readOnly={readOnlySections}
            onOpen={key => onOpenSection && onOpenSection(activeKey === key && wide ? null : key)}
            inline={wide && active ? { key: active.key, node: editor } : null} />
          <DriftIssues issues={issues} />
        </section>
      )}
      {!wide && editor}
      {partialPaymentOpen && <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4" role="presentation">
        <div role="dialog" aria-modal="true" aria-label={t('desk_contract_partial_payment')} className="w-full max-w-sm rounded-xl bg-[#FAF9F5] p-4 text-[#14130F] shadow-xl space-y-3">
          <div className="text-base font-semibold">{t('desk_contract_partial_payment')}</div>
          <div className="text-xs text-[#555]">{t('desk_contract_commission_colon')} {Number.isFinite(commissionFee) ? `${commissionFee} ${values.fee?.currency || ''}` : '—'}</div>
          <label className="block text-xs">{t('desk_contract_amount_paid')}
            <div className="mt-1 flex gap-2"><AmountInput className={`${PINP} w-28`} value={partialPayment.amount}
              onChange={n => setPartialPayment(p => ({ ...p, amount: n }))} />
              <span className="py-2">{values.fee?.currency || 'USD'}</span></div>
          </label>
          <label className="block text-xs">{t('desk_contract_payment_date')}
            <div className="mt-1"><DateInput value={partialPayment.on} onChange={d => setPartialPayment(p => ({ ...p, on: d }))} className={PDATE} /></div>
          </label>
          {commissionBalance != null && Number(partialPayment.amount) > 0 &&
            <div className="text-xs">{t('desk_contract_remaining_colon')} {Math.round((commissionBalance - Number(partialPayment.amount)) * 100) / 100} {values.fee?.currency || ''}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" className="rounded border px-3 py-2 text-xs" onClick={() => setPartialPaymentOpen(false)}>{t('desk_contract_close')}</button>
            <button type="button" className="rounded bg-[#1F6F4F] px-3 py-2 text-xs text-white"
              disabled={!partialPayment.on || !(Number(partialPayment.amount) > 0) || !(Number(partialPayment.amount) < commissionBalance)}
              onClick={() => { setState(s => ({ ...s, values: { ...s.values, commission_status: 'partial', commission_paid_amount: { value: Number(partialPayment.amount), currency: s.values.fee?.currency || 'USD' }, commission_paid_on: partialPayment.on } })); setPartialPaymentOpen(false) }}>{t('desk_contract_confirm')}</button>
          </div>
        </div>
      </div>}
    </fieldset>
  )
}

// `values` is the whole form — the prepayment list reads the rent (currency default, the
// amount of a full month) and the deposit (the amount of a full deposit row) from it.
// `paper` — draw with the paper's inputs (./contract/paper.js) instead of the Desk's dark ones.
// `periodMonths` — the lease period in force (null = none), for the prepayment month list;
// `disabled` — a read-only control (kept for callers; the lease end is always editable now —
// editing it recalculates the duration, owner ruling 2026-09-11).
//
// WIDTHS (owner, phone testing 2026-09-11: the payment-method select overflowed the paper).
// Every control is `max-w-full min-w-0` so nothing can be wider than its row: a flex item's
// minimum width is its CONTENT by default, and a select's content is its longest option, so
// a long Georgian label pushed the box past the 390 px sheet. Selects and text go `w-full`
// on phones (`sm:w-auto` from 640 px); the money amount keeps its fixed width but may shrink.
const FIT = 'max-w-full min-w-0'
// 📐 A LONG VALUE MUST BE READABLE, NOT MERELY STORED (owner 2026-09-12, twice — and the second
// time to point out I had not acted on the first: "address still overflows… did you follow all my
// previous messages"). A single-line <input> clips whatever does not fit, so on a phone the
// address „თბილისი, ვაკე-საბურთალო, საბურთალო, კოსტავას ქ. 68ბ" showed as far as „საბურთა…" and
// the agent could not read the address they were about to put into a contract.
//
// 🔑 FIXED IN THE CONTROL, NOT IN THE FIELD. Converting `address` to a textarea in three templates
// would have fixed three fields and left the next long value — recipient details, a power of
// attorney reference, an app name — to be reported again. Every text field grows instead.
//
// ⚠️ Enter is suppressed. This is a textarea for WRAPPING, not for multi-line input: a newline
//    inside an address would travel into the rendered contract and into the PDF, where it becomes
//    a line break nobody chose in the middle of a legal document. The value stays one line; only
//    its display wraps.
function AutoGrowText({ className, value, disabled, onChange, ...rest }) {
  const fit = el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px' } }
  return (
    <textarea
      {...rest}
      className={className}
      rows={1}
      style={{ resize: 'none', overflow: 'hidden' }}
      value={value}
      disabled={disabled}
      ref={fit}
      onKeyDown={e => { if (e.key === 'Enter') e.preventDefault() }}
      onInput={e => fit(e.currentTarget)}
      onChange={onChange}
    />
  )
}

export function FieldInput({ f, v, set, values, paper = false, periodMonths = null, disabled = false, depositSlot = null, agents = null }) {
  const cls = paper ? PINP : inp
  switch (f.kind) {
    case 'prepay_list':
      return <PrepayListInput v={v} set={set} values={values || {}} paper={paper} periodMonths={periodMonths} depositSlot={depositSlot} required={!!(f.required || f.for_lang)} />
    case 'date': {
      // The rate date is the one date an agent sets from another date they already answered, so it
      // gets shortcuts. Everything else is just a date.
      const shortcuts = f.key === 'fx_rate_date' ? fxDateChoices(values || {}) : []
      const active = shortcuts.length ? activeFxChoice(v, values || {}) : null
      return (
        <span className="inline-flex flex-wrap items-center gap-1">
          <DateInput value={v || ''} onChange={set} className={`${paper ? PDATE : ''} ${FIT}`} disabled={disabled} />
          {shortcuts.map(s => (
            <button key={s.id} type="button" disabled={disabled} onClick={() => set(s.date)}
              title={s.date}
              className={`px-1.5 py-0.5 text-[11px] rounded border ${active === s.id
                ? 'border-[#1F6F4F] text-[#1F6F4F] bg-[#1F6F4F]/10'
                : 'border-slate-300 text-slate-600 hover:border-slate-400'}`}>
              {t(`desk_contract_fxdate_${s.id}`)}
            </button>
          ))}
        </span>
      )
    }
    case 'datetime':
      return <DateTimeInput value={v || ''} onChange={set} className={`${cls} ${FIT}`} disabled={disabled} />
    case 'money':
      return (
        <>
          <AmountInput className={`${cls} w-28 ${FIT}`} value={v?.value ?? ''} disabled={disabled}
            onChange={n => set({ ...(v || {}), value: n })} />
          <select className={`${cls} ${FIT}`} value={v?.currency || 'USD'} disabled={disabled} onChange={e => set({ ...(v || {}), currency: e.target.value })}>
            {CURRENCIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </>
      )
    case 'agent':
      // 🧑‍💼 CHOSEN FROM THE ROSTER, NEVER TYPED (owner 2026-09-13). The list is senTavus's
      // authorised signatories, which the draft already carries for the agency party row. Stores
      // {party_id,name}: the id is the durable link to the person, the name is what the contract
      // says — a contract that named only a uuid would be unreadable, and one that stored only a
      // name could not be reconciled against anybody.
      // ⚠️ When the list has not loaded, the box is EMPTY and disabled rather than a text field.
      //    Falling back to free text would quietly reintroduce exactly what this replaces.
      return (
        <select className={`${cls} w-full sm:w-auto ${FIT}`} value={v?.party_id || ''} disabled={disabled || !(agents || []).length}
          onChange={e => {
            const a = (agents || []).find(x => x.party_id === e.target.value)
            set(a ? { party_id: a.party_id, name: L(a, 'name') || a.name_ka || a.name_en || a.name || '' } : null)
          }}>
          <option value="">{(agents || []).length ? '—' : t('desk_contract_agent_none')}</option>
          {(agents || []).map(a => (
            <option key={a.party_id} value={a.party_id}>{[L(a, 'name') || a.name_ka || a.name_en || a.name, a.title_ka].filter(Boolean).join(' — ')}</option>
          ))}
        </select>
      )
    case 'int':
      if (f.key === 'lease_break_after_months') {
        const lang = normLang(getLanguage())
        const count = Number.isInteger(periodMonths) ? Math.max(0, periodMonths - 1) : 0
        return <select className={`${cls} w-full sm:w-auto ${FIT}`} value={v ?? ''} disabled={disabled || count === 0}
          onChange={e => set(e.target.value === '' ? '' : Number(e.target.value))} data-break-month-select>
          <option value="">{count ? '—' : { ka: 'ჯერ მიუთითეთ სრული ვადა', en: 'Set the full term first', ru: 'Сначала укажите полный срок' }[lang]}</option>
          {Array.from({ length: count }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n} {lang === 'ka' ? 'თვე' : lang === 'ru' ? 'мес.' : n === 1 ? 'month' : 'months'}</option>)}
        </select>
      }
      {
        // `decimals` on the spec (contract_field_rules); the one legacy decimal key keeps its old behaviour
        const dec = hasDecimals(f) || f.key === 'tenant_agency_fee_value'
        return <input type="number" inputMode={dec ? 'decimal' : 'numeric'} step={hasDecimals(f) ? numberStep(f) : dec ? '0.01' : '1'}
          min={f.min ?? undefined} max={f.max ?? undefined} className={`${cls} w-24 ${FIT}`} value={v ?? ''} disabled={disabled}
          onChange={e => set(e.target.value === '' ? '' : hasDecimals(f) ? numberFromInput(f, e.target.value) : dec ? Number(e.target.value) : parseInt(e.target.value, 10))} />
      }
    case 'rows':
      return <RowsInput f={f} v={v} set={set} paper={paper} disabled={disabled} />
    case 'bool':
      return <input type="checkbox" className={paper ? 'accent-[#1F6F4F] w-4 h-4' : ''} checked={!!v} disabled={disabled} onChange={e => set(e.target.checked)} />
    case 'enum':
      return (
        <select className={`${cls} w-full sm:w-auto ${FIT}`} value={v ?? ''} disabled={disabled} onChange={e => set(e.target.value)}>
          <option value="">—</option>
          {(f.options || []).map(o => <option key={o.value} value={o.value}>{optionText(o)}</option>)}
        </select>
      )
    case 'multi': {
      // A checklist; each option may carry help text (the utilities explain how each is billed).
      const cur = Array.isArray(v) ? v : []
      return (
        <div className="flex flex-col gap-1 w-full">
          {(f.options || []).map(o => (
            <label key={o.value} className="flex items-start gap-1.5 cursor-pointer" title={L(o, 'help') || undefined}>
              <input type="checkbox" className={`mt-0.5 ${paper ? 'accent-[#1F6F4F]' : ''}`} checked={cur.includes(o.value)}
                onChange={e => set(e.target.checked ? [...cur, o.value] : cur.filter(x => x !== o.value))} />
              <span>{L(o, 'label') || o.value}
                {L(o, 'help') && <span className={`block text-[10px] leading-tight ${paper ? INK_MUTED : 'text-muted-foreground'}`}>{L(o, 'help')}</span>}
              </span>
            </label>
          ))}
        </div>
      )
    }
    case 'textarea':
      return <textarea className={`${cls} w-full min-h-[3.5rem] ${FIT}`} value={v ?? ''} disabled={disabled} onChange={e => set(e.target.value)} />
    default:
      return <AutoGrowText className={`${cls} w-full sm:w-auto sm:flex-1 ${FIT}`} value={v ?? ''} disabled={disabled} onChange={e => set(e.target.value)} />
  }
}

// ── A table field (kind "rows", 2026-09-24): one line per row, one box per column ─────────────
// Columns are text / textarea / date / enum. A blank row is not an answer and is not saved; a row with a
// required cell blank is named by the sticky bar (`rows:<key>`) — the server refuses the same.
export function RowsInput({ f, v, set, paper = false, disabled = false }) {
  const cls = paper ? PINP : inp
  const rows = Array.isArray(v) && v.length ? v : [{}]
  const cols = f.columns || []
  const put = (i, k, x) => set(rows.map((r, j) => (j === i ? { ...r, [k]: x } : r)))
  const max = Number(f.max_rows) > 0 ? Number(f.max_rows) : 50
  return (
    <div className="w-full space-y-2" data-rows-field={f.key}>
      {rows.map((r, i) => (
        <div key={i} className="grid gap-2 sm:grid-cols-[2fr_1.5fr_1fr_auto] items-end border-b border-[#14130F]/10 pb-2" data-rows-row={i}>
          {cols.map(c => (
            <label key={c.key} className="min-w-0">
              <span className={`block text-[11px] ${paper ? INK_MUTED : 'text-muted-foreground'}`}>{L(c, 'label') || c.key}{c.required ? ' *' : ''}</span>
              {c.kind === 'date'
                ? <DateInput value={r[c.key] || ''} onChange={x => put(i, c.key, x)} className={`${paper ? PDATE : ''} ${FIT}`} disabled={disabled} />
                : c.kind === 'enum'
                  ? <select className={`${cls} w-full ${FIT}`} value={r[c.key] || ''} disabled={disabled} onChange={e => put(i, c.key, e.target.value)}>
                      <option value="">—</option>
                      {(c.options || []).map(o => <option key={o.value} value={o.value}>{optionText(o)}</option>)}
                    </select>
                  : <AutoGrowText className={`${cls} w-full ${FIT}`} value={r[c.key] || ''} disabled={disabled} onChange={e => put(i, c.key, e.target.value)} />}
            </label>
          ))}
          <button type="button" className={`text-[12px] ${paper ? INK_MUTED : 'text-muted-foreground'} hover:text-[#8A4B2A]`} disabled={disabled}
            aria-label={t('desk_contract_rows_remove')} data-rows-remove={i}
            onClick={() => set(rows.filter((_, j) => j !== i))}>✕</button>
        </div>
      ))}
      {rows.length < max && (
        <button type="button" className={`text-[12px] underline underline-offset-4 decoration-[#1F6F4F] ${paper ? INK_MUTED : ''}`} disabled={disabled}
          data-rows-add onClick={() => set([...rows, {}])}>＋ {t('desk_contract_rows_add')}</button>
      )}
    </div>
  )
}

// ── The prepayment list (header, ruling 1 + 2) ────────────────────────────────────────────
// One row per month paid at signing. "სრულად" (default on) means the rent amount, so a full
// row asks nothing more; unticked, the row asks paid / remaining / due. The currency select
// serves both figures of a row (the API carries it on each). A new row takes the first month
// not yet listed, then "last". THE DEPOSIT IS NOT A ROW (owner 2026-09-11: "Writing in means
// there is deposit paid") — the deposit money box is the fact, and the sentence below the rows
// adds the deposit line by itself when that box carries an amount (prepaySentence.js header).
// Under the rows: the sentence the contract will carry, live, in the UI language.
//
// THE MONTH LIST FOLLOWS THE PERIOD (owner, phone testing 2026-09-11): with N WHOLE months
// in the start→end interval (floor — "6 თვე და 12 დღე" lists six; the Composer computes N
// from the two dates) the select offers 1 … N−1, then ONE row for the last month — labelled "ბოლო თვე
// (მე-N)", VALUE still 'last', which the server understands; never both "მე-12 თვე" and
// "ბოლო თვე" on a 12-month lease. No period known → today's full list. A
// stored month outside the list (a '12' on a lease later shortened) is kept as that row's
// own extra option rather than silently shown as the first one.
export const rentCurrencyOf = values => values?.rent?.currency || 'USD'

export function nextPrepayMonth(list, options = PREPAY_MONTHS) {
  const used = new Set((list || []).map(r => String(r.month)))
  if (used.has('1') && !used.has('last') && options.includes('last')) return 'last'
  return options.find(m => !used.has(m)) || options[0]
}

export function PrepayListInput({ v, set, values, paper = false, periodMonths = null, depositSlot = null, required = false }) {
  const cls = paper ? PINP : inp
  const mutedCls = paper ? INK_MUTED : 'text-muted-foreground'
  const list = Array.isArray(v) ? v : []
  const options = prepayMonthOptions(periodMonths)
  const lang = normLang(getLanguage())
  const remoteAllocation = values?.remote_transaction === true
  const cur = rentCurrencyOf(values)
  const patch = (i, p) => set(list.map((r, j) => (j === i ? { ...r, ...p } : r)))
  const money = (m, value, currency) => ({ value, currency: currency || m?.currency || cur })
  const setFull = (i, full) => {
    const r = list[i]
    if (full) {
      // Back to a full month: the partial figures are dropped from the VALUE (the API reads
      // `full`, and stale figures beside it would be two answers to one question).
      set(list.map((x, j) => (j === i ? { month: r.month, full: true } : x)))
    } else {
      patch(i, { full: false, paid: r.paid || { value: null, currency: cur }, remaining: r.remaining || { value: null, currency: cur }, due: r.due || '' })
    }
  }
  // PAID ↔ REMAINING are one number apart (owner 2026-09-11: "it's calculable in both ways"):
  // typing the paid amount fills the remaining as rent − paid, typing the remaining fills the
  // paid as rent − remaining — only when the rent is known and the row's currency is the
  // rent's (a GEL part-payment on a USD rent would invent a rate, the binder rule). The field
  // just typed is never overwritten; a blank clears its counterpart's derived value only when
  // that counterpart was itself derived (the agent's own figure stays).
  const rentAmt = isMoney(values?.rent) ? values.rent.value : null
  const counterpart = (typed, rowCur) => (rentAmt != null && rowCur === (values?.rent?.currency || rowCur) && typeof typed === 'number' && typed >= 0 && typed <= rentAmt)
    ? Math.round((rentAmt - typed) * 100) / 100 : null
  const setPaid = (i, r, v, rowCur) => {
    const paid = v === '' ? null : Number(v)
    const rem = paid == null ? null : counterpart(paid, rowCur)
    patch(i, { paid: money(r.paid, paid, rowCur), ...(rem != null ? { remaining: money(r.remaining, rem, rowCur) } : {}) })
  }
  const setRemaining = (i, r, v, rowCur) => {
    const rem = v === '' ? null : Number(v)
    const paid = rem == null ? null : counterpart(rem, rowCur)
    patch(i, { remaining: money(r.remaining, rem, rowCur), ...(paid != null ? { paid: money(r.paid, paid, rowCur) } : {}) })
  }
  const add = () => set([...list, { month: nextPrepayMonth(list, options), full: true }])
  const remove = i => set(list.filter((_, j) => j !== i))
  // the advance's list is the MAIN agreement's future payments (planned wording, as on the paper); only the
  // advance template carries an advance_amount field, so its presence identifies it here
  const sentence = remoteAllocation || !isMoney(values?.rent) ? '' : prepaySentence(list, lang, values?.rent, values?.deposit, creditableAtSigning(values), Object.prototype.hasOwnProperty.call(values || {}, 'advance_amount'))
  const num = (m) => (m && typeof m.value === 'number' ? m.value : '')

  return (
    <div className="w-full space-y-1" data-testid="contract-prepay">
      {/* a required, empty list says what to add — "pays nothing in advance" under a required star read
          as an answer the form then refused (live test 2026-09-23) */}
      {list.length === 0 && <div className={`text-[11px] italic ${required ? 'text-[#1F6F4F]' : mutedCls}`}>{t(required ? 'desk_contract_prepay_none_required' : 'desk_contract_prepay_none')}</div>}
      {list.map((r, i) => {
        const full = r.full !== false
        const rowCur = r.paid?.currency || r.remaining?.currency || cur
        return (
          <div key={i} className={`flex items-center gap-1.5 flex-wrap min-w-0 ${paper ? 'rounded-md border border-[#14130F]/10 bg-white/50 px-2 py-1.5' : ''}`} data-prepay-row={i}>
            <label className="flex items-center gap-1 min-w-0 max-w-full">
              <span className={`text-[10px] ${mutedCls}`}>{t('desk_contract_prepay_which')}</span>
              <select className={`${cls} ${FIT}`} value={String(r.month ?? '1')} data-prepay-month onChange={e => patch(i, { month: e.target.value })}>
                {(options.includes(String(r.month)) ? options : [...options, String(r.month)])
                  .filter(m => m === String(r.month) || !list.some((x, j) => j !== i && String(x?.month) === m))
                  .map(m => <option key={m} value={m}>{monthLabel(m, lang, periodMonths)}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-1">
              <input type="checkbox" className={paper ? 'accent-[#1F6F4F]' : ''} checked={full} data-prepay-full onChange={e => setFull(i, e.target.checked)} />
              {remoteAllocation ? (lang === 'ka' ? 'მთლიანი თვის ქირა პირველადი გადახდის შემადგენლობაში' : 'Full month included in initial payment') : t('desk_contract_prepay_full')}
            </label>
            {!full && (
              <>
                <label className="flex items-center gap-1">
                  <span className={`text-[10px] ${mutedCls}`}>{remoteAllocation ? (lang === 'ka' ? 'პირველად გადასახდელში' : 'In initial payment') : t('desk_contract_prepay_paid')}</span>
                  <AmountInput className={`${cls} w-24 ${FIT}`} value={num(r.paid)} data-prepay-paid
                    onChange={n => setPaid(i, r, n == null ? '' : n, rowCur)} />
                </label>
                <select className={`${cls} ${FIT}`} value={rowCur} data-prepay-currency
                  onChange={e => patch(i, { paid: money(r.paid, num(r.paid) === '' ? null : num(r.paid), e.target.value), remaining: money(r.remaining, num(r.remaining) === '' ? null : num(r.remaining), e.target.value) })}>
                  {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                </select>
                <label className="flex items-center gap-1">
                  <span className={`text-[10px] ${mutedCls}`}>{t('desk_contract_prepay_remaining')}</span>
                  <AmountInput className={`${cls} w-24 ${FIT}`} value={num(r.remaining)} data-prepay-remaining
                    onChange={n => setRemaining(i, r, n == null ? '' : n, rowCur)} />
                </label>
                <label className="flex items-center gap-1">
                  <span className={`text-[10px] ${mutedCls}`}>{t('desk_contract_prepay_due')}</span>
                  <DateInput value={r.due || ''} onChange={d => patch(i, { due: d || '' })} className={`${paper ? PDATE : ''} ${FIT}`} />
                </label>
              </>
            )}
            <button type="button" className={`${mutedCls} hover:text-red-700 px-1`} title={t('desk_contract_prepay_remove')}
              aria-label={t('desk_contract_prepay_remove')} data-prepay-remove onClick={() => remove(i)}>×</button>
          </div>
        )
      })}
      <button type="button" className={paper
        ? 'inline-flex items-center rounded-full border border-dashed border-[#14130F]/30 px-2.5 py-0.5 text-[11px] text-[#14130F]/70 hover:border-[#14130F]/60'
        : 'inline-flex items-center rounded-full border border-dashed border-border px-2.5 py-0.5 text-[11px] text-muted-foreground hover:border-primary/50'}
        data-prepay-add data-goto-focus onClick={add}>{t('desk_contract_prepay_add')}</button>
      {/* The deposit box, inside the prepayment (owner 2026-09-11) — the sentence below counts it. */}
      {depositSlot}
      {remoteAllocation && <div className={`text-[12px] italic ${mutedCls}`}>{lang === 'ka' ? 'ეს განაწილება არ ადასტურებს თანხის მიღებას.' : 'This allocation does not confirm receipt of funds.'}</div>}
      {sentence && (
        <div className={`text-[12px] leading-snug italic ${paper ? 'text-[#14130F]/75 border-l-2 border-[#14130F]/15 pl-2' : 'text-muted-foreground'}`} data-testid="contract-prepay-sentence">{sentence}</div>
      )}
    </div>
  )
}

// ── The rendered version ──────────────────────────────────────────────────────────────────
// The server's own self-contained fragment (no scripts, no external CSS — API §Rendering).
// Drawn ON THE PAPER: ink text, the server's own table layout for the language columns.
export function DocumentPreview({ html, className = '' }) {
  if (!html) return null
  return (
    <>
      <style>{`
        .contract-document-preview { overflow-wrap:anywhere; min-width:0; }
        .contract-document-preview table { max-width:100%; table-layout:fixed; }
        .contract-document-preview .contract-table { width:100%; border-collapse:collapse; font-size:11px; line-height:1.4; }
        .contract-document-preview .contract-table th, .contract-document-preview .contract-table td { border:1px solid #14130f22; padding:5px; overflow-wrap:anywhere; }
        .contract-document-preview .contract-table th { background:#14130f0a; font-weight:600; }
        @media (max-width:700px) {
          .contract-document-preview { overflow-x:hidden; }
          .contract-document-preview table.bi, .contract-document-preview table.bi > tbody, .contract-document-preview table.bi > tbody > tr { display:block; width:100%; }
          .contract-document-preview table.bi > tbody > tr > td[lang] { display:block; width:100%; padding:8px 0; border-left:0; }
          .contract-document-preview table.bi > tbody > tr > td[lang] + td[lang] { border-top:1px solid #14130f20; }
        }
      `}</style>
      <div className={`contract-document-preview overflow-auto text-[13px] leading-relaxed text-[#14130F] [&_table]:w-full [&_td]:align-top [&_td]:p-1.5 [&_h1]:text-base [&_h1]:font-semibold [&_h3]:text-base [&_h3]:font-semibold [&_p]:my-2 ${className}`}
        data-testid="contract-document" dangerouslySetInnerHTML={{ __html: safeHtml(html) }} />
    </>
  )
}

// Print = the DOCUMENT, not the Desk around it: a throwaway window holding only the
// version's fragment. `window.print()` on the app would print the header, the nav and the
// form beside the contract.
// THE PRINT IS THE WHOLE DOCUMENT (owner 2026-09-11: "printed version should have signatures
// too, and there should be a hash number in every type"): the version's bytes, then the
// signature block (the drawn signatures, who / when / how / in which language — or an empty
// line while a party has not signed), then the annex photos, and on EVERY PAGE a fixed header
// (title · version · short hash) and footer (agency · contract id · full hash) — the same
// identity the PDF carries. `window.open('')` is same-origin, so the annex photos are fetched
// with the viewer token and inlined as data urls before the print dialog opens.
export async function printContract(contract, { fetchPhoto = null } = {}) {
  const html = contract?.current_version?.rendered_html
  if (!html) return
  const w = window.open('', '_blank')
  if (!w) return
  const esc = x => String(x == null ? '' : x).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  const title = L(contract, 'template_title') || contract.template_code || ''
  const ver = contract.current_version || {}
  const hash = ver.sha256 || ''
  const langs = contract.langs || ['ka']
  const sigRows = (contract.parties || []).filter(p => p.required !== false && p.role !== 'resident').map(p => {   // 🏠 residents do not sign
    const sig = p.signature
    const name = p.legal_name || p.display_name || ''
    return `<div class="sig"><div class="role">${esc(tOr(`desk_contract_role_${p.role}`, p.role))}</div><div class="name">${esc(name)}</div>`
      + (sig
        ? (sig.image ? `<img class="ink" alt="" src="${esc(sig.image)}">` : `<div class="ink typed">${esc(sig.signer_name_typed || name)}</div>`)
          + `<div class="meta">✓ ${esc(tLog('desk_contract_signed_at', { at: fmtWhen(sig.signed_at) }))}`
          + (sig.method ? ` · ${esc(tOr(`desk_contract_method_${sig.method}`, sig.method))}` : '')
          + (sig.viewed_language ? `<br>${esc(t('desk_contract_viewed_lang'))}: ${esc(tOr(`desk_contract_lang_${sig.viewed_language}`, sig.viewed_language))}` : '') + `</div>`
        : `<div class="ink empty"></div><div class="meta">${esc(t('desk_contract_sig_none'))}</div>`)
      + `</div>`
  }).join('')
  // the annex: the photos this version's signatures cover, inlined
  let annexHtml = ''
  const photos = contract.annex?.photos || []
  if (photos.length) {
    const tiles = []
    for (const [i, ph] of photos.entries()) {
      let src = ''
      try { if (fetchPhoto) { const blob = await fetchPhoto(ph.id); src = await new Promise(res => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = () => res(''); r.readAsDataURL(blob) }) } } catch { src = '' }
      tiles.push(`<figure class="annex-photo">${src ? `<img src="${src}" alt="">` : '<div class="ink empty"></div>'}<figcaption><b>${esc(t('desk_contract_annex_photo_n', { n: i + 1 }))}</b>${ph.room ? ` · ${esc(ph.room)}` : ''}${ph.caption ? ` — ${esc(ph.caption)}` : ''}`
        + `${ph.taken_at ? `<br><span class="muted">${esc(fmtWhen(ph.taken_at))}</span>` : ''}<br><span class="muted hash">sha256: <code>${esc(ph.sha256)}</code></span></figcaption></figure>`)
    }
    annexHtml = `<section class="annex"><h2>${esc(t('desk_contract_annex_title'))}</h2><p class="muted">${esc(tLog('desk_contract_annex_count', { n: photos.length }))} · v${esc(contract.annex.version)} · <code>${esc(contract.annex.sha256 || '')}</code></p>${tiles.join('')}</section>`
  }
  const langsLine = langs.map((l, i) => `${tOr(`desk_contract_lang_${l}`, l)}${i === 0 && langs.length > 1 ? ' — ' + t('desk_contract_prevails_short') : ''}`).join(' · ')
  w.document.write(`<!doctype html><html lang="${esc(langs[0])}"><head><meta charset="utf-8"><title>${esc(title)}</title>`
    + `<style>
      @page { size: A4; margin: 22mm 14mm 18mm; }
      body { font-family: "Noto Sans", "Noto Sans Georgian", system-ui, sans-serif; font-size: 10.5pt; line-height: 1.42; color: #111; margin: 0; }
      .hdr, .ftr { position: fixed; left: 0; right: 0; font-size: 7.5pt; color: #666; display: flex; justify-content: space-between; gap: 8px; }
      .hdr { top: -16mm; } .ftr { bottom: -12mm; }
      .ftr code, .hdr code { font-family: ui-monospace, Menlo, monospace; }
      table { width: 100%; border-collapse: collapse; table-layout: fixed; } td { vertical-align: top; padding: 1.5mm 3mm; border-left: 1px solid #ddd; } td:first-child { border-left: 0; padding-left: 0; }
      .contract-table { font-size:8.5pt; } .contract-table th, .contract-table td { padding:1.2mm 1mm; border:1px solid #ddd; overflow-wrap:anywhere; }
      .contract-table th { background:#f3f1ec; } .contract-table thead { display:table-header-group; } .contract-table tr { break-inside:avoid; page-break-inside:avoid; }
      .contract-table th:nth-child(1), .contract-table td:nth-child(1) { width:32%; } .contract-table th:nth-child(2), .contract-table td:nth-child(2) { width:24%; }
      .contract-table th:nth-child(3), .contract-table td:nth-child(3) { width:18%; } .contract-table th:nth-child(4), .contract-table td:nth-child(4) { width:26%; }
      table.bi.clause, figure.annex-photo, .sig { page-break-inside: avoid; break-inside: avoid; }
      h1 { font-size: 15pt; text-align: center; } h2 { font-size: 11.5pt; margin: 5mm 0 2mm; }
      .sigblock { margin-top: 8mm; border-top: 1px solid #999; padding-top: 4mm; }
      .sig { margin-bottom: 5mm; } .role { font-weight: 600; font-size: 8.5pt; text-transform: uppercase; letter-spacing: .08em; color: #555; } .name { font-weight: 600; }
      .ink { display: block; height: 22mm; max-width: 70mm; object-fit: contain; object-position: left; margin: 1.5mm 0; border-bottom: 1px solid #333; }
      .ink.typed { font-family: Georgia, serif; font-style: italic; font-size: 14pt; line-height: 22mm; } .ink.empty { border-bottom: 1px dashed #999; }
      .meta { font-size: 8.5pt; color: #444; } .muted { color: #666; } .hash code { font-size: 7pt; word-break: break-all; }
      .annex { page-break-before: always; } figure.annex-photo { margin: 0 0 5mm; } figure.annex-photo img { display: block; max-width: 100%; max-height: 95mm; object-fit: contain; border: 1px solid #ddd; } figcaption { font-size: 9pt; margin-top: 1.5mm; }
      .docid { margin-top: 6mm; font-size: 7.5pt; color: #666; word-break: break-all; }
    </style></head><body>`
    + `<div class="hdr"><span>${esc(title)}</span><span>${esc(langsLine)}</span><span>v${esc(ver.version ?? '')} · ${esc(hash.slice(0, 10))}</span></div>`
    + `<div class="ftr"><span>senTavus · ${esc(String(contract.id || '').slice(0, 8))}</span><span>sha256 <code>${esc(hash)}</code></span></div>`
    + html
    + `<section class="sigblock"><h2>${esc(t('desk_contract_signatures'))}</h2>${sigRows}<div class="docid">${esc(t('desk_contract_doc_hash'))}: <code>${esc(hash)}</code></div></section>`
    + annexHtml + `</body></html>`)
  w.document.close()
  // let the inlined images decode before the dialog snapshots the page
  await new Promise(r => setTimeout(r, 250))
  w.focus()
  w.print()
}

// ── The party rows moved to ./contract/PartyTimeline.jsx (2026-09-11): a TIMELINE per party
// with the actions under the current state, incl. "hand my phone to <name>". ─────────────

// ── Who signed, when, and HOW — and the signature itself ──────────────────────────────────
// The method is named out loud because the methods differ in evidentiary strength (design
// §9.2): a link opened on the party's own phone is not the same fact as the agent's device
// handed across a table, and a reader of a signed contract must be able to tell them apart.
//
// Owner ruling 2026-09-10 (late night): "the signatures must be SHOWN on the document". So
// since migration 1046 this block sits UNDER the document preview like the signature page of
// a paper contract — one row per party: role · legal name · the drawn signature (a png data
// url, `signature.image`) over a bottom rule, or an EMPTY dashed line where nobody has signed
// yet · "signed · when" · the method · the language the party VIEWED at signing
// (`signature.viewed_language` — the evidence of which text they read, which matters once a
// contract carries three) · and, for a viewer who can act, the email the PDF goes / went to
// (`signature.email` is omitted by the server for everyone else, so it is never drawn for them).
// `canAct` = ContractDetail.can_act; `pdfSentAt` decides the tense of the email line.
// Drawn ON THE PAPER (design build): ink on the sheet, the two signatures side by side.
export function SignaturesBlock({ contract: c, canAct = false, pdfSentAt = null }) {
  const parties = (c.parties || []).filter(p => p.required !== false && p.role !== 'resident')   // 🏠 residents are listed, never sign
  if (parties.length === 0) return null
  return (
    <div className="space-y-3 text-[13px] text-[#14130F]" data-testid="contract-signatures">
      <div className={GROUP}>{t('desk_contract_signatures')}</div>
      <div className="grid gap-4 sm:grid-cols-2">
        {parties.map(p => {
          const sig = p.signature
          const legal = sig?.signer_name_typed || p.legal_name || p.display_name
          return (
            <div key={p.id} className="space-y-1 min-w-0" data-testid="contract-signature-row" data-signed={sig ? '1' : '0'}>
              <div className={`font-mono text-[10px] uppercase tracking-[.12em] ${INK_MUTED}`}>{tOr(`desk_contract_role_${p.role}`, p.role)}</div>
              {/* The signature line: the drawing when there is one, an empty rule otherwise. A
                  signed contract without an image (a paper upload, an older row) still shows
                  the rule with the ✓ line under it — the fact is the row, the ink is a bonus. */}
              <div className={`h-[70px] flex items-end ${sig ? 'border-b border-[#14130F]/70' : 'border-b border-dashed border-[#14130F]/35'}`}>
                {sig?.image ? (
                  <img src={sig.image} alt="" className="max-h-[68px] max-w-full object-contain object-left-bottom" draggable={false} />
                ) : null}
              </div>
              <div className="font-medium truncate" title={legal}>{legal}</div>
              {sig ? (
                <div className="space-y-0.5 text-[11px]">
                  <div className="text-[#1F6F4F]">
                    ✓ {tLog('desk_contract_signed_at', { at: fmtWhen(sig.signed_at) })}
                    {sig.version != null && sig.version !== c.current_version?.version
                      ? ` · ${tLog('desk_contract_version', { v: sig.version })}` : ''}
                  </div>
                  <div className={INK_MUTED}>{tOr(`desk_contract_method_${sig.method}`, sig.method || '')}</div>
                  {sig.viewed_language && (
                    <div className={INK_MUTED}>
                      {t('desk_contract_viewed_lang')}: {tOr(`desk_contract_lang_${sig.viewed_language}`, sig.viewed_language)}
                    </div>
                  )}
                  {canAct && (
                    sig.email
                      ? <div className={`${INK_MUTED} truncate`} title={sig.email}>
                          ✉ {tLog(pdfSentAt ? 'desk_contract_sig_email_sent' : 'desk_contract_sig_email_will', { email: sig.email })}
                        </div>
                      : <div className="text-amber-700">{t('desk_contract_sig_no_email')}</div>
                  )}
                </div>
              ) : p.declined ? (
                <div className="text-[11px] text-red-700">{tLog('desk_contract_link_declined', { at: fmtWhen(p.declined.at), reason: p.declined.reason || '' })}</div>
              ) : (
                <div className={`text-[11px] italic ${INK_MUTED}`}>{t('desk_contract_sig_none')}</div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
